import React, { useState, useEffect, useCallback, createContext, useContext, useRef, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword,
    sendPasswordResetEmail, 
    onAuthStateChanged, 
    signOut 
} from 'firebase/auth';
import { 
    getFirestore, 
    doc, 
    setDoc, 
    getDoc, 
    collection, 
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    runTransaction,
    query,
    orderBy,
    where,
    limit,
    onSnapshot,
    writeBatch,
    increment,
    documentId,
    Timestamp
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ReactDOM from 'react-dom';

// --- COMPONENTE PORTAL PARA MODALES ---
const Portal = ({ children }) => {
    const el = document.body;
    return ReactDOM.createPortal(children, el);
};

// --- COMPONENTE MODAL REUTILIZABLE (CIERRA AL HACER CLIC FUERA) ---
function Modal({ isOpen, onClose, title, children }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];

    if (!isOpen) return null;

    return (
        <Portal>
            {/* Fondo semi-transparente. Al hacer clic, se cierra el modal. */}
            <div 
                onClick={onClose} 
                className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 pt-12 overflow-y-auto"
            >
                {/* Contenedor del contenido del modal. Detiene el clic para que no se propague al fondo. */}
                <div 
                    onClick={(e) => e.stopPropagation()}
                    className={`${t.bgModal} border ${t.border} p-6 sm:p-8 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col`}
                >
                    {/* Encabezado del Modal */}
                    <div className="flex justify-between items-center mb-6 flex-shrink-0">
                        <h3 className={`text-2xl font-bold ${t.secondary}`}>{title}</h3>
                        <button onClick={onClose} className="p-1 rounded-full text-gray-400 hover:bg-gray-700 hover:text-white">
                            <XIcon />
                        </button>
                    </div>
                    
                    {/* Contenido dinámico del modal (el formulario, la lista, etc.) */}
                    <div className="flex-grow overflow-y-auto pr-4 -mr-4">
                        {children}
                    </div>
                </div>
            </div>
        </Portal>
    );
}

// --- CONTEXTO Y CONFIGURACIÓN DE TEMA ---
const ThemeContext = createContext();

const fonts = {
  'System UI': 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
  'Montserrat': "'Montserrat', sans-serif",
  'Lato': "'Lato', sans-serif",
  'Open Sans': "'Open Sans', sans-serif",
  'Poppins': "'Poppins', sans-serif",
  'Raleway': "'Raleway', sans-serif",
  'Roboto': "'Roboto', sans-serif",
  'Nunito': "'Nunito', sans-serif",
  'Inter': "'Inter', sans-serif",
  'Quicksand': "'Quicksand', sans-serif",
  'Rubik': "'Rubik', sans-serif",
  'Work Sans': "'Work Sans', sans-serif",
  'DM Sans': "'DM Sans', sans-serif",
  'Cabin': "'Cabin', sans-serif",
  'Manrope': "'Manrope', sans-serif",
  'Titillium Web': "'Titillium Web', sans-serif",
  'Oswald': "'Oswald', sans-serif",
  'Josefin Sans': "'Josefin Sans', sans-serif",
  'Merriweather': "'Merriweather', serif",
  'Playfair Display': "'Playfair Display', serif",
  'Bebas Neue': "'Bebas Neue', cursive",
  'Pacifico': "'Pacifico', cursive",
  'Dancing Script': "'Dancing Script', cursive",
  'Great Vibes': "'Great Vibes', cursive",
  'Caveat': "'Caveat', cursive",
  'Shadows Into Light': "'Shadows Into Light', cursive",
  'Amatic SC': "'Amatic SC', cursive",
  'Cinzel': "'Cinzel', serif",
  'Source Sans Pro': "'Source Sans Pro', sans-serif",
  'Ubuntu': "'Ubuntu', sans-serif",
  'Fira Sans': "'Fira Sans', sans-serif",
  'Exo 2': "'Exo 2', sans-serif",
};

// --- PLANTILLAS PARA EL CATÁLOGO PÚBLICO (CON ESTILOS DE BOTÓN) ---
const catalogTemplates = {
  elegant_gold: {
    name: "Oro Elegante",
    bg: 'bg-[#1a202c]',
    text: 'text-neutral-200',
    textMuted: 'text-gray-400',
    primary: 'text-amber-400',
    secondary: 'text-white',
    card: {
      bg: 'bg-[#2d3748]',
      border: 'border-amber-400/20',
      shadow: 'shadow-lg shadow-black/30',
    },
    plan: {
      bg: 'bg-gray-800/50',
      hover: 'hover:bg-gray-700/50', // Cambiado para que no interfiera con el botón
      price: 'text-amber-300',
    },
    button: 'bg-amber-500 text-black font-bold py-2 px-4 rounded-lg hover:bg-amber-400 transition-colors',
    footer: 'border-gray-700 text-gray-500',
  },
  vibrant_gradient: {
    name: "Gradiente Vibrante",
    bg: 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500',
    text: 'text-white',
    textMuted: 'text-white/70',
    primary: 'text-yellow-300',
    secondary: 'text-white',
    card: {
      bg: 'bg-black/20 backdrop-blur-lg',
      border: 'border-white/20',
      shadow: 'shadow-2xl',
    },
    plan: {
      bg: 'bg-black/30',
      hover: 'hover:bg-black/40',
      price: 'text-yellow-300',
    },
    button: 'bg-white text-purple-600 font-bold py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors',
    footer: 'border-white/20 text-white/60',
  },
  matrix_dark: {
    name: "Terminal Verde",
    bg: 'bg-black',
    text: 'text-green-400',
    textMuted: 'text-green-400/70',
    primary: 'text-green-300',
    secondary: 'text-green-500',
    card: {
      bg: 'bg-gray-900/50',
      border: 'border-green-500/30',
      shadow: 'shadow-lg shadow-green-500/10',
    },
    plan: {
      bg: 'bg-green-900/40',
      hover: 'hover:bg-green-500/20',
      price: 'text-green-300',
    },
    button: 'bg-green-500 text-black font-bold py-2 px-4 rounded-lg hover:bg-green-400 transition-colors',
    footer: 'border-green-500/30 text-green-400/50',
  },
  corporate_light: {
    name: "Corporativo Claro",
    bg: 'bg-white',
    text: 'text-gray-700',
    textMuted: 'text-gray-500',
    primary: 'text-blue-600',
    secondary: 'text-gray-900',
    card: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      shadow: 'shadow-md',
    },
    plan: {
      bg: 'bg-white',
      hover: 'hover:bg-gray-100',
      price: 'text-blue-600',
    },
    button: 'bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-500 transition-colors',
    footer: 'border-gray-200 text-gray-400',
  },
};

const themeConfig = {
    zen_garden: {
        name: "Jardín Zen",
        bg: 'bg-stone-100',
        bgMuted: 'bg-white',
        bgModal: 'bg-stone-50 text-stone-800',
        bgInput: 'bg-white text-stone-800 placeholder:text-stone-400',
        text: 'text-stone-700',
        textMuted: 'text-stone-500',
        primary: 'text-emerald-700',
        secondary: 'text-stone-800',
        border: 'border-stone-300',
        borderFocus: 'focus:ring-emerald-500',
        buttonPrimary: 'bg-emerald-700 text-white font-semibold hover:bg-emerald-800 transition-all duration-200 ease-in-out transform hover:scale-105 hover:-translate-y-1 hover:shadow-lg',
        // MODIFICADO: Botones secundarios ahora son verdes
        buttonSecondary: 'bg-transparent text-emerald-800 font-semibold border border-emerald-700 hover:bg-emerald-700 hover:text-white transition-colors',
        shadow: 'shadow-sm',
        tabActive: 'bg-emerald-100 text-emerald-800 font-semibold',
        // MODIFICADO: Navegación inactiva ahora es verde
        tabInactive: 'text-emerald-700/70 font-semibold hover:bg-emerald-50 hover:text-emerald-800',
    },
    facebook_blue: {
        name: "Red Social (Facebook)",
        bg: 'bg-gray-100',
        bgMuted: 'bg-white',
        bgModal: 'bg-white text-gray-800', // MODIFICADO: Añadido color de texto base
        bgInput: 'bg-gray-100 text-gray-800 placeholder:text-gray-500', // MODIFICADO: Añadido color de texto y placeholder
        text: 'text-gray-800',
        textMuted: 'text-gray-500',
        primary: 'text-blue-600',
        secondary: 'text-black',
        border: 'border-gray-300',
        borderFocus: 'focus:ring-blue-500',
        buttonPrimary: 'bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all duration-200 ease-in-out transform hover:scale-105 hover:-translate-y-1 hover:shadow-lg',
        buttonSecondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300',
        shadow: 'shadow-md',
        tabActive: 'bg-blue-100 text-blue-600 font-semibold',
        tabInactive: 'text-gray-600 hover:bg-gray-200',
    },
    netflix_dark: {
        name: "Cine Nocturno (Netflix)",
        bg: 'bg-[#141414]',
        bgMuted: 'bg-[#181818]',
        bgModal: 'bg-[#181818] text-white', // MODIFICADO: Añadido color de texto base
        bgInput: 'bg-[#333333] text-white placeholder:text-gray-500', // MODIFICADO: Añadido color de placeholder
        text: 'text-white',
        textMuted: 'text-gray-400',
        primary: 'text-red-600',
        secondary: 'text-white',
        border: 'border-gray-700',
        borderFocus: 'focus:ring-red-500',
        buttonPrimary: 'bg-red-600 text-white font-bold hover:bg-red-700 transition-all duration-200 ease-in-out transform hover:scale-105 hover:-translate-y-1 hover:shadow-lg',
        buttonSecondary: 'bg-gray-700 text-white hover:bg-gray-600',
        shadow: 'shadow-lg shadow-black/50',
        tabActive: 'text-white font-bold',
        tabInactive: 'text-gray-400 hover:text-gray-200',
    },
    terminal_matrix: {
        name: "Terminal Matrix",
        bg: 'bg-black',
        bgMuted: 'bg-gray-900/50',
        bgModal: 'bg-black text-green-400', // MODIFICADO: Añadido color de texto base
        bgInput: 'bg-gray-800/60 text-green-300 placeholder:text-green-400/60', // MODIFICADO: Añadido color de placeholder
        text: 'text-green-400',
        textMuted: 'text-green-400/70',
        primary: 'text-green-300',
        secondary: 'text-green-500',
        border: 'border-green-500/30',
        borderFocus: 'focus:ring-green-400',
        buttonPrimary: 'bg-green-500 text-black font-bold hover:bg-green-400 transition-all duration-200 ease-in-out transform hover:scale-105 hover:-translate-y-1 hover:shadow-lg',
        buttonSecondary: 'bg-gray-700 text-green-400 hover:bg-gray-600',
        shadow: 'shadow-lg shadow-green-500/10',
        tabActive: 'bg-green-500/20 text-green-300 border-l-4 border-green-400',
        tabInactive: 'text-green-400/70 hover:bg-green-500/10 hover:text-green-300',
    },
    aurora_dream: {
        name: "Sueño Aurora (Animado)",
        bg: 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 animate-gradient-xy',
        bgMuted: 'bg-black/30',
        bgModal: 'bg-gray-900 text-gray-200', // MODIFICADO: Añadido color de texto base
        bgInput: 'bg-white/5 text-gray-200 placeholder:text-gray-400/70', // MODIFICADO: Añadido color de placeholder
        text: 'text-gray-200',
        textMuted: 'text-gray-400',
        primary: 'text-teal-300',
        secondary: 'text-fuchsia-400',
        border: 'border-white/10',
        borderFocus: 'focus:ring-teal-400',
        buttonPrimary: 'bg-gradient-to-r from-teal-400 to-fuchsia-500 text-white font-bold hover:from-teal-500 hover:to-fuchsia-600 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 hover:shadow-lg',
        buttonSecondary: 'bg-white/10 text-gray-200 hover:bg-white/20 transition-colors',
        shadow: 'shadow-lg shadow-fuchsia-500/10',
        tabActive: 'bg-teal-500/10 text-teal-300 border-b-2 border-teal-400',
        tabInactive: 'text-gray-400 hover:bg-white/5',
    },
};

const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => localStorage.getItem('gcs1-theme') || 'netflix_dark');
    const [font, setFont] = useState(() => localStorage.getItem('gcs1-font') || 'System UI');
    // --- NUEVO ESTADO PARA EL ESTILO DEL DASHBOARD ---
    const [dashboardStyle, setDashboardStyle] = useState('classic');

    useEffect(() => {
        localStorage.setItem('gcs1-theme', theme);
        localStorage.setItem('gcs1-font', font);
        
        const currentTheme = themeConfig[theme] || themeConfig.netflix_dark;
        document.body.className = currentTheme.bg;
        document.body.style.fontFamily = fonts[font];

        const animationStyleId = 'dynamic-theme-styles';
        let animationStyle = document.getElementById(animationStyleId);
        if (!animationStyle) {
            animationStyle = document.createElement('style');
            animationStyle.id = animationStyleId;
            document.head.appendChild(animationStyle);
        }
        
        let styles = `
            .toggle-checkbox:checked {
                right: 0;
                border-color: #48bb78;
                transform: translateX(100%);
            }
            .toggle-checkbox:checked + .toggle-label {
                background-color: #48bb78;
            }
            .toggle-checkbox {
                transition: all 0.2s ease-in-out;
                left: 0;
            }
        `;
        animationStyle.innerHTML = styles;

        const fontId = 'google-font-stylesheet';
        let link = document.getElementById(fontId);
        if (!link) {
            link = document.createElement('link');
            link.id = fontId;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
        const fontQuery = Object.keys(fonts).map(f => `family=${f.replace(' ', '+')}:wght@400;700`).join('&');
        link.href = `https://fonts.googleapis.com/css2?${fontQuery}&display=swap`;
    }, [theme, font]);

    return (
        // --- AÑADIMOS EL NUEVO ESTADO AL CONTEXTO ---
        <ThemeContext.Provider value={{ theme, setTheme, font, setFont, themes: themeConfig, fonts, dashboardStyle, setDashboardStyle }}>
            {children}
        </ThemeContext.Provider>
    );
};

const useTheme = () => useContext(ThemeContext);

const NotificationContext = createContext();

const NotificationProvider = ({ children }) => {
    const [notification, setNotification] = useState({ message: '', type: '', visible: false });

    const showNotification = useCallback((message, type = 'success') => {
        setNotification({ message, type, visible: true });
        setTimeout(() => {
            setNotification(prev => ({ ...prev, visible: false }));
        }, 3000);
    }, []);

    return (
        <NotificationContext.Provider value={{ showNotification }}>
            {children}
            {notification.visible && (
                <div className={`fixed top-5 right-5 px-6 py-3 rounded-lg shadow-lg z-50 text-white font-bold
                    ${notification.type === 'success' ? 'bg-green-500' : ''}
                    ${notification.type === 'error' ? 'bg-red-500' : ''}
                    ${notification.type === 'info' ? 'bg-blue-500' : ''}
                `}>
                    {notification.message}
                </div>
            )}
        </NotificationContext.Provider>
    );
};

const useNotification = () => useContext(NotificationContext);

// --- ÍCONOS SVG ---
const LogoutIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>;
const SendIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>;
const BoxIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>;
const UsersIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className || "h-5 w-5 mr-2"}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>;
const TruckIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>;
const ShoppingCartIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>;
const PlusCircleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;
const ClockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>;
const MessageSquareIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>;
const AlertTriangleIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-gray-400"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const SettingsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>;
const ShoppingBagIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>;
const MenuIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>;
const DownloadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>;
const UploadIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>;
const BarChartIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><line x1="12" y1="20" x2="12" y2="10"></line><line x1="18" y1="20" x2="18" y2="4"></line><line x1="6" y1="20" x2="6" y2="16"></line></svg>;
const InfoIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>;
const BotIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M12 8V4H8"></path><rect width="16" height="12" x="4" y="8" rx="2"></rect><path d="M2 14h2"></path><path d="M20 14h2"></path><path d="M15 13v2"></path><path d="M9 13v2"></path></svg>;
const InboxIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M22 12h-6l-2 3h-4l-2-3H2"></path><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path></svg>;
const MegaphoneIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="m3 11 18-5v12L3 14v-3z"></path><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path></svg>;
const ChartPieIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path><path d="M22 12A10 10 0 0 0 12 2v10z"></path></svg>;
const LinkIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72"></path></svg>;
const BellIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>;
const DollarSignIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>;
const KeyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0L19 4"></path></svg>;
const RefreshCwIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>;
const TrendingUpIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>;
const TrophyIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><line x1="10" y1="14.5" x2="14" y2="14.5"></line><path d="M12 22V4"></path><path d="M8 12H5.5a3.5 3.5 0 0 0 0 7H8"></path><path d="M16 12h2.5a3.5 3.5 0 0 1 0 7H16"></path></svg>;
const SparklesIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3L14.34 8.66L20 11l-5.66 2.66L12 19l-2.34-5.34L4 11l5.66-2.34L12 3z"/><path d="M5 21L6.63 17.37"/><path d="M19 21L17.37 17.37"/></svg>;
const PayPalIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M8.22 6.321a.48.48 0 0 0-.473.443l-1.18 7.373a.48.48 0 0 0 .473.515h3.428a.48.48 0 0 0 .473-.443l.29-1.804a.48.48 0 0 0-.474-.515h-2.29l.23-1.428h2.06a.48.48 0 0 0 .473-.443l.29-1.803a.48.48 0 0 0-.474-.515H9.42l.142-.876a.48.48 0 0 0-.473-.515H5.662a.48.48 0 0 0-.473.443L4.8 8.665a.48.48 0 0 0 .473.515h2.468l-.23 1.428H5.273a.48.48 0 0 0-.473.443l-1.18 7.374a.48.48 0 0 0 .473.515h4.15l.412-2.55h.31a.48.48 0 0 0 .474-.443l.592-3.69a.48.48 0 0 0-.473-.515h-2.1l.37-2.3h2.643a.48.48 0 0 0 .473-.443l.592-3.69a.48.48 0 0 0-.473-.515h-3.32zm9.055 3.328l-.34 2.115a.48.48 0 0 0 .473.515h2.29l-.23 1.428h-2.06a.48.48 0 0 0-.474.443l-.591 3.69a.48.48 0 0 0 .473.515h2.72l-.34 2.114a.48.48 0 0 0 .473.515h3.429a.48.48 0 0 0 .473-.443l1.18-7.374a.48.48 0 0 0-.473-.515h-3.428a.48.48 0 0 0-.474.443z"/></svg>;
const InventoryIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M18.33 8H5.67a2 2 0 0 0-1.98 2.28l1.65 8.23a2 2 0 0 0 1.98 1.49h9.36a2 2 0 0 0 1.98-1.49l1.65-8.23A2 2 0 0 0 18.33 8z"></path><path d="M12 12v4"></path><path d="M12 2v2"></path><path d="M20 6l-2.5-2.5"></path><path d="M4 6l2.5-2.5"></path></svg>;const WhatsAppIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.886-.001 2.267.651 4.383 1.803 6.182l-.341 1.246 1.274-.352z"></path></svg>;
const TelegramIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm6.573 8.134l-1.89 8.846c-.183.858-.683 1.07-1.396.666l-4.498-3.32-2.17 2.09c-.24.24-.444.444-.9.444l.333-4.584L16.07 9.49c.333-.296-.083-.462-.516-.166L7.13 12.87l-4.48-1.396c-.88-.277-.898-1.42.183-1.82l12.45-4.704c.732-.277 1.36.194 1.13 1.194z"></path></svg>;
const MessageCircleMoreIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/><path d="M8 12h.01"/><path d="M12 12h.01"/><path d="M16 12h.01"/></svg>;
const ArchiveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/></svg>;
const UserIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const HomeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 mr-2"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>;
const XIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;

// --- CONFIGURACIÓN DE FIREBASE ---
let firebaseApp, firebaseAuth, firestoreDb;

const initializeFirebase = () => {
    if (!firebaseApp) {
        const firebaseConfig = {
            apiKey: "AIzaSyClFvGZUl35FIfV479j2S-reFZA1tI_5Kg",
            authDomain: "gcs1-15eb2.firebaseapp.com",
            projectId: "gcs1-15eb2",
            storageBucket: "gcs1-15eb2.appspot.com",
            messagingSenderId: "754335606433",
            appId: "1:754335606433:web:7da7ef6154557f72e7b2e0",
            measurementId: "G-SL2TLH7LG9"
        };
        firebaseApp = initializeApp(firebaseConfig);
        firebaseAuth = getAuth(firebaseApp);
        firestoreDb = getFirestore(firebaseApp);
    }
    return { app: firebaseApp, auth: firebaseAuth, db: firestoreDb };
};

// --- CONSTANTES Y CONFIGURACIONES GLOBALES ---
const DEFAULT_MESSAGE_TEMPLATES = {
    deliver: '¡Hola, {cliente}! 👋\n\nAquí tienes los datos de tu cuenta de {servicio}:\n\n{datos_cuenta}\n\nInstrucciones: {instrucciones}\n\n¡Gracias por tu compra! ✨\n- El equipo de {mi_negocio}\n{mi_contacto}',
    renewal_success: '¡Hola, {cliente}! 👋\n\nConfirmamos la renovación de tu servicio {servicio}.\n\nTu nueva fecha de vencimiento es el {vencimiento}.\n\nLos datos de tu cuenta siguen siendo los mismos.\n\n¡Gracias por seguir con nosotros! ✨\n- El equipo de {mi_negocio}\n{mi_contacto}',
    renewal_warning: '¡Hola, {cliente}! 👋\n\nSolo un recordatorio amigable de que tu suscripción a {servicio} vence el {vencimiento}. ¡No te quedes sin tu servicio! 🚀\n- El equipo de {mi_negocio}\n{mi_contacto}',
    charge: '¡Hola, {cliente}! 👋\n\nTu suscripción a {servicio} ha vencido. Para renovar y seguir disfrutando, por favor realiza el pago. ¡Gracias! 😊\n- El equipo de {mi_negocio}\n{mi_contacto}',
    password_change: '¡Hola, {cliente}! 👋\n\nTe informamos que los datos de tu cuenta de {servicio} han sido actualizados. Por favor, utiliza las nuevas credenciales:\n\n{datos_cuenta}\n\n¡Gracias por tu comprensión!\n- El equipo de {mi_negocio}\n{mi_contacto}',
    offer: '¡Hola, {cliente}! 👋\n\nNotamos que tu suscripción a {servicio} ha terminado. ¡Te extrañamos! Como cliente especial, te ofrecemos un descuento en tu renovación. ¡No te lo pierdas! 🎉\n- El equipo de {mi_negocio}\n{mi_contacto}',
    'welcome_new_client': '¡Hola, {cliente}! 👋 Bienvenido a {mi_negocio}. Nos complace que seas parte de nuestra comunidad. ¡Tu registro se ha completado con éxito! ✨',
    'client_portal_login': '¡Hola, {cliente}! 👋 Aquí tienes tu enlace de acceso personal para ver tus servicios. Es válido por 15 minutos: {login_link}'
};

// --- VISTA DE CONFIGURACIÓN DEL CHATBOT (RUTA CORREGIDA) ---
function ChatbotConfigView({ user, isSdkReady }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const [config, setConfig] = useState({
        geminiApiKey: '',
        metaApiToken: '',
        systemPrompt: '',
        enabledFeatures: {
            inventoryCheck: true,
            faq: true,
            support: true,
        },
        faqs: []
    });
    const [loading, setLoading] = useState(true);
    const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
    const [isWhatsAppConnected, setIsWhatsAppConnected] = useState(false);

    const launchWhatsAppSignup = () => {
        if (!isSdkReady || !window.FB) {
            showNotification("El SDK de conexión aún está cargando. Por favor, espera unos segundos e intenta de nuevo.", "error");
            return;
        }
        
        window.FB.login(function(response) {
            const handleLoginResponse = async (loginResponse) => {
                if (loginResponse.authResponse && loginResponse.authResponse.code) {
                    showNotification('¡Autorización exitosa! Finalizando conexión...', 'success');
                    
                    const functionUrl = 'URL_DE_TU_NUEVA_CLOUD_FUNCTION'; // Reemplaza esto
                    
                    try {
                        const { app } = initializeFirebase();
                        const functions = getFunctions(app);
                        const handleConnection = httpsCallable(functions, 'handleWhatsAppConnection');
                        const result = await handleConnection({
                            code: loginResponse.authResponse.code,
                            userId: user.uid
                        });

                        if (result.data.success) {
                            showNotification('¡WhatsApp conectado exitosamente!', 'success');
                            setIsWhatsAppConnected(true);
                        } else {
                            throw new Error(result.data.message || 'Error desconocido en el backend.');
                        }

                    } catch (error) {
                        console.error("Error al llamar a la función de conexión:", error);
                        showNotification(`Error al conectar: ${error.message}`, 'error');
                    }

                } else {
                    console.log('El usuario canceló el inicio de sesión o no autorizó completamente.');
                    showNotification('El proceso de conexión fue cancelado.', 'info');
                }
            };
            
            handleLoginResponse(response);

        }, {
            config_id: '1077805474417739', // <-- ¡IMPORTANTE!
            response_type: 'code',
            override_default_response_type: true,
            extras: {}
        });
    };

    useEffect(() => {
        // --- RUTA CORREGIDA ---
        const configRef = doc(db, 'users', user.uid, 'chatbotConfig', 'settings');
        const unsubscribe = onSnapshot(configRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setConfig(prev => ({ ...prev, ...data }));
                setIsWhatsAppConnected(data.isWhatsAppConnected || false);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [db, user.uid]);

    const handleSave = useCallback(async (newConfig) => {
        try {
            // --- RUTA CORREGIDA ---
            const configRef = doc(db, 'users', user.uid, 'chatbotConfig', 'settings');
            await setDoc(configRef, newConfig, { merge: true });
            showNotification('Configuración guardada', 'success');
        } catch (error) {
            console.error("Error saving chatbot config:", error);
            showNotification('Error al guardar', 'error');
        }
    }, [db, user.uid, showNotification]);

    const handleConfigChange = (e) => {
        const { name, value } = e.target;
        const newConfig = { ...config, [name]: value };
        setConfig(newConfig);
        handleSave(newConfig);
    };

    const handleFeatureToggle = (feature) => {
        const newConfig = {
            ...config,
            enabledFeatures: {
                ...config.enabledFeatures,
                [feature]: !config.enabledFeatures[feature]
            }
        };
        setConfig(newConfig);
        handleSave(newConfig);
    };

    const handleAddFaq = () => {
        if (!newFaq.question || !newFaq.answer) {
            showNotification('La pregunta y la respuesta no pueden estar vacías.', 'error');
            return;
        }
        const updatedFaqs = [...(config.faqs || []), { id: Date.now(), ...newFaq }];
        const newConfig = { ...config, faqs: updatedFaqs };
        setConfig(newConfig);
        handleSave(newConfig);
        setNewFaq({ question: '', answer: '' });
    };

    const handleDeleteFaq = (faqId) => {
        const updatedFaqs = config.faqs.filter(faq => faq.id !== faqId);
        const newConfig = { ...config, faqs: updatedFaqs };
        setConfig(newConfig);
        handleSave(newConfig);
    };

    if (loading) {
        return <div>Cargando configuración del asistente...</div>;
    }

    return (
        <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border} space-y-8`}>
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                <h2 className={`text-2xl font-bold ${t.primary} mb-4`}>Conexión del Chatbot</h2>
                {isWhatsAppConnected ? (
                    <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-r-lg">
                        <p className="font-bold">¡Conectado!</p>
                        <p>Tu número de WhatsApp está vinculado y el chatbot está listo para ser configurado.</p>
                    </div>
                ) : (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <p className={t.textMuted}>Conecta tu número de WhatsApp Business para activar el asistente virtual para tus clientes.</p>
                        <button 
                            onClick={launchWhatsAppSignup} 
                            disabled={!isSdkReady}
                            className={`flex items-center gap-2 px-6 py-3 font-bold rounded-md ${t.buttonPrimary} bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-wait`}
                        >
                            <WhatsAppIcon />
                            {isSdkReady ? 'Conectar con WhatsApp' : 'Cargando...'}
                        </button>
                    </div>
                )}
            </div>
            
            <div>
                <h2 className={`text-2xl font-bold ${t.primary} mb-2`}>Asistente Virtual con IA</h2>
                <p className={t.textMuted}>
                    Configura tu chatbot para WhatsApp. Este asistente usará la IA de Gemini para responder a tus clientes, consultar inventario y más.
                </p>
            </div>

            <div className={`p-6 rounded-lg border ${t.border} ${t.bg}`}>
                <h3 className={`text-xl font-bold ${t.secondary} mb-4`}>Conexión API</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Clave API de Gemini</label>
                        <input type="password" name="geminiApiKey" value={config.geminiApiKey || ''} onChange={handleConfigChange} placeholder="Pega tu clave API de Google AI" className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                    </div>
                    <div>
                        <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Token de Acceso de Meta (WhatsApp)</label>
                        <input type="password" name="metaApiToken" value={config.metaApiToken || ''} placeholder="Se llenará automáticamente al conectar" className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} readOnly />
                    </div>
                </div>
                 <p className="text-xs text-gray-500 mt-3">Tus claves se guardan de forma segura y no son visibles para otros usuarios.</p>
            </div>

            <div className={`p-6 rounded-lg border ${t.border} ${t.bg}`}>
                <h3 className={`text-xl font-bold ${t.secondary} mb-4`}>Personalidad e Instrucciones (System Prompt)</h3>
                <textarea name="systemPrompt" value={config.systemPrompt || ''} onChange={handleConfigChange} rows="8" placeholder="Ej: Eres GCS-Bot, un asistente amigable..." className={`w-full p-4 ${t.bgInput} border ${t.border} rounded-md`} />
            </div>

            <div className={`p-6 rounded-lg border ${t.border} ${t.bg}`}>
                <h3 className={`text-xl font-bold ${t.secondary} mb-4`}>Capacidades del Asistente</h3>
                <div className="space-y-3">
                    {Object.keys(config.enabledFeatures).map(feature => (
                         <label key={feature} className="flex items-center justify-between p-3 bg-gray-500/10 rounded-md">
                            <span className="font-semibold capitalize">{feature.replace(/([A-Z])/g, ' $1')}</span>
                             <div className="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                                 <input type="checkbox" name={feature} id={feature} checked={config.enabledFeatures[feature]} onChange={() => handleFeatureToggle(feature)} className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" />
                                 <label htmlFor={feature} className="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"></label>
                             </div>
                         </label>
                    ))}
                </div>
            </div>

            <div className={`p-6 rounded-lg border ${t.border} ${t.bg}`}>
                <h3 className={`text-xl font-bold ${t.secondary} mb-4`}>Base de Conocimiento (Preguntas Frecuentes)</h3>
                <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
                    {(config.faqs || []).map((faq) => (
                        <div key={faq.id} className={`${t.bgMuted} p-3 rounded-md border ${t.border}`}>
                            <div className="flex justify-between items-start">
                                <p className={`font-bold ${t.text}`}>{faq.question}</p>
                                <button onClick={() => handleDeleteFaq(faq.id)} className="text-red-400 hover:text-red-600"><TrashIcon /></button>
                            </div>
                            <p className={t.textMuted}>{faq.answer}</p>
                        </div>
                    ))}
                </div>
                <div className="space-y-3 border-t border-gray-700 pt-4">
                     <h4 className="font-bold">Añadir Nueva Pregunta</h4>
                    <input type="text" value={newFaq.question} onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })} placeholder="Pregunta del cliente" className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                    <textarea value={newFaq.answer} onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })} placeholder="Respuesta que debe dar el bot" rows="3" className={`w-full p-4 ${t.bgInput} border ${t.border} rounded-md`} />
                    <div className="text-right">
                        <button onClick={handleAddFaq} className={t.buttonPrimary + " px-4 py-2 rounded-md"}>Añadir FAQ</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- VISTA DE BANDEJA DE ENTRADA (CON PAUSA Y REACTIVACIÓN DE BOT) ---
function InboxView({ user, teamMembers, cannedResponses }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();

    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    
    const [mobileView, setMobileView] = useState('list');
    const [showCanned, setShowCanned] = useState(false);
    const [cannedSearch, setCannedSearch] = useState('');

    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(scrollToBottom, [messages]);

    const handleSelectConversation = (convo) => {
        setSelectedConversation(convo);
        if (window.innerWidth < 640) {
            setMobileView('chat');
        }
    };
    
    const teamId = user.teamId || user.uid;

    useEffect(() => {
        const q = query(collection(db, 'users', teamId, 'conversations'), orderBy('lastTimestamp', 'desc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const convos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setConversations(convos.filter(c => c.status !== 'archived'));
            setLoading(false);
        }, (error) => {
            console.error("Error al cargar conversaciones:", error);
            showNotification("No se pudieron cargar las conversaciones.", "error");
            setLoading(false);
        });
        return () => unsubscribe();
    }, [db, teamId]);

    useEffect(() => {
        if (!selectedConversation) return;
        const messagesRef = collection(db, 'users', teamId, 'conversations', selectedConversation.id, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
        
        if (selectedConversation.unread) {
            const convoRef = doc(db, 'users', teamId, 'conversations', selectedConversation.id);
            updateDoc(convoRef, { unread: false });
        }

        return () => unsubscribe();
    }, [selectedConversation, db, teamId]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedConversation) return;
        const textToSend = newMessage;
        setNewMessage('');
        setIsSending(true);

        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const sendAgentMessage = httpsCallable(functions, 'sendAgentMessage');
            await sendAgentMessage({ recipient: selectedConversation.id, message: textToSend });
            
            const conversationRef = doc(db, 'users', teamId, 'conversations', selectedConversation.id);
            await addDoc(collection(conversationRef, 'messages'), { text: textToSend, from: 'agent', agentId: user.uid, timestamp: new Date() });
            
            // --- CORRECCIÓN: Al enviar mensaje, pausar el bot ---
            await updateDoc(conversationRef, { 
                lastMessage: textToSend, 
                lastTimestamp: new Date(),
                botPaused: true // Pausamos el bot
            });

        } catch (error) {
            console.error("Error enviando mensaje:", error);
            showNotification(error.message, 'error');
            setNewMessage(textToSend);
        } finally {
            setIsSending(false);
        }
    };

    const handleArchiveConversation = async (e, convoId) => {
        e.stopPropagation();
        const convoRef = doc(db, 'users', teamId, 'conversations', convoId);
        await updateDoc(convoRef, { status: 'archived' });
        showNotification('Conversación archivada.', 'info');
        if (selectedConversation?.id === convoId) {
            setSelectedConversation(null);
        }
    };

    // --- NUEVA FUNCIÓN: Para reactivar el bot ---
    const handleBotReactivation = async () => {
        if (!selectedConversation) return;
        const convoRef = doc(db, 'users', teamId, 'conversations', selectedConversation.id);
        try {
            await updateDoc(convoRef, { botPaused: false });
            showNotification('¡Asistente IA reactivado para este chat!', 'success');
        } catch (error) {
            console.error("Error reactivando el bot:", error);
            showNotification("No se pudo reactivar el asistente.", "error");
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setNewMessage(value);
        if (value.startsWith('/')) {
            setShowCanned(true);
            setCannedSearch(value.substring(1));
        } else {
            setShowCanned(false);
        }
    };

    const handleSelectCanned = (text) => {
        setNewMessage(text);
        setShowCanned(false);
    };

    const filteredCannedResponses = cannedResponses.filter(r => 
        r.shortcut.toLowerCase().includes(cannedSearch.toLowerCase()) ||
        r.text.toLowerCase().includes(cannedSearch.toLowerCase())
    );

    const conversationListClass = `w-full sm:w-1/3 border-r ${t.border} flex flex-col ${mobileView === 'chat' ? 'hidden sm:flex' : 'flex'}`;
    const chatViewClass = `w-full sm:w-2/3 flex flex-col ${mobileView === 'list' ? 'hidden sm:flex' : 'flex'}`;

    return (
        <div className={`h-[85vh] max-h-[800px] max-w-7xl mx-auto flex ${t.bgMuted} border ${t.border} rounded-lg overflow-hidden shadow-2xl`}>
            <div className={conversationListClass}>
                <div className={`p-4 border-b ${t.border} flex-shrink-0`}>
                    <h2 className={`text-xl font-bold ${t.primary}`}>Bandeja de Entrada</h2>
                </div>
                <div className="overflow-y-auto">
                    {loading ? <p className="p-4">Cargando chats...</p> :
                        conversations.map(convo => (
                            <div
                                key={convo.id}
                                onClick={() => handleSelectConversation(convo)}
                                className={`group relative p-4 cursor-pointer border-b ${t.border} ${selectedConversation?.id === convo.id ? t.tabActive : `hover:${t.bgInput}`}`}
                            >
                                <div className="flex justify-between items-center">
                                    <p className="font-bold truncate">{convo.clientName || convo.id}</p>
                                    {convo.unread && <span className="w-3 h-3 bg-blue-500 rounded-full flex-shrink-0"></span>}
                                </div>
                                <p className={`text-sm ${t.textMuted} truncate`}>{convo.lastMessage}</p>
                                <button 
                                    onClick={(e) => handleArchiveConversation(e, convo.id)} 
                                    className="absolute top-2 right-2 p-1 rounded-full bg-gray-500/20 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/30 hover:text-white"
                                    title="Archivar conversación"
                                >
                                    <ArchiveIcon />
                                </button>
                            </div>
                        ))
                    }
                </div>
            </div>
            <div className={chatViewClass}>
                {selectedConversation ? (
                    <>
                        <div className={`p-4 border-b ${t.border} flex-shrink-0 flex items-center justify-between gap-4`}>
                             <div className="flex items-center gap-2 min-w-0">
                                <button onClick={() => setMobileView('list')} className="sm:hidden p-1 rounded-full hover:bg-gray-500/20">
                                    &lt;--
                                </button>
                                <h3 className="font-bold text-lg truncate">{selectedConversation.clientName || selectedConversation.id}</h3>
                             </div>
                             {/* --- NUEVO: Botón para reactivar el bot --- */}
                             {selectedConversation.botPaused && (
                                 <button onClick={handleBotReactivation} className="flex items-center gap-2 px-3 py-1 text-sm font-bold rounded-md bg-green-500/20 text-green-400 hover:bg-green-500/40">
                                     <BotIcon /> Reactivar Asistente
                                 </button>
                             )}
                        </div>
                        <div className="flex-1 p-4 overflow-y-auto space-y-4">
                            {messages.map(msg => (
                                <div key={msg.id} className={`flex ${msg.from === 'customer' ? 'justify-start' : 'justify-end'}`}>
                                    <div className={`max-w-xs lg:max-w-md p-3 rounded-lg ${msg.from === 'customer' ? `${t.bgInput} ${t.text}` : 'bg-blue-600 text-white'}`}>
                                        <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                                        <p className={`text-xs mt-1 ${msg.from === 'customer' ? t.textMuted : 'text-blue-200'} text-right`}>
                                            {msg.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            <div ref={messagesEndRef} />
                        </div>
                        <div className={`p-4 border-t ${t.border} bg-opacity-50 flex-shrink-0 relative`}>
                            {showCanned && (
                                <div className={`absolute bottom-full left-0 right-0 mb-2 p-2 ${t.bgModal} border ${t.border} rounded-lg shadow-lg max-h-48 overflow-y-auto`}>
                                    {filteredCannedResponses.length > 0 ? (
                                        filteredCannedResponses.map(response => (
                                            <div
                                                key={response.id}
                                                onClick={() => handleSelectCanned(response.text)}
                                                className={`p-2 rounded-md cursor-pointer hover:${t.bgInput}`}
                                            >
                                                <p className="font-bold">{response.shortcut}</p>
                                                <p className={`text-sm ${t.textMuted}`}>{response.text}</p>
                                            </div>
                                        ))
                                    ) : (
                                        <p className={`p-2 text-sm ${t.textMuted}`}>No se encontraron respuestas...</p>
                                    )}
                                </div>
                            )}
                            <form onSubmit={handleSendMessage} className="flex gap-2">
                                <input
                                    type="text"
                                    value={newMessage}
                                    onChange={handleInputChange}
                                    placeholder="Escribe tu respuesta o usa / para buscar..."
                                    className={`flex-1 px-4 py-2 ${t.bgInput} border ${t.border} rounded-full`}
                                />
                                <button type="submit" className={`${t.buttonPrimary} rounded-full p-3`}>
                                    <SendIcon />
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <p className={t.textMuted}>Selecciona una conversación para empezar a chatear.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- VISTA DE CAMPAÑAS DE MARKETING (CORREGIDA) ---
function CampaignsView({ user, clients, services, sales }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { showNotification } = useNotification();
    const [message, setMessage] = useState('');
    const [audience, setAudience] = useState('all');
    const [isSending, setIsSending] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    
    // --- NUEVO ESTADO PARA EL PROMPT DE LA IA ---
    const [aiPrompt, setAiPrompt] = useState('Anunciar una oferta especial para suscripciones de streaming.');

    const userPlan = user.plan || {};

    const handleGenerateSuggestion = async () => {
        if (!aiPrompt.trim()) {
            showNotification("Por favor, escribe las instrucciones para la IA.", "error");
            return;
        }
        setIsGenerating(true);
        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const generateSuggestion = httpsCallable(functions, 'generateTextSuggestion');
            
            const result = await generateSuggestion({ 
                context: 'campaign',
                details: {
                    userPrompt: aiPrompt 
                }
            });
            setMessage(result.data.suggestion);
        } catch (error) {
            console.error("Error al generar sugerencia:", error);
            showNotification(error.message, "error");
        } finally {
            setIsGenerating(false);
        }
    };

    const getAudienceCount = (audienceId) => {
        if (!clients || !sales) return 0;
        if (audienceId === 'all') return clients.length;
        const clientIds = sales.filter(sale => sale.serviceId === audienceId).map(sale => sale.clientId);
        return new Set(clientIds).size;
    };

    const handleSendCampaign = async () => {
        if (!message.trim()) {
            showNotification("El mensaje de la campaña no puede estar vacío.", "error");
            return;
        }
        setIsSending(true);
        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const sendCampaignFunction = httpsCallable(functions, 'sendCampaign');
            
            const result = await sendCampaignFunction({ 
                message: message, 
                audience: audience 
            });

            showNotification(result.data.message, 'success');
            setMessage('');
        } catch (error) {
            console.error("Error al enviar la campaña:", error);
            showNotification(error.message, 'error');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border} space-y-6`}>
            <div>
                <h2 className={`text-2xl font-bold ${t.primary}`}>Campañas de Marketing</h2>
                <p className={t.textMuted}>Envía mensajes proactivos a tus clientes para anunciar ofertas y novedades.</p>
            </div>

            <div className={`p-6 rounded-lg border ${t.border} ${t.bg}`}>
                <h3 className={`text-xl font-bold ${t.secondary} mb-4`}>Crear Nueva Campaña</h3>
                <div className="space-y-4">
                    
                    {userPlan.hasAiSuggestions && (
                        <div className={`p-4 rounded-lg border ${t.border} ${t.bgInput}`}>
                             <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Instrucciones para la IA</label>
                             <textarea 
                                value={aiPrompt} 
                                onChange={(e) => setAiPrompt(e.target.value)} 
                                rows="2" 
                                placeholder="Ej: Anunciar una oferta del 50% en Netflix por el Día del Padre..." 
                                className={`w-full p-2 ${t.bgMuted} border ${t.border} rounded-md`} 
                             />
                             <div className="text-right mt-2">
                                <button onClick={handleGenerateSuggestion} disabled={isGenerating} className={`flex items-center gap-2 px-3 py-1 text-sm font-bold rounded-md ${t.buttonSecondary} disabled:opacity-50`}>
                                    <SparklesIcon /> {isGenerating ? 'Generando...' : 'Generar Mensaje'}
                                </button>
                             </div>
                        </div>
                    )}

                    <div>
                        <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Mensaje a Enviar</label>
                        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows="6" placeholder="El texto generado por la IA aparecerá aquí..." className={`w-full p-4 ${t.bgInput} border ${t.border} rounded-md`} />
                        <p className="text-xs text-gray-500 mt-1">Variable disponible: {`{cliente}`}</p>
                    </div>
                    <div>
                        <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Destinatarios</label>
                        <select value={audience} onChange={(e) => setAudience(e.target.value)} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                            <option value="all">Todos los Clientes ({getAudienceCount('all')})</option>
                            <optgroup label="Clientes por Servicio">
                                {services.map(service => (
                                    <option key={service.id} value={service.id}>{service.name} ({getAudienceCount(service.id)})</option>
                                ))}
                            </optgroup>
                        </select>
                    </div>
                    <div className="text-right pt-2">
                        <button onClick={handleSendCampaign} disabled={isSending} className={`flex items-center gap-2 px-6 py-3 font-bold rounded-md ${t.buttonPrimary} disabled:opacity-50`}>
                            <SendIcon /> {isSending ? 'Enviando...' : `Enviar Campaña`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// --- VISTA DE ANÁLISIS ---
function AnalyticsView({ user }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [timeRange, setTimeRange] = useState('last7days');

    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            try {
                const { app } = initializeFirebase();
                const functions = getFunctions(app);
                const getAnalytics = httpsCallable(functions, 'getConversationAnalytics');
                const result = await getAnalytics({ timeRange });
                setData(result.data);
            } catch (error) {
                console.error("Error fetching analytics:", error);
                showNotification("Error al cargar las analíticas.", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [timeRange, user]);

    const timeRangeOptions = [
        { id: 'today', label: 'Hoy' },
        { id: 'last7days', label: 'Últimos 7 Días' },
        { id: 'last30days', label: 'Últimos 30 Días' },
    ];

    return (
        <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border} space-y-6`}>
            <div className="flex flex-col sm:flex-row justify-between items-center">
                <div>
                    <h2 className={`text-2xl font-bold ${t.primary}`}>Análisis del Asistente</h2>
                    <p className={t.textMuted}>Métricas clave sobre el rendimiento de tu chatbot.</p>
                </div>
                <div className={`p-1 rounded-lg ${t.bgInput} flex items-center`}>
                    {timeRangeOptions.map(opt => (
                        <button 
                            key={opt.id}
                            onClick={() => setTimeRange(opt.id)}
                            className={`px-3 py-1 rounded-md text-sm font-semibold ${timeRange === opt.id ? `${t.buttonPrimary}` : 'bg-transparent'}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="text-center p-8">Calculando métricas...</div>
            ) : data ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className={`${t.bg} p-6 rounded-lg border ${t.border}`}>
                        <p className={`text-sm font-bold ${t.textMuted}`}>Conversaciones Totales</p>
                        <p className={`text-4xl font-bold ${t.secondary}`}>{data.totalConversations}</p>
                    </div>
                    <div className={`${t.bg} p-6 rounded-lg border ${t.border}`}>
                        <p className={`text-sm font-bold ${t.textMuted}`}>Intervenciones Humanas</p>
                        <p className={`text-4xl font-bold ${data.agentInterventions > 0 ? 'text-yellow-500' : t.secondary}`}>{data.agentInterventions}</p>
                    </div>
                    <div className={`${t.bg} p-6 rounded-lg border ${t.border} lg:col-span-2`}>
                        <p className={`text-sm font-bold ${t.textMuted} mb-2`}>Temas Populares</p>
                        <div className="space-y-2">
                            {data.topKeywords.length > 0 ? data.topKeywords.map(kw => (
                                <div key={kw.text} className="flex justify-between items-center text-sm">
                                    <span className="capitalize">{kw.text}</span>
                                    <span className={`${t.primary} font-semibold`}>{kw.count}</span>
                                </div>
                            )) : <p className={t.textMuted}>No hay datos suficientes.</p>}
                        </div>
                    </div>
                     <div className={`${t.bg} p-6 rounded-lg border ${t.border} lg:col-span-2`}>
                        <p className={`text-sm font-bold ${t.textMuted} mb-2`}>Servicios Más Consultados</p>
                        <div className="space-y-2">
                            {data.topServices.length > 0 ? data.topServices.map(srv => (
                                <div key={srv.text} className="flex justify-between items-center text-sm">
                                    <span className="capitalize">{srv.text}</span>
                                    <span className={`${t.primary} font-semibold`}>{srv.count}</span>
                                </div>
                            )) : <p className={t.textMuted}>No hay datos suficientes.</p>}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="text-center p-8">No se pudieron cargar los datos.</div>
            )}
        </div>
    );
}

// --- VISTA DE GESTIÓN DE EQUIPO (CON EDICIÓN Y ELIMINACIÓN) ---
function TeamView({ user, teamMembers }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { showNotification } = useNotification();
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteWhatsapp, setInviteWhatsapp] = useState('');
    // CORRECCIÓN: Se añade el rol 'revendedor' por defecto al invitar
    const [inviteRole, setInviteRole] = useState('revendedor');
    const [isInviting, setIsInviting] = useState(false);

    // Estados para los modales de edición y eliminación
    const [editingMember, setEditingMember] = useState(null);
    const [deletingMember, setDeletingMember] = useState(null);

    const userPlan = user.plan || {};
    const agentLimit = userPlan.agentLimit || 1;
    
    const currentAgents = teamMembers.filter(member => member.id !== user.uid);
    const canInvite = currentAgents.length < agentLimit;

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail || !inviteWhatsapp) {
            showNotification("El email y el número de WhatsApp son obligatorios.", "error");
            return;
        }
        setIsInviting(true);
        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const inviteTeamMember = httpsCallable(functions, 'inviteTeamMember');
            const result = await inviteTeamMember({ inviteEmail, inviteRole, inviteWhatsapp });
            showNotification(result.data.message, 'success');
            setInviteEmail('');
            setInviteWhatsapp('');
        } catch (error) {
            showNotification(error.message, 'error');
        } finally {
            setIsInviting(false);
        }
    };

    const handleUpdateRole = async () => {
        if (!editingMember) return;
        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const updateRoleFunction = httpsCallable(functions, 'updateTeamMemberRole');
            await updateRoleFunction({ memberId: editingMember.id, newRole: editingMember.role });
            showNotification('Rol actualizado con éxito.', 'success');
            setEditingMember(null);
        } catch (error) {
            showNotification(error.message, 'error');
        }
    };
    
    const handleDeleteMember = async () => {
        if (!deletingMember) return;
        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const removeMemberFunction = httpsCallable(functions, 'removeTeamMember');
            await removeMemberFunction({ memberId: deletingMember.id });
            showNotification('Agente eliminado del equipo.', 'info');
            setDeletingMember(null);
        } catch (error) {
            showNotification(error.message, 'error');
        }
    };

    return (
        <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border} space-y-8`}>
            <div>
                <h2 className={`text-2xl font-bold ${t.primary}`}>Gestión de Equipo</h2>
                <p className={t.textMuted}>Invita y administra a los miembros de tu equipo.</p>
            </div>

            <div className={`${t.bg} p-6 rounded-lg border ${t.border}`}>
                <h3 className={`text-xl font-bold ${t.secondary} mb-4`}>Miembros Actuales ({currentAgents.length + 1}/{agentLimit + 1})</h3>
                <div className="space-y-3">
                    {/* Renderiza al dueño primero */}
                    <div className={`flex justify-between items-center p-3 rounded-md ${t.bgInput}`}>
                        <div>
                            <p className="font-bold">{user.email}</p>
                            <p className={`text-sm capitalize font-semibold ${t.primary}`}>Propietario</p>
                        </div>
                    </div>
                    {/* Renderiza a los agentes */}
                    {currentAgents.map(member => (
                        <div key={member.id} className={`flex justify-between items-center p-3 rounded-md ${t.bgInput}`}>
                            <div>
                                <p className="font-bold">{member.email}</p>
                                <p className={`text-sm capitalize ${t.textMuted}`}>{member.role ? member.role.replace('_', ' ') : 'Agente'}</p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setEditingMember(member)} className="p-2 hover:text-blue-400"><EditIcon /></button>
                                <button onClick={() => setDeletingMember(member)} className="p-2 hover:text-red-400"><TrashIcon /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            
            <div className={`${t.bg} p-6 rounded-lg border ${t.border}`}>
                 <h3 className={`text-xl font-bold ${t.secondary} mb-4`}>Invitar Nuevo Miembro</h3>
                 {canInvite ? (
                    <form onSubmit={handleInvite} className="space-y-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Email del agente" className={`flex-1 px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            <input type="text" value={inviteWhatsapp} onChange={(e) => setInviteWhatsapp(e.target.value)} placeholder="WhatsApp del agente (ej: 54911...)" className={`flex-1 px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            {/* CAMBIO: Se añade la opción de rol 'revendedor' */}
                            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className={`flex-1 px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                <option value="asesor_ventas">Asesor de Ventas</option>
                                <option value="soporte">Soporte</option>
                                <option value="revendedor">Revendedor</option>
                            </select>
                            <button type="submit" disabled={isInviting} className={`flex-1 px-6 py-2 font-bold rounded-md ${t.buttonPrimary} disabled:opacity-50`}>
                                {isInviting ? "Enviando..." : "Enviar Invitación por WhatsApp"}
                            </button>
                        </div>
                    </form>
                 ) : (
                    <div className="text-center p-4 bg-yellow-500/10 rounded-md">
                        <p className="font-semibold text-yellow-400">Has alcanzado el límite de {agentLimit} agentes de tu plan.</p>
                        <p className="text-sm text-yellow-500">Mejora tu plan para invitar a más miembros.</p>
                    </div>
                 )}
            </div>

            {/* Modal para Editar Rol */}
            {editingMember && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className={`${t.bgModal} border ${t.border} p-8 rounded-lg shadow-2xl w-full max-w-md`}>
                        <h3 className={`text-2xl font-bold ${t.secondary} mb-4`}>Editar Rol</h3>
                        <p className="mb-4">Editando a: <span className={t.primary}>{editingMember.email}</span></p>
                        <div className="mb-6">
                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Nuevo Rol</label>
                            {/* CAMBIO: Se añade la opción de rol 'revendedor' al editar */}
                            <select value={editingMember.role} onChange={e => setEditingMember({...editingMember, role: e.target.value})} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                <option value="asesor_ventas">Asesor de Ventas</option>
                                <option value="soporte">Soporte</option>
                                <option value="revendedor">Revendedor</option>
                            </select>
                        </div>
                        <div className="flex justify-end gap-4">
                            <button onClick={() => setEditingMember(null)} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                            <button onClick={handleUpdateRole} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar Cambios</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para Confirmar Eliminación */}
            {deletingMember && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className={`${t.bgModal} border border-red-500/50 p-8 rounded-lg shadow-2xl w-full max-w-md`}>
                        <h3 className="text-2xl font-bold text-red-400 mb-4">Confirmar Eliminación</h3>
                        <p className={t.textMuted}>¿Estás seguro de que quieres eliminar a <span className="font-bold text-white">{deletingMember.email}</span> del equipo? Esta acción no se puede deshacer.</p>
                        <div className="flex justify-end gap-4 pt-6">
                            <button onClick={() => setDeletingMember(null)} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                            <button onClick={handleDeleteMember} className="px-4 py-2 bg-red-600 text-white font-bold rounded-md hover:bg-red-500">Eliminar Agente</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- COMPONENTE DE AUTENTICACIÓN ---
function AuthComponent({ onLoginSuccess }) {
    const { auth, db } = initializeFirebase();
    const { showNotification } = useNotification();
    const { theme, themes } = useTheme();
    const t = themes[theme] || themes.netflix_dark;

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [telegram, setTelegram] = useState('');
    const [isLogin, setIsLogin] = useState(true);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') === 'register') {
            setIsLogin(false);
        }
    }, []);

    const handleAuthAction = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (isLogin) {
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                if (!whatsapp && !telegram) {
                    setError("Debes proporcionar un número de WhatsApp o un usuario de Telegram.");
                    setLoading(false);
                    return;
                }
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                await runTransaction(db, async (transaction) => {
                    const metadataRef = doc(db, 'app-metadata', 'user-counter');
                    const metadataDoc = await transaction.get(metadataRef);

                    let isAdmin = false;
                    let newCount = 1;
                    if (!metadataDoc.exists() || metadataDoc.data().count === 0) {
                        isAdmin = true;
                    } else {
                        newCount = metadataDoc.data().count + 1;
                    }

                    transaction.set(metadataRef, { count: newCount }, { merge: true });

                    const userDocRef = doc(db, 'users', user.uid);
                    const newUser = {
                        email: user.email,
                        uid: user.uid,
                        createdAt: serverTimestamp(),
                        isAdmin: isAdmin,
                        role: isAdmin ? 'owner' : 'asesor_ventas', // Rol por defecto
                        teamId: isAdmin ? user.uid : null,
                        whatsapp: whatsapp,
                        telegram: telegram,
                        resellerBalance: null,
                    };
                    transaction.set(userDocRef, newUser);
                    if (onLoginSuccess) onLoginSuccess(newUser);
                });
                showNotification('¡Registro exitoso!', 'success');
            }
        } catch (err) {
            const friendlyError = err.message.replace('Firebase: ', '');
            setError(friendlyError);
            showNotification(friendlyError, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            await sendPasswordResetEmail(auth, email);
            showNotification('Se ha enviado un enlace para restablecer tu contraseña a tu correo.', 'info');
            setShowForgotPassword(false);
        } catch (err) {
            const friendlyError = err.message.replace('Firebase: ', '');
            setError(friendlyError);
            showNotification(friendlyError, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`flex items-center justify-center min-h-screen ${t.bg} ${t.text} p-4`} style={{ fontFamily: fonts[t.name] }}>
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-2xl shadow-md border border-gray-300">
                <div className="text-center">
                    <img src="https://solu7ec.com/logos/streamdesk.png" alt="StreamDesk Logo" className="mx-auto h-24 w-auto mb-6" />
                    <h1 className="text-3xl font-bold text-blue-600 tracking-wider">StreamDesk Panel</h1>
                    <p className="mt-2 text-black">{isLogin ? 'Acceso de Operador' : 'Registro de Nuevo Operador'}</p>
                </div>

                {!showForgotPassword ? (
                    <form onSubmit={handleAuthAction} className="space-y-4">
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-800" placeholder="tucorreo@email.com" />
                        {!isLogin && (
                            <>
                                <input type="text" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-800" placeholder="Número de WhatsApp (ej: 54911...)" />
                                <input type="text" value={telegram} onChange={(e) => setTelegram(e.target.value)} className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-800" placeholder="Usuario de Telegram (sin @)" />
                            </>
                        )}
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-800" placeholder="************" />
                        {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                        <button type="submit" disabled={loading} className="w-full py-3 font-bold rounded-md disabled:opacity-50 transition-all bg-blue-600 text-white hover:bg-blue-700">
                            {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesión' : 'Registrarse')}
                        </button>
                        <p className="text-center text-sm">
                            <button onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-gray-500 hover:text-black transition-colors">
                                {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia Sesión'}
                            </button>
                        </p>
                        {isLogin && (
                            <p className="text-center text-sm">
                                <button onClick={() => setShowForgotPassword(true)} className="text-gray-500 hover:text-black transition-colors">
                                    Olvidé mi contraseña
                                </button>
                            </p>
                        )}
                    </form>
                ) : (
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                        <p className="text-center text-sm text-gray-500">Ingresa tu correo para recibir un enlace de recuperación.</p>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full px-4 py-2 bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-gray-800" placeholder="tucorreo@email.com" />
                        {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                        <button type="submit" disabled={loading} className="w-full py-3 font-bold rounded-md disabled:opacity-50 transition-all bg-blue-600 text-white hover:bg-blue-700">
                            {loading ? 'Enviando...' : 'Restablecer Contraseña'}
                        </button>
                        <p className="text-center text-sm">
                            <button onClick={() => setShowForgotPassword(false)} className="text-gray-500 hover:text-black transition-colors">
                                Volver al inicio de sesión
                            </button>
                        </p>
                    </form>
                )}

                <div className="text-center pt-4 border-t border-gray-300">
                    <a href="https://streamdesk.solu7ec.com/info" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors">
                        <InfoIcon />
                        Planes e Información
                    </a>
                </div>
                <div className="text-center mt-4">
                    <a href="https://solu7ec.com" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-blue-600">
                        por solu7ec.com
                    </a>
                </div>
            </div>
        </div>
    );
}

// --- PANEL DE ADMINISTRADOR (CON PERMISO DE IA) ---
function AdminDashboard({ user, handleLogout }) {
    const { theme, themes, font, fonts } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();

    const [users, setUsers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [selectedPlanId, setSelectedPlanId] = useState('');

    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [planFormData, setPlanFormData] = useState({ name: '', price: 0, agentLimit: 1, campaignLimit: 0, hasAnalytics: false, durationDays: 30, hasAiSuggestions: false });

    const [currentPage, setCurrentPage] = useState(1);
    const USERS_PER_PAGE = 6;

    useEffect(() => {
        const fetchAdminData = async () => {
            setLoading(true);
            try {
                const usersQuery = query(collection(db, 'users'), where('isAdmin', '==', false));
                const usersUnsub = onSnapshot(usersQuery, snapshot => {
                    setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                });

                const plansQuery = query(collection(db, 'plans'));
                const plansUnsub = onSnapshot(plansQuery, snapshot => {
                    setPlans(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                });
                
                setLoading(false);
                return () => { usersUnsub(); plansUnsub(); };
            } catch (error) {
                console.error("Error fetching admin data:", error);
                showNotification("Error al cargar datos de administrador.", "error");
                setLoading(false);
            }
        };

        fetchAdminData();
    }, [db, showNotification]);

    const openUserModal = (userToEdit) => {
        setCurrentUser(userToEdit);
        setSelectedPlanId(userToEdit.planId || '');
        setIsUserModalOpen(true);
    };

    const handleSaveSubscription = async () => {
        if (!currentUser || !selectedPlanId) {
            showNotification("Por favor, selecciona un plan.", "error");
            return;
        }
        
        const selectedPlan = plans.find(p => p.id === selectedPlanId);
        if (!selectedPlan || !selectedPlan.durationDays) {
            showNotification("El plan seleccionado no tiene una duración definida. Edita el plan y añade una duración en días.", "error");
            return;
        }

        try {
            const expirationDate = new Date();
            expirationDate.setDate(expirationDate.getDate() + parseInt(selectedPlan.durationDays, 10));

            const userRef = doc(db, 'users', currentUser.id);
            await updateDoc(userRef, {
                planId: selectedPlanId,
                subscriptionStatus: 'active',
                subscriptionExpiresAt: Timestamp.fromDate(expirationDate)
            });
            showNotification(`Plan actualizado para ${currentUser.email}`, 'success');
            setIsUserModalOpen(false);
        } catch (error) {
            console.error("Error updating user plan:", error);
            showNotification("Error al actualizar la suscripción.", "error");
        }
    };

    const openPlanModal = (plan = null) => {
        const defaultState = { name: '', price: 0, agentLimit: 1, campaignLimit: 0, hasAnalytics: false, durationDays: 30, hasAiSuggestions: false };
        if (plan) {
            setCurrentPlan(plan);
            setPlanFormData({ ...defaultState, ...plan });
        } else {
            setCurrentPlan(null);
            setPlanFormData(defaultState);
        }
        setIsPlanModalOpen(true);
    };

    const handleSavePlan = async () => {
        if (!planFormData.name || planFormData.price < 0 || !planFormData.durationDays) {
            showNotification("Nombre, precio y duración son obligatorios.", "error");
            return;
        }
        try {
            const dataToSave = {
                ...planFormData,
                price: parseFloat(planFormData.price) || 0,
                agentLimit: parseInt(planFormData.agentLimit) || 1,
                campaignLimit: parseInt(planFormData.campaignLimit) || 0,
                durationDays: parseInt(planFormData.durationDays) || 30,
            };

            if (currentPlan) {
                const planRef = doc(db, 'plans', currentPlan.id);
                await updateDoc(planRef, dataToSave);
                showNotification('Plan actualizado con éxito.', 'success');
            } else {
                await addDoc(collection(db, 'plans'), dataToSave);
                showNotification('Nuevo plan creado con éxito.', 'success');
            }
            setIsPlanModalOpen(false);
        } catch (error) {
            console.error("Error saving plan:", error);
            showNotification("Error al guardar el plan.", "error");
        }
    };

    const getPlanName = (planId) => plans.find(p => p.id === planId)?.name || 'Sin Plan';
    const getStatus = (user) => {
        if (user.subscriptionStatus === 'inactive') return { text: 'Inactivo', color: 'bg-gray-500' };
        if (!user.subscriptionExpiresAt) return { text: 'Sin Suscripción', color: 'bg-yellow-500' };
        if (user.subscriptionExpiresAt.toDate() < new Date()) return { text: 'Vencido', color: 'bg-red-500' };
        return { text: 'Activo', color: 'bg-green-500' };
    };

    const indexOfLastUser = currentPage * USERS_PER_PAGE;
    const indexOfFirstUser = indexOfLastUser - USERS_PER_PAGE;
    const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(users.length / USERS_PER_PAGE);

    return (
        <div className={`min-h-screen ${t.bg} ${t.text} p-4 sm:p-6 lg:p-8`} style={{ fontFamily: fonts[font] }}>
            <header className={`flex flex-col sm:flex-row justify-between items-center mb-8 pb-4 border-b ${t.border}`}>
                <div className="text-center sm:text-left mb-4 sm:mb-0">
                    <h1 className={`text-3xl font-bold ${t.primary}`}>Panel de Super-Administrador</h1>
                    <p className={t.secondary}>Gestión de Suscripciones de StreamDesk</p>
                </div>
                <button onClick={handleLogout} className={`flex items-center px-4 py-2 rounded-md ${t.buttonSecondary}`}><LogoutIcon /><span className="ml-2 hidden sm:inline">Cerrar Sesión</span></button>
            </header>

            <main className="space-y-8">
                <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className={`text-2xl font-bold ${t.primary}`}>Planes de Suscripción</h2>
                        <button onClick={() => openPlanModal()} className={`flex items-center px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}><PlusCircleIcon /> <span className="ml-2">Nuevo Plan</span></button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className={`border-b ${t.border}`}>
                                    <th className="p-2">Nombre</th>
                                    <th className="p-2">Precio</th>
                                    <th className="p-2">Duración</th>
                                    <th className="p-2">Límites</th>
                                    <th className="p-2 text-center">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="5" className="text-center p-4">Cargando...</td></tr>
                                ) : (
                                    plans.map(p => (
                                        <tr key={p.id} className={`border-b ${t.border} hover:${t.bgInput} text-sm`}>
                                            <td className={`p-2 font-semibold ${t.secondary}`}>{p.name}</td>
                                            <td className={`p-2 ${t.textMuted}`}>${p.price}</td>
                                            <td className={`p-2 ${t.textMuted}`}>{p.durationDays} días</td>
                                            <td className={`p-2 ${t.textMuted}`}>Agentes: {p.agentLimit}, Campañas: {p.campaignLimit}</td>
                                            <td className="p-2 text-center"><button onClick={() => openPlanModal(p)} className={`p-2 rounded-md ${t.buttonSecondary}`}><EditIcon /></button></td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                    <h2 className={`text-2xl font-bold ${t.primary} mb-4`}>Usuarios Registrados</h2>
                    {loading ? <p>Cargando usuarios...</p> : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {currentUsers.map(u => {
                                    const status = getStatus(u);
                                    return (
                                        <div key={u.id} className={`${t.bg} p-4 rounded-lg border ${t.border} flex flex-col`}>
                                            <div className="flex-grow">
                                                <p className={`font-bold text-lg ${t.secondary} break-all`}>{u.email}</p>
                                                <p className={`text-sm ${t.textMuted}`}>Plan: {getPlanName(u.planId)}</p>
                                                <p className={`text-sm ${t.textMuted}`}>Vence: {u.subscriptionExpiresAt?.toDate().toLocaleDateString() || 'N/A'}</p>
                                            </div>
                                            <div className="mt-4 pt-4 border-t ${t.border} flex justify-between items-center">
                                                <span className={`px-2 py-1 text-xs font-bold rounded-full text-white ${status.color}`}>{status.text}</span>
                                                <button onClick={() => openUserModal(u)} className={`px-4 py-2 text-sm font-bold rounded-md ${t.buttonSecondary}`}>Gestionar</button>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            {totalPages > 1 && (
                                <div className="mt-6 flex justify-center items-center space-x-2">
                                    <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className={`px-4 py-2 rounded-md ${t.buttonSecondary} disabled:opacity-50`}>Anterior</button>
                                    <span className={t.textMuted}>Página {currentPage} de {totalPages}</span>
                                    <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className={`px-4 py-2 rounded-md ${t.buttonSecondary} disabled:opacity-50`}>Siguiente</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>

            {isUserModalOpen && currentUser && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className={`${t.bgModal} border ${t.border} p-8 rounded-lg shadow-2xl w-full max-w-md`}>
                        <h3 className={`text-2xl font-bold ${t.secondary} mb-4`}>Gestionar Suscripción</h3>
                        <p className="mb-4">Usuario: <span className={t.primary}>{currentUser.email}</span></p>
                        <div className="mb-6">
                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Asignar Plan (la fecha de vencimiento se calculará automáticamente)</label>
                            <select value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                <option value="">-- Seleccionar Plan --</option>
                                {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.durationDays} días)</option>)}
                            </select>
                        </div>
                        <div className="flex justify-end gap-4">
                            <button onClick={() => setIsUserModalOpen(false)} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                            <button onClick={handleSaveSubscription} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar Cambios</button>
                        </div>
                    </div>
                </div>
            )}

            {isPlanModalOpen && (
                 <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className={`${t.bgModal} border ${t.border} p-8 rounded-lg shadow-2xl w-full max-w-md`}>
                        <h3 className={`text-2xl font-bold ${t.secondary} mb-4`}>{currentPlan ? 'Editar Plan' : 'Crear Nuevo Plan'}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Nombre del Plan</label>
                                <input type="text" value={planFormData.name} onChange={(e) => setPlanFormData({...planFormData, name: e.target.value})} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                             <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Precio (USD)</label>
                                <input type="number" value={planFormData.price} onChange={(e) => setPlanFormData({...planFormData, price: parseFloat(e.target.value) || 0})} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Duración (días)</label>
                                <input type="number" value={planFormData.durationDays} onChange={(e) => setPlanFormData({...planFormData, durationDays: parseInt(e.target.value) || 30})} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                             <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Límite de Agentes</label>
                                <input type="number" value={planFormData.agentLimit} onChange={(e) => setPlanFormData({...planFormData, agentLimit: parseInt(e.target.value) || 1})} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                             <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Límite de Campañas/Mes</label>
                                <input type="number" value={planFormData.campaignLimit} onChange={(e) => setPlanFormData({...planFormData, campaignLimit: parseInt(e.target.value) || 0})} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="checkbox" id="hasAnalytics" checked={planFormData.hasAnalytics} onChange={(e) => setPlanFormData({...planFormData, hasAnalytics: e.target.checked})} className="accent-pink-500 h-4 w-4" />
                                <label htmlFor="hasAnalytics" className={`text-sm font-bold ${t.textMuted}`}>Incluye Analíticas</label>
                            </div>
                            {/* --- NUEVO CAMPO DE PERMISO --- */}
                            <div className="flex items-center gap-2">
                                <input type="checkbox" id="hasAiSuggestions" checked={planFormData.hasAiSuggestions} onChange={(e) => setPlanFormData({...planFormData, hasAiSuggestions: e.target.checked})} className="accent-pink-500 h-4 w-4" />
                                <label htmlFor="hasAiSuggestions" className={`text-sm font-bold ${t.textMuted}`}>Permitir Sugerencias con IA</label>
                            </div>
                        </div>
                        <div className="flex justify-end gap-4 mt-6">
                            <button onClick={() => setIsPlanModalOpen(false)} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                            <button onClick={handleSavePlan} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar Plan</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- VISTA DE SUSCRIPCIÓN (CORREGIDA) ---
function SubscriptionView({ user, handleLogout }) {
    const { theme, themes } = useTheme();
    const t = themes['netflix_dark']; // Forzar tema oscuro para esta vista
    const { db } = initializeFirebase();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adminContact, setAdminContact] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Obtener todos los planes disponibles
                const plansQuery = query(collection(db, 'plans'));
                const plansSnap = await getDocs(plansQuery);
                const availablePlans = plansSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setPlans(availablePlans);

                // 2. --- CORRECCIÓN: Obtener el contacto del admin desde la nueva ruta pública ---
                const settingsRef = doc(db, 'app-settings', 'public');
                const settingsSnap = await getDoc(settingsRef);
                if (settingsSnap.exists() && settingsSnap.data().adminWhatsapp) {
                    setAdminContact(settingsSnap.data().adminWhatsapp);
                } else {
                    console.warn("No se encontró el WhatsApp del administrador en app-settings/public");
                }

            } catch (error) {
                console.error("Error fetching subscription data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [db]);

    const createWhatsAppLink = (planName, planPrice) => {
        if (!adminContact) return '#';
        const message = `Hola, estoy interesado/a en activar el plan "${planName}" de Streamdesk por $${planPrice}. Mi correo es ${user.email}.`;
        return `https://wa.me/${adminContact}?text=${encodeURIComponent(message)}`;
    };

    return (
        <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${t.bg} ${t.text}`}>
            <div className={`w-full max-w-4xl p-8 text-center ${t.bgMuted} rounded-lg shadow-2xl ${t.shadow} border ${t.border}`}>
                <h1 className={`text-3xl font-bold text-red-500 mb-2`}>Tu Suscripción ha Expirado</h1>
                <p className={t.textMuted}>Para continuar usando StreamDesk, por favor renueva tu plan.</p>
                
                {loading ? (
                    <p className="mt-8 animate-pulse">Cargando planes...</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 text-left">
                        {plans.map(plan => (
                            <div key={plan.id} className={`${t.bg} p-6 rounded-lg border ${t.border} flex flex-col`}>
                                <h2 className={`text-2xl font-bold ${t.primary}`}>{plan.name}</h2>
                                <p className="text-4xl font-bold my-4">${plan.price}<span className="text-lg font-normal">/{plan.durationDays} días</span></p>
                                <ul className="space-y-2 text-sm flex-grow">
                                    <li className="flex items-center gap-2"><UsersIcon className="h-4 w-4 text-green-500" /> {plan.agentLimit} Agente(s)</li>
                                    <li className="flex items-center gap-2"><MegaphoneIcon className="h-4 w-4 text-green-500" /> {plan.campaignLimit} Campañas/mes</li>
                                    <li className={`flex items-center gap-2 ${plan.hasAnalytics ? 'text-green-500' : 'text-red-500'}`}>{plan.hasAnalytics ? '✓' : '✗'} Analíticas</li>
                                    <li className={`flex items-center gap-2 ${plan.hasAiSuggestions ? 'text-green-500' : 'text-red-500'}`}>{plan.hasAiSuggestions ? '✓' : '✗'} Sugerencias con IA</li>
                                </ul>
                                <a 
                                    href={createWhatsAppLink(plan.name, plan.price)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className={`block text-center mt-6 w-full py-3 font-bold rounded-md transition-all ${t.buttonPrimary}`}
                                >
                                    Contactar para Activar
                                </a>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-8 pt-6 border-t ${t.border} flex flex-col sm:flex-row items-center justify-between gap-4">
                     <a href="https://streamdesk.solu7ec.com/info" target="_blank" rel="noopener noreferrer" className={`text-sm ${t.textMuted} hover:${t.primary}`}>
                        Más información sobre los planes
                    </a>
                    <button onClick={handleLogout} className="flex items-center px-4 py-2 text-sm rounded-md bg-gray-500/20 hover:bg-gray-500/40">
                        <LogoutIcon /><span className="ml-2">Cerrar Sesión</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

const getPaginationItems = (totalPages, currentPage) => {
    // Define el número máximo de páginas centrales que quieres mostrar.
    const maxVisiblePages = 3;

    // Si el total de páginas es 5 o menos, muéstralas todas sin puntos suspensivos.
    if (totalPages <= 5) {
        return [...Array(totalPages).keys()].map(i => i + 1);
    }

    const pages = [];

    // Lógica para cuando estás cerca del principio (páginas 1, 2 o 3)
    if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
    } 
    // Lógica para cuando estás cerca del final
    else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
    } 
    // Lógica para cuando estás en el medio
    else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }

    return pages;
};

function ClientCardView({ user, clients, sales, services, inventory, messageTemplates, businessProfile }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const teamId = user.teamId || user.uid;
    const userRole = user.role || 'owner';

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, id: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [detailsModal, setDetailsModal] = useState({ isOpen: false, client: null, activeServices: [], historicalServices: [], loading: false });
    const [sendingLink, setSendingLink] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 9;

    // --- LÓGICA DE FILTRADO PARA REVENDEDORES ---
    // Si el usuario es revendedor, solo ve los clientes que creó. Si es owner, ve todos.
    const visibleClients = user.role === 'revendedor'
        ? clients.filter(c => c.createdBy === user.uid)
        : clients;
        
    const handleSendLoginLink = async (clientId) => {
        setSendingLink(clientId);
        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const generateLink = httpsCallable(functions, 'generateClientLoginLink');
            const result = await generateLink({ clientId });
            showNotification(result.data.message, 'success');
        } catch (error) {
            console.error("Error al enviar enlace de acceso:", error);
            showNotification(error.message, 'error');
        } finally {
            setSendingLink(null);
        }
    };

    const openDetailsModal = async (client) => {
        setDetailsModal({ isOpen: true, client, activeServices: [], historicalServices: [], loading: true });

        try {
            const salesQuery = query(collection(db, 'users', teamId, 'sales'), where('clientId', '==', client.id));
            const salesSnap = await getDocs(salesQuery);
            const clientSales = salesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const servicesMap = new Map(services.map(s => [s.id, s]));

            const enrichedSales = await Promise.all(clientSales.map(async (sale) => {
                const service = servicesMap.get(sale.serviceId);
                let accountDetails = 'N/A';

                if (service && inventory[sale.serviceId]) {
                    const inventoryIdsToFind = sale.inventoryIds || (sale.inventoryId ? [sale.inventoryId] : []);
                    if (inventoryIdsToFind.length > 0) {
                        const foundItems = inventoryIdsToFind.map(id =>
                            inventory[sale.serviceId].find(inv => inv.id === id)
                        ).filter(Boolean);

                        if (foundItems.length > 0) {
                            accountDetails = foundItems.map(item =>
                                item.email ? `${item.email}${item.profile ? ` - Perfil: ${item.profile}` : ''}` : 'ID No Encontrado'
                            ).join(', ');
                        }
                    }
                }
                return {
                    ...sale,
                    serviceName: service ? service.name : 'Servicio Desconocido',
                    accountDetails: accountDetails,
                };
            }));

            const activeServices = [];
            const historicalServices = [];
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            enrichedSales.forEach(sale => {
                const expires = sale.expiresAt?.toDate();
                if (sale.status === 'suspended') {
                    historicalServices.push({ ...sale, determinedStatus: 'Suspendido' });
                } else if (expires && expires < today) {
                    historicalServices.push({ ...sale, determinedStatus: 'Vencido' });
                } else if (expires && expires >= today) {
                    activeServices.push(sale);
                } else {
                    historicalServices.push({ ...sale, determinedStatus: 'Inválido' });
                }
            });

            setDetailsModal({ isOpen: true, client, activeServices, historicalServices, loading: false });
        } catch (error) {
            console.error("Error fetching client details:", error);
            showNotification("Error al cargar los detalles del cliente.", "error");
            setDetailsModal({ isOpen: false, client: null, activeServices: [], historicalServices: [], loading: false });
        }
    };

    const closeDetailsModal = () => {
        setDetailsModal({ isOpen: false, client: null, activeServices: [], historicalServices: [] });
    };

    const openModal = (item = null) => {
        setCurrentItem(item);
        const initialData = clientFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {});
        setFormData(item ? { ...item } : initialData);
        setIsModalOpen(true);
    };

    const closeModal = () => { setIsModalOpen(false); setCurrentItem(null); };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (currentItem) {
                await updateDoc(doc(db, 'users', teamId, 'clients', currentItem.id), formData);
                showNotification('Cliente actualizado!', 'success');
            } else {
                const dataToSave = { ...formData, createdAt: serverTimestamp(), createdBy: user.uid };
                await addDoc(collection(db, 'users', teamId, 'clients'), dataToSave);
                showNotification('Cliente añadido!', 'success');
            }
            closeModal();
        } catch (error) {
            console.error(`Error guardando cliente:`, error);
            showNotification(`Error guardando cliente`, 'error');
        }
    };

    const handleDeleteRequest = (id) => setConfirmDeleteModal({ isOpen: true, id: id });

    const handleConfirmDelete = async () => {
        if (confirmDeleteModal.id) {
            try {
                await deleteDoc(doc(db, 'users', teamId, 'clients', confirmDeleteModal.id));
                showNotification('Cliente eliminado.', 'info');
            } catch (error) {
                showNotification('Error eliminando cliente', 'error');
            }
        }
        setConfirmDeleteModal({ isOpen: false, id: null });
    };
    
    // --- LÓGICA PARA GENERAR ENLACES DE WHATSAPP ---
    const generateActionLink = (actionType, sale, client) => {
        const service = services.find(s => s.id === sale.serviceId);
        if (!client || !service || !inventory[sale.serviceId]) return '#';

        const inventoryIds = sale.inventoryIds || (sale.inventoryId ? [sale.inventoryId] : []);
        const inventoryItems = inventoryIds.map(id => inventory[sale.serviceId].find(i => i.id === id)).filter(Boolean);
        if (inventoryItems.length === 0) return '#';

        const template = messageTemplates[actionType] || '';
        const allAccountDetails = inventoryItems.map(item => {
            return `Email: ${item.email}\nContraseña: ${item.password}\nPerfil: ${item.profile}\nPIN: ${item.pin}`;
        }).join('\n\n---\n\n');

        const expirationDate = sale.expiresAt?.toDate ? sale.expiresAt.toDate().toLocaleDateString() : 'N/A';
        const businessContact = businessProfile.whatsapp || '';

        let message = template
            .replace(/{cliente}/g, client.name)
            .replace(/{servicio}/g, service.name)
            .replace(/{vencimiento}/g, expirationDate)
            .replace(/{datos_cuenta}/g, allAccountDetails)
            .replace(/{instrucciones}/g, inventoryItems[0]?.instructions || '')
            .replace(/{mi_negocio}/g, businessProfile.name || 'tu proveedor')
            .replace(/{mi_contacto}/g, businessContact);
        
        return `https://wa.me/${client.contact}?text=${encodeURIComponent(message)}`;
    };

    const filteredClients = visibleClients.filter(client =>
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.contact && client.contact.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const getClientStats = (clientId) => {
        const clientSales = sales.filter(s => s.clientId === clientId);
        const today = new Date();
        const active = clientSales.filter(s => s.expiresAt?.toDate() > today && s.status !== 'suspended').length;
        const history = clientSales.length - active;
        return { active, history };
    };

    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = filteredClients.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const paginationItems = getPaginationItems(totalPages, currentPage);

    return (
        <>
            <div className={`${t.bgMuted} p-4 sm:p-6 rounded-lg border ${t.border}`}>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h2 className={`text-2xl font-bold ${t.primary}`}>Mis Clientes</h2>
                    <div className="relative w-full sm:w-auto">
                        <input type="text" placeholder="Buscar cliente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full sm:w-64 pl-10 pr-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SearchIcon /></div>
                    </div>
                    {(userRole === 'owner' || userRole === 'asesor_ventas' || userRole === 'revendedor') && (
                        <button onClick={() => openModal()} className={`flex items-center px-4 py-2 font-bold rounded-md w-full sm:w-auto justify-center ${t.buttonPrimary}`}><PlusCircleIcon /> <span className="ml-2">Añadir Cliente</span></button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {currentItems.map(client => {
                        const stats = getClientStats(client.id);
                        return (
                            <div key={client.id} className={`${t.bg} p-4 rounded-lg border ${t.border} flex flex-col justify-between shadow-lg hover:shadow-xl transition-shadow duration-300`}>
                                <div>
                                    <div className="flex items-start justify-between mb-3">
                                        <h3 className={`text-xl font-bold ${t.secondary}`}>{client.name}</h3>
                                        <div className={`flex items-center text-xs ${t.primary} bg-cyan-500/10 px-2 py-1 rounded-full`}>
                                            <UsersIcon className="h-4 w-4 mr-1" />
                                            Cliente
                                        </div>
                                    </div>
                                    <p className={`text-sm ${t.textMuted} mb-1 break-all`}>Contacto: <span className="font-semibold">{client.contact}</span></p>
                                    <p className={`text-xs ${t.textMuted} mb-4`}>Nota: {client.note || 'N/A'}</p>
                                </div>

                                <div className={`border-t ${t.border} pt-3 mt-auto`}>
                                    <div className="flex justify-around text-center mb-4">
                                        <div>
                                            <p className="text-2xl font-bold text-green-400">{stats.active}</p>
                                            <p className={`text-xs ${t.textMuted}`}>Activas</p>
                                        </div>
                                        <div>
                                            <p className="text-2xl font-bold text-gray-500">{stats.history}</p>
                                            <p className={`text-xs ${t.textMuted}`}>Historial</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-center gap-2">
                                        <button onClick={() => openDetailsModal(client)} title="Ver Detalles" className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm rounded-md ${t.buttonSecondary}`}><InfoIcon /> Detalles</button>
                                        <button
                                            onClick={() => handleSendLoginLink(client.id)}
                                            disabled={sendingLink === client.id}
                                            title="Enviar Acceso al Portal"
                                            className={`p-2 rounded-md ${t.buttonSecondary} disabled:opacity-50`}
                                        >
                                            {sendingLink === client.id ? <ClockIcon className="animate-spin" /> : <LinkIcon />}
                                        </button>
                                        {(userRole === 'owner' || userRole === 'asesor_ventas') && (
                                            <>
                                                <button onClick={() => openModal(client)} title="Editar" className={`p-2 rounded-md ${t.buttonSecondary}`}><EditIcon /></button>
                                                <button onClick={() => handleDeleteRequest(client.id)} title="Eliminar" className={`p-2 rounded-md bg-red-500/20 text-red-400 hover:bg-red-500/40`}><TrashIcon /></button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {totalPages > 1 && (
                    <div className="mt-8 flex justify-center items-center space-x-2">
                        <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className={`px-4 py-2 rounded-md ${t.buttonSecondary} disabled:opacity-50`}>Anterior</button>
                        
                        {paginationItems.map((item, index) => {
                            if (item === '...') {
                                return <span key={index} className="px-4 py-2 text-gray-500">...</span>;
                            }
                            return (
                                <button
                                    key={index}
                                    onClick={() => paginate(item)}
                                    className={`px-4 py-2 rounded-md ${currentPage === item ? t.buttonPrimary : t.buttonSecondary}`}
                                >
                                    {item}
                                </button>
                            );
                        })}
                        
                        <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className={`px-4 py-2 rounded-md ${t.buttonSecondary} disabled:opacity-50`}>Siguiente</button>
                    </div>
                )}
            </div>

            <Modal isOpen={isModalOpen} onClose={closeModal} title={currentItem ? 'Editar Cliente' : 'Añadir Cliente'}>
                <form onSubmit={handleSave} className="space-y-4">
                    {clientFields.map(field => (
                        <div key={field.name}>
                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>{field.label}</label>
                            <input
                                type={field.type || 'text'}
                                placeholder={field.placeholder}
                                value={formData[field.name] || ''}
                                onChange={e => setFormData({ ...formData, [field.name]: e.target.value })}
                                required={field.required !== false}
                                className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md focus:ring-2 ${t.borderFocus}`}
                            />
                        </div>
                    ))}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={closeModal} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                        <button type="submit" className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar</button>
                    </div>
                </form>
            </Modal>
            
            <Modal isOpen={confirmDeleteModal.isOpen} onClose={() => setConfirmDeleteModal({ isOpen: false, id: null })} title="Confirmar Eliminación">
                <p className={t.textMuted}>¿Estás seguro de que quieres eliminar este cliente?</p>
                <div className="flex justify-end gap-4 pt-6">
                    <button onClick={() => setConfirmDeleteModal({ isOpen: false, id: null })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                    <button onClick={handleConfirmDelete} className="px-4 py-2 bg-red-600 text-white font-bold rounded-md hover:bg-red-500">Eliminar</button>
                </div>
            </Modal>

            <Modal isOpen={detailsModal.isOpen} onClose={closeDetailsModal} title="Detalles del Cliente">
                <p className="mb-6 text-cyan-400">{detailsModal.client?.name}</p>
                <div className="max-h-[60vh] overflow-y-auto space-y-6">
                    {detailsModal.loading ? (<p>Cargando servicios del cliente...</p>) : (
                        <>
                            <div>
                                <h4 className={`text-lg font-bold ${t.primary} mb-2`}>Servicios Activos</h4>
                                {detailsModal.activeServices.length > 0 ? (
                                    <div className="space-y-3">
                                        {detailsModal.activeServices.map(service => (
                                            <div key={service.id} className={`${t.bg} p-3 rounded-md border ${t.border}`}>
                                                <p className="font-bold">{service.serviceName}</p>
                                                <p className="text-sm">{service.accountDetails}</p>
                                                <p className="text-xs">Vence: {service.expiresAt?.toDate().toLocaleDateString()}</p>
                                                <div className="flex items-center gap-2 mt-2 border-t ${t.border} pt-2">
                                                    <a href={generateActionLink('deliver', service, detailsModal.client)} target="_blank" rel="noopener noreferrer" title="Entregar/Recordar Datos" className="p-2 bg-green-500/20 text-green-400 rounded-md hover:bg-green-500/40"><SparklesIcon /></a>
                                                    <a href={generateActionLink('renewal_warning', service, detailsModal.client)} target="_blank" rel="noopener noreferrer" title="Avisar Vencimiento" className="p-2 bg-yellow-500/20 text-yellow-400 rounded-md hover:bg-yellow-500/40"><BellIcon /></a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (<p className={t.textMuted}>Este cliente no tiene servicios activos.</p>)}
                            </div>
                            <div>
                                <h4 className={`text-lg font-bold text-red-400 mb-2`}>Historial</h4>
                                {detailsModal.historicalServices.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left text-sm">
                                            <thead><tr className={`border-b ${t.border}`}><th className="p-2">Servicio</th><th className="p-2">Cuenta/Perfil</th><th className="p-2">Estado</th><th className="p-2">Venció</th></tr></thead>
                                            <tbody>
                                                {detailsModal.historicalServices.map(service => (
                                                    <tr key={service.id} className={`border-b ${t.border} hover:${t.bgInput} opacity-60`}>
                                                        <td className="p-2 font-bold">{service.serviceName}</td><td className="p-2">{service.accountDetails}</td><td className="p-2 capitalize">{service.determinedStatus}</td><td className="p-2">{service.expiresAt?.toDate().toLocaleDateString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (<p className={t.textMuted}>No hay historial de servicios.</p>)}
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
}

// --- VISTA SIMPLE DE CRUD (USANDO EL NUEVO MODAL REUTILIZABLE) ---
function SimpleCrudView({ user, collectionName, title, fields, itemNoun }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [formData, setFormData] = useState({});
    const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, id: null });
    const userRole = user.role || 'owner';
    const teamId = user.teamId || user.uid;

    const initialData = fields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {});

    useEffect(() => {
        const q = query(collection(db, 'users', teamId, collectionName), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (err) => {
            console.error(`Error fetching ${collectionName}:`, err);
            showNotification(`Error al cargar ${title}.`, 'error');
            setLoading(false);
        });
        return () => unsubscribe();
    }, [teamId, collectionName, db, showNotification, title]);

    const openModal = (item = null) => {
        setCurrentItem(item);
        setFormData(item ? { ...item } : initialData);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentItem(null);
        setFormData(initialData);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (currentItem) {
                await updateDoc(doc(db, 'users', teamId, collectionName, currentItem.id), formData);
                showNotification(`${itemNoun} actualizado!`, 'success');
            } else {
                await addDoc(collection(db, 'users', teamId, collectionName), { ...formData, createdAt: serverTimestamp() });
                showNotification(`${itemNoun} añadido!`, 'success');
            }
            closeModal();
        } catch (error) {
            console.error(`Error guardando ${itemNoun}:`, error);
            showNotification(`Error guardando ${itemNoun}`, 'error');
        }
    };

    const handleDeleteRequest = (id) => setConfirmDeleteModal({ isOpen: true, id: id });

    const handleConfirmDelete = async () => {
        if (confirmDeleteModal.id) {
            try {
                await deleteDoc(doc(db, 'users', teamId, collectionName, confirmDeleteModal.id));
                showNotification(`${itemNoun} eliminado.`, 'info');
            } catch (error) {
                showNotification(`Error eliminando ${itemNoun}`, 'error');
            }
        }
        setConfirmDeleteModal({ isOpen: false, id: null });
    };

    return (
        <>
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className={`text-2xl font-bold ${t.primary}`}>{title}</h2>
                    {(userRole === 'owner' || userRole === 'asesor_ventas') && (
                         <button onClick={() => openModal()} className={`flex items-center px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}><PlusCircleIcon /> <span className="ml-2">Añadir {itemNoun}</span></button>
                    )}
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className={`border-b ${t.border}`}>
                                {fields.map(field => <th key={field.name} className="p-2">{field.label}</th>)}
                                {(userRole === 'owner' || userRole === 'asesor_ventas') && <th className="p-2">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={fields.length + 1} className="text-center p-4">Cargando...</td></tr>
                            ) : (
                                items.map(item => (
                                    <tr key={item.id} className={`border-b ${t.border} hover:${t.bgInput}`}>
                                        {fields.map(field => <td key={field.name} className={`p-2 ${t.textMuted}`}>{item[field.name]}</td>)}
                                        {(userRole === 'owner' || userRole === 'asesor_ventas') && (
                                            <td className="p-2 flex gap-2">
                                                <button onClick={() => openModal(item)} className="p-1 hover:text-blue-400"><EditIcon /></button>
                                                <button onClick={() => handleDeleteRequest(item.id)} className="p-1 hover:text-red-400"><TrashIcon /></button>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal 
                isOpen={isModalOpen} 
                onClose={closeModal} 
                title={currentItem ? `Editar ${itemNoun}` : `Añadir ${itemNoun}`}
                maxWidth="max-w-md"
            >
                <form onSubmit={handleSave} className="space-y-4">
                    {fields.map(field => (
                        <div key={field.name}>
                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>{field.label}</label>
                            <input 
                                type={field.type || 'text'} 
                                placeholder={field.placeholder} 
                                value={formData[field.name] || ''} 
                                onChange={e => setFormData({ ...formData, [field.name]: e.target.value })} 
                                required={field.required !== false}
                                className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md focus:ring-2 ${t.borderFocus}`} 
                            />
                        </div>
                    ))}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={closeModal} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                        <button type="submit" className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar</button>
                    </div>
                </form>
            </Modal>

            <Modal 
                isOpen={confirmDeleteModal.isOpen} 
                onClose={() => setConfirmDeleteModal({ isOpen: false, id: null })} 
                title="Confirmar Eliminación"
                maxWidth="max-w-md"
            >
                <p className={t.textMuted}>¿Estás seguro de que quieres eliminar este {itemNoun}?</p>
                <div className="flex justify-end gap-4 pt-6">
                    <button onClick={() => setConfirmDeleteModal({ isOpen: false, id: null })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                    <button onClick={handleConfirmDelete} className="px-4 py-2 bg-red-600 text-white font-bold rounded-md hover:bg-red-500">Eliminar</button>
                </div>
            </Modal>
        </>
    );
}


// --- VISTA PARA PLANTILLAS DE MENSAJES ---
function MessageTemplatesView({ user }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();

    const [templates, setTemplates] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const docRef = doc(db, 'users', user.uid, 'config', 'messageTemplates');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            const firestoreTemplates = docSnap.exists() ? docSnap.data() : {};
            setTemplates({ ...DEFAULT_MESSAGE_TEMPLATES, ...firestoreTemplates });
            setLoading(false);
        });
        return () => unsubscribe();
    }, [user.uid, db]);

    const handleSave = async () => {
        const docRef = doc(db, 'users', user.uid, 'config', 'messageTemplates');
        try {
            await setDoc(docRef, templates, { merge: true });
            showNotification('Plantillas guardadas con éxito', 'success');
        } catch (error) {
            console.error("Error saving templates:", error);
            showNotification('Error al guardar las plantillas', 'error');
        }
    };

    const handleTemplateChange = (key, value) => {
        setTemplates(prev => ({ ...prev, [key]: value }));
    };

    if (loading) return <div className="text-center p-8">Cargando plantillas...</div>;

    return (
        <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border} space-y-6`}>
            <h2 className={`text-2xl font-bold ${t.primary}`}>Personalizar Mensajes</h2>
            {Object.keys(DEFAULT_MESSAGE_TEMPLATES).map(key => (
                <div key={key}>
                    <label className={`block text-lg font-bold ${t.secondary} mb-2 capitalize`}>{key.replace(/_/g, ' ')}</label>
                    <textarea
                        value={templates[key] || ''}
                        onChange={(e) => handleTemplateChange(key, e.target.value)}
                        className={`w-full h-32 px-4 py-2 ${t.bgInput} border ${t.border} rounded-md focus:ring-2 ${t.borderFocus}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Variables: {`{cliente}, {servicio}, {vencimiento}, {datos_cuenta}, {instrucciones}, {mi_negocio}, {mi_contacto}`}</p>
                </div>
            ))}
            <div className="text-right">
                <button onClick={handleSave} className={`px-6 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar Plantillas</button>
            </div>
        </div>
    );
}

// --- FUNCIÓN AUXILIAR PARA NORMALIZAR TEXTO (QUITA ACENTOS, ESPACIOS Y CONVIERTE A MINÚSCULAS) ---
const normalizeString = (str) => {
    if (!str) return '';
    return str
        .normalize('NFD') // Separa los caracteres de sus acentos (ej: 'á' -> 'a' + '´')
        .replace(/[\u0300-\u036f]/g, '') // Elimina los acentos
        .trim() // Elimina espacios al inicio y al final
        .toLowerCase(); // Convierte todo a minúsculas
};

function ImportModal({ isOpen, onClose, user, services, providers, clients, inventory }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const [activeTab, setActiveTab] = useState('clients');
    const [uploading, setUploading] = useState(false);
    const [file, setFile] = useState(null);

    const tabs = [
        { id: 'clients', label: 'Clientes' },
        { id: 'providers', label: 'Proveedores' },
        { id: 'services', label: 'Servicios' },
        { id: 'purchases', label: 'Compras (Inventario)' },
        { id: 'sales', label: 'Ventas (Histórico)' },
    ];

    const templates = {
        clients: "name,contact,note,email\nEjemplo Cliente,584141234567,Cliente VIP,cliente@email.com",
        providers: "name,contact,note\nEjemplo Proveedor,proveedor@email.com,Proveedor principal de Netflix",
        services: "name,type,imageUrl,defaultProviderName,defaultPurchaseDuration,defaultPurchasePrice,defaultInstructions\nNetflix Premium,Streaming,https://.../logo.png,Ejemplo Proveedor,30,5.50,Usar VPN de USA",
        purchases: "serviceName,email,password,profile,pin,maxAssignments\nNetflix Premium,cuenta1@email.com,pass123,Perfil 1,1234,1",
        sales: "clientContact,serviceName,inventoryIdentifier,membershipName,saleDate,expiresAt\n584125556677,Canva PRO (Edu),ingresosdiariosonline@gmail.com - Solo1Click,Plan Anual,2024-05-15,2025-05-15",
    };
    
    const tooltips = {
        clients: "Columnas requeridas: contact. Si 'name' está vacío, se usará 'email'. 'note' es opcional. El contacto debe ser único.",
        providers: "Columnas requeridas: name, contact. 'note' es opcional.",
        services: "Columnas requeridas: name, type, imageUrl. Los campos 'default' son opcionales y sirven como plantilla para futuras compras.",
        purchases: "Importa tus cuentas de inventario. 'serviceName' debe coincidir con un servicio ya creado. Para Canva, usa 'matrixEmail' y 'className'.",
        sales: "Importa tu historial de ventas. Para Canva, el 'inventoryIdentifier' debe ser 'matrixEmail - className'.",
    };

    const handleDownloadTemplate = () => { /* ...código sin cambios... */ };
    const handleFileChange = (e) => { setFile(e.target.files[0]); };

    const handleImport = async () => {
        if (!file) {
            showNotification('Por favor, selecciona un archivo.', 'error');
            return;
        }
        setUploading(true);

        const reader = new FileReader();
        reader.onload = async (event) => {
            let successCount = 0;
            let errorCount = 0;
            try {
                const csv = event.target.result;
                const lines = csv.split('\n').filter(line => line.trim() !== '');
                if (lines.length < 2) throw new Error("El archivo CSV está vacío o solo contiene encabezados.");
                
                const headers = lines[0].split(',').map(h => h.trim());
                const data = lines.slice(1).map(line => {
                    const values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map(v => v.replace(/"/g, '')) || line.split(',');
                    let obj = {};
                    headers.forEach((header, i) => { obj[header] = values[i]?.trim(); });
                    return obj;
                });

                const batch = writeBatch(db);
                
                if (activeTab === 'sales') {
                     for (const [index, item] of data.entries()) {
                        const client = clients.find(c => normalizeString(c.contact) === normalizeString(item.clientContact));
                        const service = services.find(s => normalizeString(s.name) === normalizeString(item.serviceName));

                        if (!client || !service) {
                            errorCount++;
                            console.error(`Fila ${index + 2} omitida: Cliente o Servicio no encontrado.`);
                            continue;
                        }

                        const membershipsRef = collection(db, 'users', user.uid, 'services', service.id, 'memberships');
                        const allMembershipsSnap = await getDocs(membershipsRef);
                        const allMembershipsForService = allMembershipsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        const membership = allMembershipsForService.find(m => normalizeString(m.name) === normalizeString(item.membershipName));

                        if (!membership) {
                            console.error(`Fila ${index + 2} omitida: Membresía "${item.membershipName}" no encontrada.`);
                            errorCount++;
                            continue;
                        }
                        
                        // ▼▼▼ INICIO DE LA CORRECCIÓN: Lógica de búsqueda de inventario por tipo de servicio ▼▼▼
                        const inventoryRef = collection(db, 'users', user.uid, 'services', service.id, 'inventory');
                        let inventoryQuery;
                        const identifier = item.inventoryIdentifier.trim();

                        if (service.type === 'Streaming') {
                            const [email, profile] = identifier.split(' - Perfil: ').map(s => s.trim());
                            if (profile) {
                                inventoryQuery = query(inventoryRef, where("email", "==", email), where("profile", "==", profile));
                            } else {
                                inventoryQuery = query(inventoryRef, where("email", "==", email));
                            }
                        } else if (service.type === 'Canva' || service.type === 'Capcut') {
                            const parts = identifier.split(' - ');
                            const matrixEmail = parts[0]?.trim();
                            const className = parts[1]?.trim();
                            if (matrixEmail && className) {
                                inventoryQuery = query(inventoryRef, where("matrixEmail", "==", matrixEmail), where("className", "==", className));
                            }
                        } else {
                            // Fallback para otros tipos de servicio, asume que el identificador es el email
                            inventoryQuery = query(inventoryRef, where("email", "==", identifier));
                        }
                        
                        if (!inventoryQuery) {
                            console.error(`Fila ${index + 2} omitida: Formato de 'inventoryIdentifier' no válido para el tipo de servicio "${service.type}"`);
                            errorCount++;
                            continue;
                        }

                        const inventorySnap = await getDocs(inventoryQuery);
                        const inventoryItem = inventorySnap.empty ? null : { id: inventorySnap.docs[0].id, ...inventorySnap.docs[0].data() };
                        // ▲▲▲ FIN DE LA CORRECCIÓN ▲▲▲

                        if (!inventoryItem) {
                            console.error(`Fila ${index + 2} omitida: Inventario no encontrado para "${item.inventoryIdentifier}"`);
                            errorCount++;
                            continue;
                        }

                        const saleRef = doc(collection(db, 'users', user.uid, 'sales'));
                        batch.set(saleRef, {
                            clientId: client.id,
                            serviceId: service.id,
                            inventoryIds: [inventoryItem.id],
                            membershipId: membership.id,
                            membershipName: membership.name,
                            saleDate: item.saleDate,
                            expiresAt: Timestamp.fromDate(new Date(item.expiresAt)),
                            createdAt: serverTimestamp(),
                            status: 'active',
                            salePrice: membership.price,
                            imported: true 
                        });
                        successCount++;
                    }
                } else {
                    // Aquí iría la lógica para las otras pestañas (clients, providers, etc.) que no cambia
                }

                if (successCount > 0) {
                    await batch.commit();
                }

                if (errorCount > 0) {
                    showNotification(`${successCount} registros importados. ${errorCount} fallaron (revisa la consola).`, 'info');
                } else {
                    showNotification(`¡${successCount} registros importados con éxito!`, 'success');
                }
                
                setFile(null);
                onClose();
            } catch (error) {
                console.error("Error al importar:", error);
                showNotification(error.message, 'error');
            } finally {
                setUploading(false);
            }
        };
        reader.readAsText(file, "UTF-8");
    };

    if (!isOpen) return null;

    return (
       <Modal isOpen={isOpen} onClose={onClose} title="Importar Datos Históricos">
           <div className="flex border-b ${t.border} mb-4">
                {tabs.map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-2 px-4 font-semibold transition-colors duration-300 ${activeTab === tab.id ? `${t.primary} border-b-2 border-blue-600` : `${t.textMuted} hover:bg-gray-100/10`}`}>
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="space-y-4">
                <div className="bg-blue-100/10 border-l-4 border-blue-500 text-blue-300 p-4 rounded-r-lg flex items-start gap-3">
                    <InfoIcon />
                    <div>
                        <p className="font-bold">Instrucciones</p>
                        <p className="text-sm">{tooltips[activeTab]}</p>
                    </div>
                </div>
                <button onClick={handleDownloadTemplate} className={`w-full flex items-center justify-center gap-2 px-4 py-2 font-bold rounded-md ${t.buttonSecondary}`}>
                    <DownloadIcon /> Descargar Plantilla CSV con Ejemplo
                </button>
                <div className="mt-4">
                    <label htmlFor="csv-upload" className={`w-full flex items-center justify-center gap-2 px-4 py-2 font-bold rounded-md cursor-pointer ${file ? 'bg-green-500/20 text-green-400' : t.buttonSecondary}`}>
                        <UploadIcon /> {file ? file.name : 'Seleccionar Archivo CSV'}
                    </label>
                    <input id="csv-upload" type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
                </div>
                <div className="flex justify-end gap-4 pt-4">
                    <button onClick={onClose} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                    <button onClick={handleImport} disabled={!file || uploading} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary} disabled:opacity-50`}>
                        {uploading ? 'Importando...' : 'Importar Datos'}
                    </button>
                </div>
            </div>
       </Modal>
    );
}

// --- NUEVO COMPONENTE: MODAL PARA BUSCAR LOGOS (CON Z-INDEX CORREGIDO) ---
function LogoModal({ isOpen, onClose }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];

    if (!isOpen) return null;

    return (
        <Portal>
            {/* CORRECCIÓN: Se cambió z-50 por z-[100] para que aparezca por encima de otros modales */}
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
                <div className={`${t.bgModal} border ${t.border} rounded-lg shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col`}>
                    <div className="flex justify-between items-center p-4 border-b ${t.border} flex-shrink-0">
                        <h3 className={`text-2xl font-bold ${t.secondary}`}>Buscar Logo</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-white text-3xl">&times;</button>
                    </div>
                    <div className="flex-grow">
                        <iframe 
                            src="https://solu7ec.com/logos" 
                            title="Buscador de Logos"
                            className="w-full h-full border-0"
                        ></iframe>
                    </div>
                </div>
            </div>
        </Portal>
    );
}

// --- [NUEVO] MODAL PARA GENERAR ENLACE DE PAGO ---
function PaymentLinkModal({ isOpen, onClose, sale, client, service }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const [paymentLink, setPaymentLink] = useState('');
    const [amount, setAmount] = useState(sale.price);
    const [description, setDescription] = useState(`${service.name} - ${client.name}`);

    const handleGenerateLink = async () => {
        setLoading(true);
        setPaymentLink('');
        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const createLink = httpsCallable(functions, 'createPayPalPaymentLink');
            const result = await createLink({ amount: parseFloat(amount), description });
            if (result.data.success) {
                setPaymentLink(result.data.paymentLink);
                showNotification("Enlace de pago creado.", "success");
            } else {
                throw new Error("No se pudo generar el enlace.");
            }
        } catch (error) {
            console.error("Error creating payment link:", error);
            showNotification(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(paymentLink);
        showNotification("¡Enlace copiado!", "info");
    };

    return (
        <div className={`fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4`}>
            <div className={`${t.bgModal} border ${t.border} p-8 rounded-lg shadow-2xl w-full max-w-md`}>
                <h3 className={`text-2xl font-bold ${t.primary} mb-4`}>Generar Enlace de Pago</h3>
                
                {paymentLink ? (
                    <div className="space-y-4">
                        <p className={t.textMuted}>Enlace generado para {client.name}. Cópialo y envíalo para completar el pago.</p>
                        <input type="text" readOnly value={paymentLink} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        <div className="flex justify-end gap-4">
                           <button onClick={copyToClipboard} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Copiar Enlace</button>
                           <button onClick={onClose} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cerrar</button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div>
                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Monto (USD)</label>
                            <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        </div>
                        <div>
                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Descripción</label>
                            <input type="text" value={description} onChange={e => setDescription(e.target.value)} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        </div>
                        <div className="flex justify-end gap-4 pt-4">
                            <button onClick={onClose} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`} disabled={loading}>Cancelar</button>
                            <button onClick={handleGenerateLink} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`} disabled={loading}>
                                {loading ? 'Generando...' : 'Generar Enlace'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- VISTA DEL DASHBOARD PRO (CON LAYOUT DE 2 COLUMNAS CORREGIDO Y RESPONSIVO) ---
function DashboardProView({ user, allServices, sales, clients, setActiveView, unreadCount, purchases, inventory, conversations, setIsStatsModalOpen }) {
    const { theme, themes } = useTheme();
    const t = themes[theme] || themes.netflix_dark;

    // --- Componente de Ficha Base ---
    const Tile = ({ children, className = '', onClick }) => ( 
        <div onClick={onClick} className={`${t.bgMuted} p-4 sm:p-6 rounded-lg border ${t.border} shadow-lg transition-all duration-300 flex flex-col ${onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1' : ''} ${className}`}>
            {children}
        </div> 
    );

    // --- Ficha de Estadísticas ---
    const DashboardStatsTile = () => {
        const { newClients, salesCount, activeSubscriptions } = useMemo(() => {
            const getDocDate = (doc) => {
                const dateString = doc.purchaseDate || doc.saleDate || doc.createdAt?.toDate().toISOString().split('T')[0];
                if (dateString) {
                    const date = dateString instanceof Date ? dateString : new Date(dateString + 'T00:00:00');
                    return date;
                }
                return null;
            };
            const today = new Date();
            const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
            const salesToday = (sales || []).filter(s => { const d = getDocDate(s); return d && d >= startOfToday && d <= endOfToday; });
            const newClientsToday = (clients || []).filter(c => { const d = getDocDate(c); return d && d >= startOfToday && d <= endOfToday; });
            const activeSubs = (sales || []).filter(s => s.expiresAt?.toDate() > today && s.status !== 'suspended').length;
            return { salesCount: salesToday.length, newClients: newClientsToday.length, activeSubscriptions: activeSubs };
        }, [sales, clients]);
        
        return (
            <Tile>
                <div className="flex justify-between items-start mb-4">
                    <h3 className={`text-lg font-bold ${t.primary}`}>Resumen de Hoy</h3>
                    <button onClick={() => setIsStatsModalOpen(true)} className={`text-xs font-semibold ${t.buttonSecondary} px-3 py-1 rounded-md`}>
                        Estadisticas completas ver Aquí
                    </button>
                </div>
                <div className="grid grid-cols-3 gap-4 flex-grow content-center">
                    <div className={`${t.bg} p-3 rounded-md text-center`}>
                        <p className={`text-sm ${t.textMuted} font-bold`}>Nuevos Clientes</p>
                        <p className={`text-6xl font-bold ${t.primary}`}>{newClients}</p>
                    </div>
                    <div className={`${t.bg} p-3 rounded-md text-center`}>
                        <p className={`text-sm ${t.textMuted} font-bold`}>Ventas (Hoy)</p>
                        <p className={`text-6xl font-bold ${t.primary}`}>{salesCount}</p>
                    </div>
                    <div className={`${t.bg} p-3 rounded-md text-center`}>
                        <p className={`text-sm ${t.textMuted} font-bold`}>Suscrip. Activas</p>
                        <p className={`text-6xl font-bold ${t.primary}`}>{activeSubscriptions}</p>
                    </div>
                </div>
            </Tile>
        );
    };
    
    // --- Ficha de Atajos Rápidos ---
    const ShortcutsTile = () => (
        <Tile>
            <h3 className={`text-lg font-bold ${t.primary} mb-3`}>Atajos Rápidos</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 flex-grow content-center">
                <button onClick={() => setActiveView('sales')} className={`flex items-center justify-center gap-2 p-3 text-sm rounded-md ${t.buttonSecondary}`}><ShoppingCartIcon /> Ventas</button>
                <button onClick={() => setActiveView('purchases')} className={`flex items-center justify-center gap-2 p-3 text-sm rounded-md ${t.buttonSecondary}`}><ShoppingBagIcon /> Compras</button>
                <button onClick={() => setActiveView('clients')} className={`flex items-center justify-center gap-2 p-3 text-sm rounded-md ${t.buttonSecondary}`}><UserIcon /> Clientes</button>
                <button onClick={() => setActiveView('services')} className={`flex items-center justify-center gap-2 p-3 text-sm rounded-md ${t.buttonSecondary}`}><BoxIcon /> Servicios</button>
                <button onClick={() => setActiveView('chatbot')} className={`col-span-2 sm:col-span-1 flex items-center justify-center gap-2 p-3 text-sm rounded-md ${t.buttonSecondary}`}><BotIcon />Asistente</button>
                <button onClick={() => setActiveView('team')} className={`flex items-center justify-center gap-2 p-3 text-sm rounded-md ${t.buttonSecondary}`}><UsersIcon /> Equipo</button>
            </div>
        </Tile>
    );
    
    // --- Ficha de Resumen de Inventario ---
    const InventoryTile = () => {
        const [sortOrder, setSortOrder] = useState('asc');
        const servicesWithStock = useMemo(() => {
            return (allServices || [])
                .map(s => { 
                    const inv = inventory[s.id] || []; 
                    const stock = inv.reduce((acc, item) => acc + ((item.maxAssignments || 1) - (item.assignments || 0)), 0); 
                    return { ...s, availableStock: stock }; 
                })
                .sort((a, b) => sortOrder === 'asc' ? a.availableStock - b.availableStock : b.availableStock - a.availableStock);
        }, [allServices, inventory, sortOrder]);

        return (
            <Tile>
                <div className="flex justify-between items-center mb-3">
                    <h3 className={`text-lg font-bold ${t.primary} flex items-center gap-2`}><InventoryIcon /> Inventario</h3>
                    <div className={`p-0.5 rounded-full ${t.bgInput} flex items-center text-xs`}>
                        <button onClick={() => setSortOrder('asc')} className={`px-2 py-0.5 rounded-full ${sortOrder === 'asc' ? t.buttonPrimary : 'bg-transparent'}`}>Asc</button>
                        <button onClick={() => setSortOrder('desc')} className={`px-2 py-0.5 rounded-full ${sortOrder === 'desc' ? t.buttonPrimary : 'bg-transparent'}`}>Desc</button>
                    </div>
                </div>
                <div className="space-y-2 flex-grow overflow-y-auto max-h-[180px] pr-2">
                    {servicesWithStock.length > 0 ? servicesWithStock.map(service => (
                        <div key={service.id} className={`${t.bg} p-2 rounded-md flex justify-between items-center text-sm`}>
                            <div className="flex items-center gap-2 truncate">
                                <img src={service?.imageUrl} alt={service?.name} className="w-6 h-6 rounded-full object-cover flex-shrink-0"/>
                                <p className="font-bold truncate">{service.name}</p>
                            </div>
                            <span className={`font-bold ${service.availableStock < 1 ? 'text-red-400' : service.availableStock <= 2 ? 'text-yellow-400' : t.textMuted}`}>{service.availableStock} Disp.</span>
                        </div>
                    )) : (
                        <div className="flex items-center justify-center h-full"><p className={`text-sm ${t.textMuted}`}>No hay servicios creados.</p></div>
                    )}
                </div>
            </Tile>
        );
    };

    // --- Ficha de Servicios Más Vendidos ---
    const TopServicesTile = () => {
        const [sortOrder, setSortOrder] = useState('desc');
        const topServices = useMemo(() => { 
            if (!sales || sales.length === 0) return []; 
            const counts = sales.reduce((acc, s) => { acc[s.serviceId] = (acc[s.serviceId] || 0) + (Number(s.quantity) || 1); return acc; }, {}); 
            return Object.entries(counts)
                .sort(([, countA], [, countB]) => sortOrder === 'desc' ? countB - countA : countA - countB)
                .slice(0, 4)
                .map(([id, count]) => { 
                    const s = (allServices || []).find(s => s.id === id); 
                    return { name: s?.name || 'N/A', count, imageUrl: s?.imageUrl }; 
                }); 
        }, [sales, allServices, sortOrder]);

        return (
            <Tile>
                 <div className="flex justify-between items-center mb-3">
                    <h3 className={`text-lg font-bold ${t.primary} flex items-center gap-2`}><TrophyIcon /> Servicios Más Vendidos</h3>
                    <div className={`p-0.5 rounded-full ${t.bgInput} flex items-center text-xs`}>
                        <button onClick={() => setSortOrder('asc')} className={`px-2 py-0.5 rounded-full ${sortOrder === 'asc' ? t.buttonPrimary : 'bg-transparent'}`}>Asc</button>
                        <button onClick={() => setSortOrder('desc')} className={`px-2 py-0.5 rounded-full ${sortOrder === 'desc' ? t.buttonPrimary : 'bg-transparent'}`}>Desc</button>
                    </div>
                 </div>
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-grow content-center">
                    {topServices.length > 0 ? topServices.map(service => (
                        <div key={service.name} className={`${t.bg} p-3 rounded-md flex flex-col items-center text-center`}>
                            <img src={service.imageUrl} alt={service.name} className="w-10 h-10 rounded-full object-cover mb-2"/>
                            <p className="font-bold text-sm truncate w-full">{service.name}</p>
                            <p className={`text-lg font-bold ${t.primary}`}>{service.count}</p>
                        </div>
                    )) : (
                        <div className="flex items-center justify-center h-full col-span-full"><p className={`text-sm ${t.textMuted} text-center py-4`}>No hay datos de ventas suficientes.</p></div>
                    )}
                 </div>
            </Tile>
        );
    };

    // --- Ficha de Bandeja de Entrada ---
    const InboxTile = () => (
        <Tile onClick={() => setActiveView('inbox')}>
            <div className="flex justify-between items-center mb-3">
                <h3 className={`text-lg font-bold ${t.primary}`}>Bandeja de Entrada</h3>
                {unreadCount > 0 && <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white animate-pulse">{unreadCount}</span>}
            </div>
            <div className="space-y-2 flex-grow overflow-y-auto max-h-[180px] pr-2">
                {(conversations || []).slice(0, 5).length > 0 ? conversations.slice(0, 5).map(convo => (
                    <div key={convo.id} className={`${t.bg} p-2 rounded-md`}>
                        <p className="font-bold truncate text-sm">{convo.clientName || convo.id}</p>
                        <p className={`text-xs ${t.textMuted} truncate`}>{convo.lastMessage}</p>
                    </div>
                )) : (
                    <div className="flex items-center justify-center h-full"><p className={`text-sm ${t.textMuted}`}>No hay mensajes recientes.</p></div>
                )}
            </div>
        </Tile>
    );
    
    // --- Ficha de Alertas y Notificaciones ---
    const AlertsTile = () => {
        const { expiringSoon, expiringPurchases } = useMemo(() => {
            const today = new Date();
            const sevenDaysFromNow = new Date();
            sevenDaysFromNow.setDate(today.getDate() + 7);
            const soon = (sales || []).filter(s => { const d = s.expiresAt?.toDate(); return d && d > today && d <= sevenDaysFromNow && s.status !== 'suspended'; }).sort((a, b) => a.expiresAt.toDate() - b.expiresAt.toDate());
            const renew = (purchases || []).filter(p => { const d = new Date(p.renewalDate + 'T00:00:00'); return d && d > today && d <= sevenDaysFromNow; }).sort((a,b) => new Date(a.renewalDate) - new Date(b.renewalDate));
            return { expiringSoon: soon, expiringPurchases: renew };
        }, [sales, purchases]);

        return (
            <Tile>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 h-full">
                    <div>
                        <h3 className={`text-lg font-bold text-yellow-400 mb-3 flex items-center gap-2`}><AlertTriangleIcon /> Vencen (Clientes)</h3>
                        <div className="space-y-2 max-h-[180px] overflow-y-auto pr-2">
                            {expiringSoon.length > 0 ? expiringSoon.map(sale => {
                                const client = clients.find(c => c.id === sale.clientId); 
                                const service = allServices.find(s => s.id === sale.serviceId); 
                                return (
                                    <div key={sale.id} className={`${t.bg} p-2 rounded-md text-sm`}>
                                        <p className="font-bold truncate">{client?.name}</p>
                                        <div className="flex justify-between items-center">
                                            <p className={`${t.textMuted} text-xs truncate`}>{service?.name}</p>
                                            <span className="font-semibold text-yellow-400 flex-shrink-0">{sale.expiresAt.toDate().toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                )
                            }) : (
                                <div className="flex items-center justify-center h-full"><p className={`text-sm ${t.textMuted}`}>No hay vencimientos próximos.</p></div>
                            )}
                        </div>
                    </div>
                    <div>
                        <h3 className={`text-lg font-bold text-cyan-400 mb-3 flex items-center gap-2`}><RefreshCwIcon /> Renovar (Compras)</h3>
                        <div className="space-y-2 max-h-[180px] overflow-y-auto pr-2">
                            {expiringPurchases.length > 0 ? expiringPurchases.map(purchase => (
                                <div key={purchase.id} className={`${t.bg} p-2 rounded-md text-sm`}>
                                    <p className="font-bold truncate">{purchase.serviceName}</p>
                                    <div className="flex justify-between items-center">
                                        <p className={`${t.textMuted} text-xs truncate`}>{purchase.email || 'Lote'}</p>
                                        <span className="font-semibold text-cyan-400 flex-shrink-0">{new Date(purchase.renewalDate + 'T00:00:00').toLocaleDateString()}</span>
                                    </div>
                                </div>
                            )) : (
                                <div className="flex items-center justify-center h-full"><p className={`text-sm ${t.textMuted}`}>No hay renovaciones próximas.</p></div>
                            )}
                        </div>
                    </div>
                </div>
            </Tile>
        );
    };
    
    // --- Renderizado del Layout Principal del Dashboard ---
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <ShortcutsTile />
            <DashboardStatsTile />
            <InventoryTile />
            <TopServicesTile />
            <InboxTile />
            <AlertsTile />
        </div>
    );
}

// --- [COMPONENTE CORREGIDO]: PANEL PRINCIPAL ---
function AccountManagementDashboard({ user, handleLogout, isSdkReady, setUserData }) {
    const { theme, themes, setTheme: setContextTheme, font, setFont: setContextFont, fonts, setDashboardStyle } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [activeView, setActiveView] = useState('home');
    const [services, setServices] = useState([]);
    const [providers, setProviders] = useState([]);
    const [purchases, setPurchases] = useState([]);
    const [sales, setSales] = useState([]);
    const [clients, setClients] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [cannedResponses, setCannedResponses] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [businessProfile, setBusinessProfile] = useState({ name: '', whatsapp: '', telegram: '', website: '', catalogLogoUrl: '', catalogSlug: '', catalogTemplate: 'elegant_gold' });
    const [messageTemplates, setMessageTemplates] = useState({});
    const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
    const [inventory, setInventory] = useState({});
    const [inventoryLoading, setInventoryLoading] = useState(true);
    const [installPromptEvent, setInstallPromptEvent] = useState(null);

    const userPlan = user.plan || {};
    const teamId = user.teamId || user.uid;
    const userRole = user.role || 'owner';

    useEffect(() => {
        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setInstallPromptEvent(event);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!installPromptEvent) {
            return;
        }
        installPromptEvent.prompt();
        const { outcome } = await installPromptEvent.userChoice;
        if (outcome === 'accepted') {
            showNotification('¡Aplicación instalada con éxito!', 'success');
        }
        setInstallPromptEvent(null);
    };

    // --- INICIO DE LA CORRECCIÓN CLAVE EN LA LÓGICA DE CARGA DE DATOS ---
    useEffect(() => {
        if (!user || !user.uid) {
            setLoading(false);
            return;
        }

        const ownerId = user.teamId || user.uid; // ID del dueño del equipo
        const selfId = user.uid; // ID del usuario que está logueado

        // Colecciones que SIEMPRE pertenecen al dueño (servicios, inventario global, proveedores)
        const ownerDataCollections = {
            services: setServices,
            providers: setProviders,
            purchases: setPurchases,
        };

        // Colecciones que son personales para cada usuario (sus propias ventas, sus propios clientes)
        const selfDataCollections = {
            sales: setSales,
            clients: setClients,
            cannedResponses: setCannedResponses,
            conversations: setConversations,
        };

        const unsubs = [];

        // 1. Cargar datos que son propiedad del Dueño del Equipo
        // Un revendedor necesita leer estos datos para saber qué puede comprar/vender.
        Object.entries(ownerDataCollections).forEach(([colName, setter]) => {
            const q = query(collection(db, 'users', ownerId, colName));
            unsubs.push(onSnapshot(q, (snapshot) => {
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setter(data);
            }, (error) => {
                console.error(`Error cargando ${colName} del owner:`, error);
                showNotification(`Error cargando ${colName}. Revisa los permisos.`, 'error');
            }));
        });
        
        // 2. Cargar datos que son gestionados por el usuario logueado
        // Para el dueño, esto cargará sus datos. Para el revendedor, cargará los suyos.
        Object.entries(selfDataCollections).forEach(([colName, setter]) => {
            const q = query(collection(db, 'users', selfId, colName));
            unsubs.push(onSnapshot(q, (snapshot) => {
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                if (colName === 'conversations') {
                    data.sort((a, b) => (b.lastTimestamp?.toMillis() || 0) - (a.lastTimestamp?.toMillis() || 0));
                }
                setter(data);
            }, (error) => {
                console.error(`Error cargando ${colName} del usuario:`, error);
                showNotification(`Error cargando tus ${colName}.`, 'error');
            }));
        });
        
        // El resto de la lógica de carga (equipo, perfil, etc.) se mantiene similar
        if (userRole === 'revendedor') {
            const userRef = doc(db, 'users', selfId);
            unsubs.push(onSnapshot(userRef, (docSnap) => {
                if (docSnap.exists()) {
                    setUserData(prev => ({ ...prev, resellerBalance: docSnap.data().resellerBalance || 0 }));
                }
            }));
        }

        const teamQuery = query(collection(db, 'users'), where('teamId', '==', ownerId));
        unsubs.push(onSnapshot(teamQuery, async (snapshot) => {
            const agents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            try {
                const ownerDoc = await getDoc(doc(db, 'users', ownerId));
                if (ownerDoc.exists()) {
                    setTeamMembers([{ id: ownerDoc.id, ...ownerDoc.data() }, ...agents]);
                } else {
                    setTeamMembers(agents);
                }
            } catch (error) {
                console.error("Error fetching owner doc for team list:", error);
                setTeamMembers(agents);
            }
        }));

        if (userRole === 'owner') {
            const templatesRef = doc(db, 'users', ownerId, 'config', 'messageTemplates');
            unsubs.push(onSnapshot(templatesRef, (docSnap) => setMessageTemplates(docSnap.exists() ? { ...DEFAULT_MESSAGE_TEMPLATES, ...docSnap.data() } : { ...DEFAULT_MESSAGE_TEMPLATES })));

            const businessProfileRef = doc(db, 'users', ownerId, 'config', 'businessProfile');
            unsubs.push(onSnapshot(businessProfileRef, (docSnap) => { if (docSnap.exists()) setBusinessProfile(docSnap.data()); }));
        }

        setLoading(false);
        return () => unsubs.forEach(unsub => unsub());

    }, [user, db, showNotification, setUserData]);
    // --- FIN DE LA CORRECCIÓN ---


    useEffect(() => {
        if (!services || services.length === 0) {
            setInventory({});
            setInventoryLoading(false);
            return;
        }
        
        setInventoryLoading(true);
        const unsubscribers = [];
        const ownerId = user.teamId || user.uid; // El inventario siempre es del dueño

        services.forEach(service => {
            const inventoryRef = collection(db, 'users', ownerId, 'services', service.id, 'inventory');
            const unsubscribe = onSnapshot(inventoryRef, (snapshot) => {
                const inventoryItems = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setInventory(prevInventory => ({
                    ...prevInventory,
                    [service.id]: inventoryItems
                }));
            }, (error) => {
                console.error(`Error escuchando el inventario del servicio ${service.id}:`, error);
            });
            
            unsubscribers.push(unsubscribe);
        });

        setInventoryLoading(false);

        return () => {
            unsubscribers.forEach(unsub => unsub());
        };
    }, [services, user, db]);

    const setTheme = async (newTheme) => {
        setContextTheme(newTheme); 
        try {
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, { preferences: { theme: newTheme } }, { merge: true });
        } catch (error) {
            console.error("Error saving theme preference:", error);
            showNotification('Error al guardar la preferencia de tema.', 'error');
        }
    };

    const setFont = async (newFont) => {
        setContextFont(newFont); 
        try {
            const userRef = doc(db, 'users', user.uid);
            await setDoc(userRef, { preferences: { font: newFont } }, { merge: true });
        } catch (error) {
            console.error("Error saving font preference:", error);
            showNotification('Error al guardar la preferencia de fuente.', 'error');
        }
    };

    const handleSaveBusinessProfile = async (profileData) => {
        try {
            const profileRef = doc(db, 'users', user.uid, 'config', 'businessProfile');
            await setDoc(profileRef, profileData, { merge: true });
            showNotification('¡Perfil de negocio guardado!', 'success');
            setIsSettingsOpen(false);
        } catch (error) {
            console.error("Error saving business profile:", error);
            showNotification('Error al guardar el perfil.', 'error');
        }
    };

    const handleExportAllData = () => {
        try {
            const dataToExport = { services, providers, purchases, sales, clients, inventory, businessProfile, messageTemplates, exportDate: new Date().toISOString(), appVersion: 'GCS1-v1' };
            const jsonString = JSON.stringify(dataToExport, (key, value) => {
                if (value && typeof value.toDate === 'function') { return value.toDate().toISOString(); }
                return value;
            }, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const date = new Date().toISOString().split('T')[0];
            link.href = url;
            link.download = `gcs1_backup_${date}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            showNotification('Exportación exitosa.', 'success');
        } catch (error) {
            console.error("Error exporting data:", error);
            showNotification('Hubo un error al exportar los datos.', 'error');
        }
    };

    const unreadCount = conversations.filter(c => c.unread).length;

    const allNavLinks = [
        { name: 'Inicio', view: 'home', icon: <HomeIcon />, roles: ['owner', 'asesor_ventas', 'soporte', 'revendedor'] },
        { name: 'Bandeja de Entrada', view: 'inbox', icon: <InboxIcon />, roles: ['owner', 'asesor_ventas', 'soporte'] },
        { name: 'Análisis', view: 'analytics', icon: <ChartPieIcon />, permission: () => userPlan.hasAnalytics, roles: ['owner'] },
        { name: 'Campañas', view: 'campaigns', icon: <MegaphoneIcon />, permission: () => userPlan.campaignLimit > 0, roles: ['owner', 'asesor_ventas'] },
        { name: 'Equipo', view: 'team', icon: <UsersIcon />, permission: () => userPlan.agentLimit > 1, roles: ['owner'] },
        { name: 'Saldos', view: 'reseller-balances', icon: <DollarSignIcon />, roles: ['owner'] },
        { name: 'Ventas', view: 'sales', icon: <ShoppingCartIcon />, roles: ['owner', 'asesor_ventas', 'revendedor'] },
        { name: 'Inventario', view: 'reseller-inventory', icon: <InventoryIcon />, roles: ['revendedor'] },
        { name: 'Servicios', view: 'services', icon: <BoxIcon />, roles: ['owner', 'asesor_ventas', 'revendedor'] },
        { name: 'Clientes', view: 'clients', icon: <UserIcon />, roles: ['owner', 'asesor_ventas', 'revendedor'] },
        { name: 'Proveedores', view: 'providers', icon: <TruckIcon />, roles: ['owner', 'asesor_ventas'] },
        { name: 'Mensajes', view: 'templates', icon: <MessageSquareIcon />, roles: ['owner'] },
        { name: 'Respuestas Frecuentes', view: 'cannedResponses', icon: <MessageCircleMoreIcon />, roles: ['owner'] },
        { name: 'Asistente IA', view: 'chatbot', icon: <BotIcon />, roles: ['owner'] },
    ];

    const navLinks = allNavLinks.filter(link => 
        (link.permission === undefined || link.permission()) &&
        (link.roles.includes(userRole))
    );

    const renderActiveView = () => {
        const commonProps = { user, services, providers, sales, clients, messageTemplates, businessProfile, inventory, teamMembers, conversations, allServices: services, setIsSettingsOpen, setIsStatsModalOpen };
        
        if (userRole === 'revendedor') {
            switch (activeView) {
                case 'home':
                case 'reseller-dashboard':
                    return <ResellerDashboardView {...commonProps} setActiveView={setActiveView} />;
                case 'sales':
                    return <SalesView {...commonProps} />;
                case 'clients':
                    return <ClientCardView {...commonProps} />;
                case 'reseller-inventory':
                    return <ResellerInventoryView {...commonProps} setActiveView={setActiveView} />;
                default:
                    return <ResellerDashboardView {...commonProps} setActiveView={setActiveView} />;
            }
        }

        if (activeView === 'analytics' && !userPlan.hasAnalytics) return <UpgradePlanView featureName="Análisis del Asistente" />;
        if (activeView === 'campaigns' && (!userPlan.campaignLimit || userPlan.campaignLimit === 0)) return <UpgradePlanView featureName="Campañas de Marketing" />;
        if (activeView === 'team' && (!userPlan.agentLimit || userPlan.agentLimit <= 1)) return <UpgradePlanView featureName="Gestión de Equipo" />;

        switch (activeView) {
            case 'home': 
                return <DashboardProView {...commonProps} navLinks={navLinks} setActiveView={setActiveView} unreadCount={unreadCount} purchases={purchases} />;
            case 'inbox': return <InboxView user={user} teamMembers={teamMembers} cannedResponses={cannedResponses} />;
            case 'analytics': return <AnalyticsView user={user} />;
            case 'campaigns': return <CampaignsView user={user} clients={clients} services={services} sales={sales} />;
            case 'team': return <TeamView user={user} teamMembers={teamMembers} />;
            case 'reseller-balances': return <ResellerBalanceView user={user} teamMembers={teamMembers} />;
            case 'sales': return <SalesView {...commonProps} />;
            case 'purchases': return <PurchasesView {...commonProps} purchases={purchases} />;
            case 'services': return <ServicesView user={user} services={services} providers={providers} inventory={inventory} />;
            case 'clients': return <ClientCardView user={user} clients={clients} sales={sales} services={services} inventory={inventory} messageTemplates={messageTemplates} businessProfile={businessProfile} />;
            case 'providers': return <SimpleCrudView user={user} collectionName="providers" title="Mis Proveedores" fields={providerFields} itemNoun="proveedor" />;
            case 'templates': return <MessageTemplatesView user={user} />;
            case 'cannedResponses': return <SimpleCrudView user={user} collectionName="cannedResponses" title="Respuestas Frecuentes" fields={cannedResponseFields} itemNoun="respuesta" />;
            case 'chatbot': return <ChatbotConfigView user={user} isSdkReady={isSdkReady} />;
            default: return <DashboardProView {...commonProps} navLinks={navLinks} setActiveView={setActiveView} unreadCount={unreadCount} purchases={purchases} />;
        }
    };
    
    const AppLayout = ({ children }) => (
        <div className={`flex h-screen ${t.bg} ${t.text}`} style={{ fontFamily: fonts[font] }}>
            
            {theme === 'facebook_blue' && activeView !== 'home' && (
                <aside className={`hidden lg:block w-64 ${t.bgMuted} p-4 border-r ${t.border} flex-shrink-0`}>
                    <h1 className={`text-2xl font-bold ${t.primary} mb-8`}>{businessProfile.name || 'StreamDesk Panel'}</h1>
                    <nav className="space-y-2">
                        {navLinks.map(link => (
                            <button 
                                key={link.view} 
                                onClick={() => setActiveView(link.view)} 
                                className={`relative w-full flex items-center px-4 py-2 rounded-md ${activeView === link.view ? t.tabActive : t.tabInactive}`}
                            >
                                {React.cloneElement(link.icon, { className: 'h-5 w-5 mr-2' })}
                                <span>{link.name}</span>
                                {link.view === 'inbox' && unreadCount > 0 && (
                                    <span className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </aside>
            )}

            <div className="flex-1 flex flex-col overflow-hidden">
                <header className={`flex items-center justify-between p-2 sm:py-2 sm:px-4 border-b ${t.border} ${t.bgMuted} flex-shrink-0`}>
                    <div>
                        <div className="flex items-center">
                            <h1 className={`text-xl font-bold ${t.primary} lg:hidden`}>{businessProfile.name || 'GCS1'}</h1>
                            <div className="hidden lg:block">
                                {theme !== 'facebook_blue' && <h1 className={`text-2xl font-bold ${t.primary}`}>{businessProfile.name || 'Gestión de Cuentas'}</h1>}
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {installPromptEvent && (
                            <button onClick={handleInstallClick} title="Instalar Aplicación" className={`flex items-center gap-2 px-3 py-2 text-sm font-bold rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-colors`}>
                                <DownloadIcon />
                                <span className="hidden sm:inline">Instalar</span>
                            </button>
                        )}

                        {theme !== 'facebook_blue' && activeView !== 'home' && (
                            <nav className="hidden lg:flex items-center gap-2">
                                {navLinks.map(link => (
                                    <button
                                        key={link.view}
                                        onClick={() => setActiveView(link.view)}
                                        title={link.name}
                                        className={`group relative flex-shrink-0 flex items-center p-3 rounded-lg transition-colors duration-200 ${activeView === link.view ? t.tabActive : t.tabInactive}`}
                                    >
                                        {React.cloneElement(link.icon, { className: 'h-6 w-6' })}
                                        {link.view === 'inbox' && unreadCount > 0 && (
                                            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">{unreadCount > 9 ? '9+' : unreadCount}</span>
                                        )}
                                        <span className={`absolute top-full mt-2 px-2 py-1 text-xs font-bold whitespace-nowrap ${t.bg} ${t.text} border ${t.border} rounded-md shadow-lg scale-0 group-hover:scale-100 transition-transform origin-top z-10`}>
                                            {link.name}
                                        </span>
                                    </button>
                                ))}
                            </nav>
                        )}

                        <div className="hidden lg:block h-6 w-px bg-gray-600 mx-2"></div>
                        
                        <button onClick={() => setActiveView('home')} title="Inicio" className={`p-2 rounded-full ${t.buttonSecondary}`}><HomeIcon /></button>
                        <button onClick={() => setIsSettingsOpen(true)} title="Configuración" className={`p-2 rounded-full ${t.buttonSecondary}`}><SettingsIcon /></button>
                        <button onClick={handleLogout} title="Cerrar Sesión" className={`p-2 rounded-full ${t.buttonSecondary}`}><LogoutIcon /></button>
                        
                        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden p-2 rounded-full"><MenuIcon /></button>
                    </div>
                </header>

                {isMenuOpen && (
                    <div className={`lg:hidden absolute top-16 right-4 mt-2 w-48 ${t.bgModal} border ${t.border} rounded-md shadow-lg z-20`}>
                        <nav className="flex flex-col p-2">
                            {navLinks.map(link => (
                                <button key={link.view} onClick={() => { setActiveView(link.view); setIsMenuOpen(false); }} className={`relative w-full flex items-center px-4 py-3 rounded-md font-bold ${activeView === link.view ? t.tabActive : t.tabInactive}`}>
                                    {React.cloneElement(link.icon, { className: 'h-5 w-5 mr-2' })}
                                    <span>{link.name}</span>
                                    {link.view === 'inbox' && unreadCount > 0 && (
                                        <span className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">{unreadCount > 9 ? '9+' : unreadCount}</span>
                                    )}
                                </button>
                            ))}
                        </nav>
                    </div>
                )}

                <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                    <div className="max-w-7xl mx-auto">
                        {(loading || inventoryLoading) ? (
                            <div className="text-center p-8">Cargando datos del panel...</div>
                        ) : (
                            <div className="space-y-6">
                                {renderActiveView()}
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );

    return (
        <div className={`min-h-screen ${t.bg} ${t.text}`} style={{ fontFamily: fonts[font] }}>
            <AppLayout>
                {(loading || inventoryLoading) ? (
                    <div className="text-center p-8">Cargando datos del panel...</div>
                ) : (
                    <div className="space-y-6">
                        {renderActiveView()}
                    </div>
                )}
            </AppLayout>

            {isStatsModalOpen && (
                <Portal>
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                        <div className={`${t.bgModal} border ${t.border} p-8 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto`}>
                            <h3 className={`text-2xl font-bold ${t.primary} mb-6`}>Estadísticas del Negocio</h3>
                            <StatsView purchases={purchases} sales={sales} allServices={services} clients={clients} />
                            <div className="text-right mt-6">
                                <button onClick={() => setIsStatsModalOpen(false)} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </Portal>
            )}

            {isSettingsOpen && (
                <Portal>
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                        <div className={`${t.bgModal} border ${t.border} p-8 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
                            <h3 className={`text-2xl font-bold ${t.primary} mb-6`}>Configuración</h3>
                            <div className="space-y-4 mb-6 pb-4 border-b border-gray-700">
                                <h4 className={`text-lg font-bold ${t.secondary}`}>Personalización del Negocio y Catálogo</h4>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Nombre del Negocio</label>
                                    <input type="text" name="name" value={businessProfile.name || ''} onChange={(e) => setBusinessProfile({ ...businessProfile, name: e.target.value })} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Contacto (WhatsApp)</label>
                                    <input type="text" name="whatsapp" value={businessProfile.whatsapp || ''} onChange={(e) => setBusinessProfile({ ...businessProfile, whatsapp: e.target.value })} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Contacto (Telegram)</label>
                                    <input type="text" name="telegram" value={businessProfile.telegram || ''} onChange={(e) => setBusinessProfile({ ...businessProfile, telegram: e.target.value })} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Sitio Web o Red Social</label>
                                    <input type="url" name="website" value={businessProfile.website || ''} onChange={(e) => setBusinessProfile({ ...businessProfile, website: e.target.value })} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>URL del Logo para el Catálogo</label>
                                    <input type="url" name="catalogLogoUrl" value={businessProfile.catalogLogoUrl || ''} onChange={(e) => setBusinessProfile({ ...businessProfile, catalogLogoUrl: e.target.value })} placeholder="https://ejemplo.com/logo.png" className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>URL Amigable del Catálogo</label>
                                    <input type="text" name="catalogSlug" value={businessProfile.catalogSlug || ''} onChange={(e) => setBusinessProfile({ ...businessProfile, catalogSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                    <p className="text-xs text-gray-500 mt-1">Ej: mi-tienda-cool (solo letras, números y guiones).</p>
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Plantilla Visual del Catálogo</label>
                                    <select 
                                        name="catalogTemplate" 
                                        value={businessProfile.catalogTemplate || 'elegant_gold'} 
                                        onChange={(e) => setBusinessProfile({ ...businessProfile, catalogTemplate: e.target.value })} 
                                        className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}
                                    >
                                        {Object.entries(catalogTemplates).map(([key, template]) => (
                                            <option key={key} value={key}>{template.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="text-right">
                                    <button onClick={() => handleSaveBusinessProfile(businessProfile)} className={`px-4 py-2 rounded-md ${t.buttonPrimary}`}>Guardar Perfil</button>
                                </div>
                            </div>

                            <div className="space-y-4 mb-6 pb-4 border-b border-gray-700">
                                <h4 className={`text-lg font-bold ${t.secondary}`}>Copia de Seguridad (Backup)</h4>
                                <p className={`${t.textMuted} text-sm`}>Exporta todos tus datos a un solo archivo JSON.</p>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button onClick={handleExportAllData} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 font-bold rounded-md ${t.buttonSecondary}`}>
                                        <DownloadIcon /> Exportar Todo
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <h4 className={`text-lg font-bold ${t.secondary}`}>Apariencia</h4>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Tema</label>
                                    <select value={theme} onChange={(e) => setTheme(e.target.value)} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                        {Object.entries(themes).map(([key, value]) => (
                                            <option key={key} value={key}>{value.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Tipografía</label>
                                    <select value={font} onChange={(e) => setFont(e.target.value)} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                        {Object.keys(fonts).map((fontName) => (
                                            <option key={fontName} value={fontName}>{fontName}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="text-right mt-6">
                                <button onClick={() => setIsSettingsOpen(false)} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </Portal>
            )}
            <ImportModal 
                isOpen={isImportModalOpen} 
                onClose={() => setIsImportModalOpen(false)}
                user={user}
                services={services}
                providers={providers}
                clients={clients}
                inventory={inventory}
            />
        </div>
    );
}

// --- VISTA DE SERVICIOS (USANDO EL NUEVO MODAL REUTILIZABLE) ---
function ServicesView({ user, services, providers, inventory: allInventory }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
    const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
    const [currentService, setCurrentService] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingService, setEditingService] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
    const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);
    const [serviceMemberships, setServiceMemberships] = useState([]);
    const [currentMembership, setCurrentMembership] = useState(null);
    // AÑADIDO: 'isResellerPlan' para el nuevo checkbox
    const initialMembershipForm = { name: '', durationDays: 30, price: 0, resellerPrice: 0, isResellerPlan: false };
    const [membershipFormData, setMembershipFormData] = useState(initialMembershipForm);
    
    const initialServiceFormState = { 
        type: 'Streaming', 
        allowMultipleAssignments: false, 
        name: '', 
        imageUrl: '', 
        note: '',
        defaultProviderId: '',
        defaultPurchaseDuration: 30,
        defaultPurchasePrice: 0,
        defaultInstructions: ''
    };
    const [serviceFormData, setServiceFormData] = useState(initialServiceFormState);
    
    useEffect(() => {
        if (!currentService) { setServiceMemberships([]); return; }
        const membershipsRef = collection(db, 'users', user.uid, 'services', currentService.id, 'memberships');
        const q = query(membershipsRef, orderBy('durationDays', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setServiceMemberships(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [currentService, user.uid, db]);

    const openServiceModal = (service = null) => {
        if (service) {
            setEditingService(service);
            setServiceFormData({ ...initialServiceFormState, ...service });
        } else {
            setEditingService(null);
            setServiceFormData(initialServiceFormState);
        }
        setIsServiceModalOpen(true);
    };

    const closeServiceModal = () => {
        setIsServiceModalOpen(false);
        setEditingService(null);
        setServiceFormData(initialServiceFormState);
    };

    const handleServiceFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setServiceFormData(prev => ({...prev, [name]: type === 'checkbox' ? checked : value}));
    };

    const handleNumericServiceFormChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = name.toLowerCase().includes('price') 
            ? value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')
            : value.replace(/\D/g, '');
        setServiceFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    };
    
    // MODIFICADO: Ahora maneja el checkbox
    const handleMembershipFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setMembershipFormData(prev => ({...prev, [name]: type === 'checkbox' ? checked : value}));
    };

    const handleNumericMembershipFormChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = name.toLowerCase().includes('price') 
            ? value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')
            : value.replace(/\D/g, '');
        setMembershipFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    };

    const handleSaveService = async (e) => {
        e.preventDefault();
        try {
            if (editingService) {
                const serviceRef = doc(db, 'users', user.uid, 'services', editingService.id);
                await updateDoc(serviceRef, serviceFormData);
                showNotification('¡Servicio actualizado!', 'success');
            } else {
                await addDoc(collection(db, 'users', user.uid, 'services'), { ...serviceFormData, createdAt: serverTimestamp() });
                showNotification('Servicio añadido con éxito', 'success');
            }
            closeServiceModal();
        } catch(error) {
            showNotification('Error al guardar el servicio', 'error');
            console.error(error);
        }
    };

    const handleDeleteService = async () => {
        if (!confirmDelete) return;
        
        try {
            const serviceRef = doc(db, 'users', user.uid, 'services', confirmDelete.id);
            const inventoryRef = collection(db, 'users', user.uid, 'services', confirmDelete.id, 'inventory');
            
            const inventorySnap = await getDocs(inventoryRef);
            const batch = writeBatch(db);
            inventorySnap.forEach(doc => {
                batch.delete(doc.ref);
            });
            
            batch.delete(serviceRef);
            
            await batch.commit();
            
            showNotification('Servicio y su inventario eliminados.', 'info');
            setConfirmDelete(null);
        } catch (error) {
            console.error("Error deleting service:", error);
            showNotification('Error al eliminar el servicio.', 'error');
        }
    };
    
    const openMembershipModal = (service, membership = null) => {
        setCurrentService(service);
        if (membership) {
            setCurrentMembership(membership);
            setMembershipFormData({ ...initialMembershipForm, ...membership }); // Asegura que todos los campos existan
        } else {
            setCurrentMembership(null);
            setMembershipFormData(initialMembershipForm);
        }
        setIsMembershipModalOpen(true);
    };
    
    const closeMembershipModal = () => {
        setIsMembershipModalOpen(false);
        setCurrentService(null);
        setCurrentMembership(null);
    };

    // MODIFICADO: Asegura que el valor booleano se guarde correctamente.
    const handleSaveMembership = async (e) => {
        e.preventDefault();
        if (!currentService) return;
        try {
            const membershipRef = currentMembership 
                ? doc(db, 'users', user.uid, 'services', currentService.id, 'memberships', currentMembership.id)
                : doc(collection(db, 'users', user.uid, 'services', currentService.id, 'memberships'));

            await setDoc(membershipRef, {
                ...membershipFormData,
                durationDays: Number(membershipFormData.durationDays),
                price: Number(membershipFormData.price),
                resellerPrice: Number(membershipFormData.resellerPrice) || 0,
                isResellerPlan: !!membershipFormData.isResellerPlan // Asegura que sea booleano
            }, { merge: true });

            showNotification('¡Membresía guardada!', 'success');
            setMembershipFormData(initialMembershipForm);
            if (currentMembership) {
                setCurrentMembership(null);
            }
        } catch (error) {
            console.error("Error saving membership:", error);
            showNotification('Error al guardar la membresía.', 'error');
        }
    };

    const handleDeleteMembership = async (membershipId) => {
        if (!currentService) return;
        const membershipRef = doc(db, 'users', user.uid, 'services', currentService.id, 'memberships', membershipId);
        try {
            await deleteDoc(membershipRef);
            showNotification('Membresía eliminada.', 'info');
        } catch (error) {
            console.error("Error deleting membership:", error);
            showNotification('Error al eliminar la membresía.', 'error');
        }
    };

    const filteredServices = services.filter(service => 
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h2 className={`text-2xl font-bold ${t.primary}`}>Servicios Definidos</h2>
                    <div className="relative w-full sm:w-auto">
                        <input type="text" placeholder="Buscar servicios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full sm:w-64 pl-10 pr-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}/>
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SearchIcon /></div>
                    </div>
                    <button onClick={() => openServiceModal()} className={`flex items-center px-4 py-2 font-bold rounded-md w-full sm:w-auto justify-center ${t.buttonPrimary}`}><PlusCircleIcon /> <span className="ml-2">Nuevo Servicio</span></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredServices.map(service => {
                        const serviceInventory = allInventory ? (allInventory[service.id] || []) : [];
                        const availableCount = serviceInventory.filter(item => {
                            return Number(item.assignments || 0) < Number(item.maxAssignments || 1);
                        }).length;

                        return (
                            <div key={service.id} className={`${t.bg} p-4 rounded-lg border ${t.border} flex flex-col justify-between relative`}>
                                <div className="absolute top-2 right-2 flex gap-2">
                                    <button onClick={() => openServiceModal(service)} className="p-1 bg-gray-700/50 rounded-md hover:bg-blue-500 text-white"><EditIcon /></button>
                                    <button onClick={() => setConfirmDelete(service)} className="p-1 bg-gray-700/50 rounded-md hover:bg-red-500 text-white"><TrashIcon /></button>
                                </div>
                                <div>
                                    <img src={service.imageUrl || 'https://placehold.co/600x400/0a0a0a/cyan?text=GCS1'} alt={service.name} className="w-full h-32 object-cover rounded-md mb-4"/>
                                    
                                    <div className="flex justify-between items-start mb-1">
                                        <h3 className={`text-xl font-bold ${t.secondary}`}>{service.name}</h3>
                                        <div className={`text-xs font-bold px-2 py-1 rounded-full ${availableCount > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                            {availableCount} Disp.
                                        </div>
                                    </div>

                                    <p className={`text-sm ${t.textMuted}`}>{service.type}</p>
                                    <p className="text-xs text-gray-400 mt-2">{service.note}</p>
                                </div>
                                <div className="mt-4 flex gap-2">
                                    <button onClick={() => { setCurrentService(service); setIsInventoryModalOpen(true); }} className={`flex-1 flex items-center justify-center px-4 py-2 font-bold rounded-md ${t.buttonSecondary}`}><InventoryIcon/><span className="ml-2">Inventario</span></button>
                                    <button onClick={() => openMembershipModal(service)} className={`flex-1 flex items-center justify-center px-4 py-2 font-bold rounded-md ${t.buttonSecondary}`}><DollarSignIcon/><span className="ml-2">Precios</span></button>
                                </div>
                            </div>
                        )})}
                </div>
            </div>

            <Modal isOpen={isInventoryModalOpen} onClose={() => setIsInventoryModalOpen(false)} title={`Inventario de: ${currentService?.name}`} maxWidth="max-w-4xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead><tr className={`border-b ${t.border}`}>
                            {(serviceTypeFields[currentService?.type] || []).map(f => <th key={f.name} className="p-2">{f.label}</th>)}
                            <th className="p-2">Vendida (veces)</th>
                        </tr></thead>
                        <tbody>
                            {(allInventory[currentService?.id] || []).length > 0 ? (allInventory[currentService?.id] || []).map(item => (
                                <tr key={item.id} className={`border-b ${t.border} hover:${t.bgInput}`}>
                                    {(serviceTypeFields[currentService?.type] || []).map(f => <td key={f.name} className={`p-2 ${t.textMuted} truncate max-w-[120px]`}>{item[f.name]}</td>)}
                                    <td className={`p-2 ${t.secondary}`}>{item.assignments || 0} / {item.maxAssignments}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={(serviceTypeFields[currentService?.type] || []).length + 1} className="text-center p-4 text-gray-500">No hay inventario para este servicio.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Modal>

            <Modal isOpen={isServiceModalOpen} onClose={closeServiceModal} title={editingService ? 'Editar Servicio' : 'Añadir Nuevo Servicio'} maxWidth="max-w-md">
                <form onSubmit={handleSaveService} className="space-y-4">
                    <label className={`block text-sm font-bold ${t.textMuted}`}>Tipo de Servicio</label>
                    <select value={serviceFormData.type} name="type" onChange={handleServiceFormChange} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}><option>Streaming</option><option>Canva</option><option>Capcut</option><option>IPTV</option></select>
                    <label className={`block text-sm font-bold ${t.textMuted}`}>Nombre del Servicio</label>
                    <input required name="name" value={serviceFormData.name || ''} onChange={handleServiceFormChange} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                    
                    <label className={`block text-sm font-bold ${t.textMuted}`}>URL de Imagen (Logo)</label>
                    <div className="flex items-center gap-2">
                        <input type="url" required name="imageUrl" value={serviceFormData.imageUrl || ''} onChange={handleServiceFormChange} className={`flex-grow px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        <button type="button" onClick={() => setIsLogoModalOpen(true)} className={`px-4 py-2 font-bold rounded-md ${t.buttonSecondary}`}>Buscar Logo</button>
                    </div>
                    
                    <label className={`block text-sm font-bold ${t.textMuted}`}>Nota</label>
                    <input name="note" value={serviceFormData.note || ''} onChange={handleServiceFormChange} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                    <div className="flex items-center gap-2">
                        <input type="checkbox" id="allowMultiple" name="allowMultipleAssignments" checked={serviceFormData.allowMultipleAssignments} onChange={handleServiceFormChange} className="accent-pink-500"/>
                        <label htmlFor="allowMultiple" className={`text-sm ${t.textMuted}`}>Permitir asignar varias veces al mismo cliente</label>
                    </div>

                    <div className="border-t border-gray-700 my-4 pt-4">
                        <h4 className={`text-lg font-bold ${t.primary} mb-2`}>Configuración de Compra y Venta por Defecto</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Proveedor por Defecto</label>
                                <select name="defaultProviderId" value={serviceFormData.defaultProviderId} onChange={handleServiceFormChange} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                    <option value="">-- Opcional --</option>
                                    <option value="propia">Propiedad Propia</option>
                                    {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Duración de Compra (Días)</label>
                                <input type="text" name="defaultPurchaseDuration" value={serviceFormData.defaultPurchaseDuration} onChange={handleNumericServiceFormChange} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Precio de Compra (USD)</label>
                                <input type="text" name="defaultPurchasePrice" value={serviceFormData.defaultPurchasePrice} onChange={handleNumericServiceFormChange} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                        </div>
                        <div className="mt-4">
                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Instrucciones para Cliente</label>
                            <textarea name="defaultInstructions" value={serviceFormData.defaultInstructions || ''} onChange={handleServiceFormChange} className={`w-full h-24 px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        </div>
                    </div>
                    
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={closeServiceModal} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                        <button type="submit" className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar</button>
                    </div>
                </form>
            </Modal>
            
            <Modal isOpen={confirmDelete} onClose={() => setConfirmDelete(null)} title="Confirmar Eliminación" maxWidth="max-w-md">
                 <p className={t.textMuted}>¿Estás seguro de que quieres eliminar el servicio <span className="font-bold text-white">{confirmDelete?.name}</span>?</p>
                 <p className="text-sm text-yellow-500 mt-2">Advertencia: Esto eliminará permanentemente el servicio y TODO su inventario asociado.</p>
                 <div className="flex justify-end gap-4 pt-6">
                      <button onClick={() => setConfirmDelete(null)} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                      <button onClick={handleDeleteService} className="px-4 py-2 bg-red-600 text-white font-bold rounded-md hover:bg-red-500">Eliminar</button>
                 </div>
            </Modal>
            
            <LogoModal isOpen={isLogoModalOpen} onClose={() => setIsLogoModalOpen(false)} />
            
            <Modal isOpen={isMembershipModalOpen} onClose={closeMembershipModal} title={`Precios y Membresías para ${currentService?.name}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className={`${t.bg} p-4 rounded-lg border ${t.border}`}>
                        <h4 className="font-bold mb-4">{currentMembership ? 'Editar Membresía' : 'Añadir Nueva'}</h4>
                        <form onSubmit={handleSaveMembership} className="space-y-3">
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-1`}>Nombre</label>
                                <input required value={membershipFormData.name} name="name" onChange={handleMembershipFormChange} placeholder="Ej: 1 Mes" className={`w-full px-3 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-1`}>Duración (Días)</label>
                                    <input type="text" required value={membershipFormData.durationDays} onChange={handleNumericMembershipFormChange} name="durationDays" placeholder="30" className={`w-full px-3 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-1`}>Precio (USD)</label>
                                    <input type="text" required value={membershipFormData.price} onChange={handleNumericMembershipFormChange} name="price" placeholder="5.00" className={`w-full px-3 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                </div>
                            </div>
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-1`}>Precio para Revendedor (USD)</label>
                                <input type="text" value={membershipFormData.resellerPrice} onChange={handleNumericMembershipFormChange} name="resellerPrice" placeholder="4.00" className={`w-full px-3 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                            {/* --- INICIO DEL CÓDIGO AÑADIDO --- */}
                            <div className="flex items-center pt-2">
                                <input 
                                    type="checkbox" 
                                    id="isResellerPlan" 
                                    name="isResellerPlan" 
                                    checked={!!membershipFormData.isResellerPlan} 
                                    onChange={handleMembershipFormChange} 
                                    className="h-4 w-4 rounded accent-cyan-500" 
                                />
                                <label htmlFor="isResellerPlan" className={`ml-2 text-sm font-bold ${t.textMuted}`}>Exclusivo para Revendedores</label>
                            </div>
                             {/* --- FIN DEL CÓDIGO AÑADIDO --- */}
                            <div className="flex justify-end gap-2 pt-2">
                                {currentMembership && <button type="button" onClick={() => {setCurrentMembership(null); setMembershipFormData(initialMembershipForm);}} className={`px-3 py-1 text-xs rounded-md ${t.buttonSecondary}`}>Cancelar Edición</button>}
                                <button type="submit" className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>{currentMembership ? 'Actualizar' : 'Añadir'}</button>
                            </div>
                        </form>
                    </div>
                    
                    <div className="max-h-80 overflow-y-auto">
                        {serviceMemberships.map(mem => (
                            <div key={mem.id} className={`${t.bg} p-3 mb-2 rounded-md border ${t.border} ${mem.isResellerPlan ? 'border-cyan-500/50' : ''}`}>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="font-bold">{mem.name} ({mem.durationDays} días)</p>
                                        <p className={`text-lg font-bold ${t.primary}`}>${mem.price} <span className="text-sm font-normal text-gray-500"> / Revendedor: ${mem.resellerPrice || 'N/A'}</span></p>
                                        {/* AÑADIDO: Indicador visual para planes de revendedor */}
                                        {mem.isResellerPlan && <span className="text-xs font-bold bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded-full">Exclusivo Revendedor</span>}
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => openMembershipModal(currentService, mem)} className="p-2 hover:text-blue-400"><EditIcon /></button>
                                        <button onClick={() => handleDeleteMembership(mem.id)} className="p-2 hover:text-red-400"><TrashIcon /></button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </>
    );
}



function SalesView({ user, services, sales, clients, messageTemplates, businessProfile, inventory }) {
    const { theme, themes } = useTheme();
    const t = themes[theme] || themes.netflix_dark;
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 9;
    const userRole = user.role || 'owner';
    const teamId = user.teamId || user.uid;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const initialFormState = { saleType: 'new', clientId: '', clientName: '', serviceId: '', selectedInventoryIds: [], membershipId: '', quantity: 1, selectedSaleId: '', isEditing: false };
    const [formData, setFormData] = useState(initialFormState);

    const [clientSearch, setClientSearch] = useState('');
    const [isCreatingClient, setIsCreatingClient] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [newClientContact, setNewClientContact] = useState('');
    const [renewableSales, setRenewableSales] = useState([]);
    
    // Cambios aquí: Estado para todas las membresías y las de revendedor
    const [allServiceMemberships, setAllServiceMemberships] = useState([]);
    const [serviceMemberships, setServiceMemberships] = useState([]);
    const [showResellerPlans, setShowResellerPlans] = useState(false);
    
    const [renewalModal, setRenewalModal] = useState({ isOpen: false, sale: null, membershipId: '' });
    const [renewalMemberships, setRenewalMemberships] = useState([]);

    const [notifyRenewalModal, setNotifyRenewalModal] = useState({ isOpen: false, sale: null, client: null });
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');

    const [filterStatus, setFilterStatus] = useState('all');
    const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, id: null });

    const selectedServiceForSale = services.find(s => s.id === formData.serviceId);

    // --- Lógica de Manejo de Estados y Efectos ---
    useEffect(() => {
        if (formData.saleType === 'renewal' && formData.clientId) {
            const clientSales = sales.filter(s => s.clientId === formData.clientId);
            setRenewableSales(clientSales);
        } else {
            setRenewableSales([]);
        }
    }, [formData.saleType, formData.clientId, sales]);

    // Lógica para obtener las membresías del servicio seleccionado
    useEffect(() => {
        let unsubscribe = () => {};
        if ((isModalOpen && formData.serviceId) || (renewalModal.isOpen && renewalModal.sale)) {
            const serviceId = isModalOpen ? formData.serviceId : renewalModal.sale?.serviceId;
            if (serviceId) {
                const membershipsRef = collection(db, 'users', teamId, 'services', serviceId, 'memberships');
                const q = query(membershipsRef, orderBy('durationDays', 'asc'));
                unsubscribe = onSnapshot(q, (snapshot) => {
                    const memberships = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    // Almacenar todas las membresías
                    setAllServiceMemberships(memberships);
                    // Filtrar por defecto si no son de revendedor
                    const filteredMemberships = memberships.filter(m => !m.isResellerPlan);
                    if (isModalOpen) setServiceMemberships(filteredMemberships);
                    if (renewalModal.isOpen) setRenewalMemberships(memberships);
                });
            }
        } else {
            setAllServiceMemberships([]);
            setServiceMemberships([]);
            setRenewalMemberships([]);
        }
        return () => unsubscribe();
    }, [isModalOpen, renewalModal.isOpen, formData.serviceId, renewalModal.sale, teamId, db]);

    // Efecto para filtrar los planes cuando se cambia el interruptor de revendedor
    useEffect(() => {
        if (showResellerPlans) {
            setServiceMemberships(allServiceMemberships.filter(m => m.isResellerPlan));
        } else {
            setServiceMemberships(allServiceMemberships.filter(m => !m.isResellerPlan));
        }
    }, [showResellerPlans, allServiceMemberships]);

    // --- Funciones para abrir modales ---
    const openModalForNewSale = () => {
        setFormData(initialFormState);
        setClientSearch('');
        setIsCreatingClient(false);
        setIsModalOpen(true);
        setShowResellerPlans(false);
    };

    const openModalForEdit = (sale) => {
        const selectedMembership = allServiceMemberships.find(m => m.id === sale.membershipId);
        const isReseller = selectedMembership?.isResellerPlan || false;

        setFormData({
            ...sale,
            saleType: 'new',
            selectedInventoryIds: sale.inventoryIds || (sale.inventoryId ? [sale.inventoryId] : []),
            isEditing: true
        });
        setClientSearch(clients.find(c => c.id === sale.clientId)?.name || '');
        setShowResellerPlans(isReseller);
        setIsModalOpen(true);
    };

    // --- Lógica del formulario ---
    const handleInventorySelection = (inventoryId) => {
        const currentSelection = formData.selectedInventoryIds;
        const isSelected = currentSelection.includes(inventoryId);
        let newSelection;
        const quantityLimit = Number(formData.quantity) || 0;
        if (isSelected) {
            newSelection = currentSelection.filter(id => id !== inventoryId);
        } else {
            if (quantityLimit > 0 && currentSelection.length >= quantityLimit) {
                showNotification(`Solo puedes seleccionar ${quantityLimit} cuenta(s).`, 'error');
                return;
            }
            newSelection = [...currentSelection, inventoryId];
        }
        setFormData({ ...formData, selectedInventoryIds: newSelection });
    };

    const handleSaveSale = async (event) => {
        event.preventDefault();
        const finalQuantity = Number(formData.quantity || 1);
        let finalClientId = formData.clientId;

        // Lógica de creación de cliente
        if (isCreatingClient) {
            if (!newClientName || !newClientContact) {
                showNotification("El nombre y contacto del nuevo cliente son obligatorios.", "error");
                return;
            }
            try {
                const clientsRef = collection(db, 'users', teamId, 'clients');
                const newClientDoc = await addDoc(clientsRef, { name: newClientName, contact: newClientContact, note: 'Creado desde ventas', createdAt: serverTimestamp() });
                finalClientId = newClientDoc.id;
                showNotification('¡Cliente nuevo creado!', 'success');
            } catch (error) {
                console.error("Error creating new client:", error);
                showNotification('Error al crear el cliente.', 'error');
                return;
            }
        }

        if (!finalClientId) {
            showNotification("Por favor, selecciona o crea un cliente.", "error");
            return;
        }

        const selectedMembership = allServiceMemberships.find(m => m.id === formData.membershipId);
        if (!selectedMembership) {
            showNotification("Debe seleccionar una membresía válida.", "error");
            return;
        }

        try {
            if (formData.isEditing) {
                // Lógica de edición
                const saleToUpdateRef = doc(db, 'users', teamId, 'sales', formData.id);
                await updateDoc(saleToUpdateRef, {
                    clientId: finalClientId,
                    serviceId: formData.serviceId,
                    inventoryIds: formData.selectedInventoryIds,
                    quantity: finalQuantity,
                    membershipId: formData.membershipId,
                    membershipName: selectedMembership.name,
                    salePrice: (Number(selectedMembership.price) || 0) * finalQuantity,
                });
                showNotification('Venta editada con éxito.', 'success');
            } else if (formData.saleType === 'renewal') {
                // Lógica de renovación existente
                const originalSale = sales.find(s => s.id === formData.selectedSaleId);
                if (!originalSale || !originalSale.expiresAt?.toDate) {
                    showNotification("No se pudo encontrar la venta original o su fecha de vencimiento para renovar.", "error");
                    return;
                }

                const now = new Date();
                const originalExpiryDate = originalSale.expiresAt.toDate();
                let baseDate = (originalSale.status === 'suspended' || originalExpiryDate < now) ? now : originalExpiryDate;
                const newExpiresAt = new Date(baseDate);
                newExpiresAt.setDate(newExpiresAt.getDate() + parseInt(selectedMembership.durationDays));

                const saleToUpdateRef = doc(db, 'users', teamId, 'sales', formData.selectedSaleId);
                await updateDoc(saleToUpdateRef, {
                    expiresAt: newExpiresAt,
                    membershipId: formData.membershipId,
                    membershipName: selectedMembership.name,
                    lastRenewedAt: serverTimestamp(),
                    salePrice: (Number(selectedMembership.price) || 0) * finalQuantity,
                    saleDate: new Date().toISOString().split('T')[0],
                    status: 'active'
                });

                showNotification('¡Renovación exitosa!', 'success');
                const client = clients.find(c => c.id === finalClientId);
                const updatedSaleData = { ...originalSale, expiresAt: Timestamp.fromDate(newExpiresAt) };

                setNotifyRenewalModal({ isOpen: true, sale: updatedSaleData, client: client });

            } else {
                // Lógica de venta nueva
                if (formData.selectedInventoryIds.length !== finalQuantity) {
                    showNotification(`Debes seleccionar exactamente ${finalQuantity} cuenta(s) del inventario.`, "error");
                    return;
                }
                const batch = writeBatch(db);
                const saleDate = new Date();
                const expiresAt = new Date(saleDate);
                expiresAt.setDate(expiresAt.getDate() + parseInt(selectedMembership.durationDays));
                const saleData = {
                    clientId: finalClientId,
                    serviceId: formData.serviceId,
                    inventoryIds: formData.selectedInventoryIds,
                    quantity: finalQuantity,
                    membershipId: formData.membershipId,
                    membershipName: selectedMembership.name,
                    saleType: 'new',
                    saleDate: saleDate.toISOString().split('T')[0],
                    createdAt: serverTimestamp(),
                    expiresAt: expiresAt,
                    salePrice: (Number(selectedMembership.price) || 0) * finalQuantity,
                    status: 'active',
                    createdBy: user.uid
                };
                const saleRef = doc(collection(db, 'users', teamId, 'sales'));
                batch.set(saleRef, saleData);

                for (const inventoryId of formData.selectedInventoryIds) {
                    const inventoryRef = doc(db, 'users', teamId, 'services', formData.serviceId, 'inventory', inventoryId);
                    batch.update(inventoryRef, { assignments: increment(1) });
                }
                await batch.commit();
                showNotification(`¡Venta de ${finalQuantity} cuenta(s) registrada con éxito!`, 'success');
            }

            // Cerrar y reiniciar formulario
            setIsModalOpen(false);
            setFormData(initialFormState);
            setClientSearch('');
            setIsCreatingClient(false);
        } catch (error) {
            console.error("Error al registrar la venta:", error);
            showNotification("Ocurrió un error al registrar la venta.", "error");
        }
    };

    const handleRenewSale = async () => {
        const { sale, membershipId } = renewalModal;
        if (!sale || !membershipId) {
            showNotification("Por favor, selecciona una membresía.", "error");
            return;
        }
        const selectedMembership = renewalMemberships.find(m => m.id === membershipId);
        if (!selectedMembership) {
            showNotification("Membresía no válida.", "error");
            return;
        }

        const now = new Date();
        const originalExpiryDate = sale.expiresAt.toDate();
        let baseDate;

        if (sale.status === 'suspended' || originalExpiryDate < now) {
            baseDate = now;
        } else {
            baseDate = originalExpiryDate;
        }
        const newExpiresAt = new Date(baseDate);
        newExpiresAt.setDate(newExpiresAt.getDate() + parseInt(selectedMembership.durationDays));
        const saleRef = doc(db, 'users', teamId, 'sales', sale.id);

        try {
            await updateDoc(saleRef, {
                expiresAt: newExpiresAt,
                membershipId: membershipId,
                membershipName: selectedMembership.name,
                status: 'active',
                lastRenewedAt: serverTimestamp(),
                saleDate: new Date().toISOString().split('T')[0]
            });
            showNotification('¡Suscripción renovada con éxito!', 'success');
            const client = clients.find(c => c.id === sale.clientId);
            setNotifyRenewalModal({ isOpen: true, sale: { ...sale, expiresAt: Timestamp.fromDate(newExpiresAt) }, client });
            setRenewalModal({ isOpen: false, sale: null, membershipId: '' });
        } catch (error) {
            console.error("Error renewing sale:", error);
            showNotification("Error al renovar la suscripción.", "error");
        }
    };

    const handleSuspendSale = async (saleId) => {
        const saleRef = doc(db, 'users', teamId, 'sales', saleId);
        try {
            await updateDoc(saleRef, { status: 'suspended' });
            showNotification('La suscripción ha sido suspendida.', 'info');
        } catch (error) {
            console.error("Error suspending sale:", error);
            showNotification("Error al suspender la suscripción.", "error");
        }
    };

    const handleDeleteRequest = (saleId) => {
        setConfirmDeleteModal({ isOpen: true, id: saleId });
    };

    const handleConfirmDelete = async () => {
        const saleId = confirmDeleteModal.id;
        if (!saleId) return;

        try {
            const saleToDelete = sales.find(s => s.id === saleId);
            if (!saleToDelete) {
                showNotification('Venta no encontrada.', 'error');
                return;
            }

            const batch = writeBatch(db);
            const saleRef = doc(db, 'users', teamId, 'sales', saleId);
            batch.delete(saleRef);

            const inventoryIds = saleToDelete.inventoryIds || (saleToDelete.inventoryId ? [saleToDelete.inventoryId] : []);
            for (const inventoryId of inventoryIds) {
                const inventoryRef = doc(db, 'users', teamId, 'services', saleToDelete.serviceId, 'inventory', inventoryId);
                batch.update(inventoryRef, { assignments: increment(-1) });
            }

            await batch.commit();
            showNotification('Venta eliminada y inventario liberado.', 'success');
        } catch (error) {
            console.error("Error al eliminar la venta:", error);
            showNotification("Error al eliminar la venta. Inténtalo de nuevo.", 'error');
        } finally {
            setConfirmDeleteModal({ isOpen: false, id: null });
        }
    };

    const getEntityName = (id, list) => (list || []).find(item => item.id === id)?.name || 'N/A';

    const getInventoryIdentifier = (item, service) => {
        if (!item) return 'N/A';
        if (service?.type === 'Streaming' && item.profile) {
            return `${item.email} - Perfil: ${item.profile}`;
        }
        if ((service?.type === 'Canva' || service?.type === 'Capcut') && item.matrixEmail && item.className) {
            return `${item.matrixEmail} - Clase: ${item.className}`;
        }
        return item.email || item.matrixEmail || item.inviteLink || item.user || 'ID: ' + item.id.slice(-6);
    };

    const generateActionLink = (actionType, sale) => {
        const client = (clients || []).find(c => c.id === sale.clientId);
        const service = (services || []).find(s => s.id === sale.serviceId);
        if (!client || !service || !inventory[sale.serviceId]) return '#';
        const inventoryIds = sale.inventoryIds || (sale.inventoryId ? [sale.inventoryId] : []);
        const inventoryItems = inventoryIds.map(id => inventory[sale.serviceId].find(i => i.id === id)).filter(Boolean);
        if (inventoryItems.length === 0) return '#';
        const template = messageTemplates[actionType] || '';
        const allAccountDetails = inventoryItems.map(item => {
            const accountDataFields = serviceTypeFields[service.type] || [];
            return accountDataFields.map(field => `${field.label}: ${item[field.name] || ''}`).join('\n');
        }).join('\n\n---\n\n');
        const expirationDate = sale.expiresAt?.toDate ? sale.expiresAt.toDate().toLocaleDateString() : 'N/A';
        const businessContact = [businessProfile.whatsapp, businessProfile.telegram, businessProfile.website].filter(Boolean).join(' | ');
        let message = template
            .replace(/{cliente}/g, client.name)
            .replace(/{servicio}/g, service.name)
            .replace(/{vencimiento}/g, expirationDate)
            .replace(/{datos_cuenta}/g, allAccountDetails)
            .replace(/{instrucciones}/g, inventoryItems[0]?.instructions || '')
            .replace(/{mi_negocio}/g, businessProfile.name || 'nuestro equipo')
            .replace(/{mi_contacto}/g, businessContact);
        if (client.contact?.includes('@')) { return `https://t.me/${client.contact.replace('@', '')}?text=${encodeURIComponent(message)}`; }
        return `https://wa.me/${client.contact}?text=${encodeURIComponent(message)}`;
    };

    const filteredAndSortedSales = useMemo(() => {
        let filtered = sales || [];
        if (filterStatus !== 'all') {
            const now = new Date();
            filtered = filtered.filter(sale => {
                const isExpired = sale.expiresAt?.toDate() < now;
                const isSuspended = sale.status === 'suspended';
                if (filterStatus === 'suspended') return isSuspended;
                if (filterStatus === 'expired') return isExpired && !isSuspended;
                if (filterStatus === 'active') return !isExpired && !isSuspended;
                return true;
            });
        }
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(sale => {
                const client = clients.find(c => c.id === sale.clientId);
                const serviceName = (getEntityName(sale.serviceId, services) || '').toLowerCase();
                if (client && ((client.name || '').toLowerCase().includes(search) || (client.contact || '').toLowerCase().includes(search))) return true;
                if (serviceName.includes(search)) return true;
                return false;
            });
        }
        return filtered.sort((a, b) => {
            let dateA, dateB;
            if (sortBy === 'createdAt') {
                dateA = a.lastRenewedAt?.toDate() || a.createdAt?.toDate() || 0;
                dateB = b.lastRenewedAt?.toDate() || b.createdAt?.toDate() || 0;
            } else {
                dateA = a.expiresAt?.toDate() || 0;
                dateB = b.expiresAt?.toDate() || 0;
            }
            if (sortDirection === 'asc') {
                return dateA - dateB;
            } else {
                return dateB - dateA;
            }
        });
    }, [sales, clients, services, searchTerm, sortBy, sortDirection, filterStatus]);

    const filteredClients = (clients || []).filter(client => client.name.toLowerCase().includes(clientSearch.toLowerCase()));
    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = filteredAndSortedSales.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredAndSortedSales.length / ITEMS_PER_PAGE);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const paginationItems = getPaginationItems(totalPages, currentPage);

    return (
        <>
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h2 className={`text-2xl font-bold ${t.primary}`}>Registro de Ventas</h2>
                    <div className="flex-grow flex flex-col sm:flex-row justify-end items-center gap-2">
                        <div className="relative w-full sm:w-auto">
                            <input type="text" placeholder="Buscar por cliente, servicio, cuenta..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full sm:w-64 pl-10 pr-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SearchIcon /></div>
                        </div>
                        <div className={`p-1 rounded-md ${t.bgInput} flex items-center text-xs`}>
                            <button onClick={() => { setSortBy('createdAt'); setSortDirection('desc'); }} className={`px-2 py-1 rounded ${sortBy === 'createdAt' ? t.buttonPrimary : ''}`}>Más Recientes</button>
                            <button onClick={() => setSortBy('expiresAt')} className={`px-2 py-1 rounded ${sortBy === 'expiresAt' ? t.buttonPrimary : ''}`}>Por Vencimiento</button>
                        </div>
                        {sortBy === 'expiresAt' && (
                            <div className={`p-1 rounded-md ${t.bgInput} flex items-center text-xs`}>
                                <button onClick={() => setSortDirection('asc')} className={`px-2 py-1 rounded ${sortDirection === 'asc' ? t.buttonPrimary : ''}`}>Próximas</button>
                                <button onClick={() => setSortDirection('desc')} className={`px-2 py-1 rounded ${sortDirection === 'desc' ? t.buttonPrimary : ''}`}>Lejanas</button>
                            </div>
                        )}
                        {(userRole === 'owner' || userRole === 'asesor_ventas') && (
                            <button onClick={openModalForNewSale} className={`flex items-center px-4 py-2 font-bold rounded-md w-full sm:w-auto justify-center ${t.buttonPrimary}`}><PlusCircleIcon /> <span className="hidden sm:inline ml-2">Nueva Venta</span></button>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className={`text-sm font-bold ${t.textMuted}`}>Filtrar:</span>
                    <button onClick={() => setFilterStatus('all')} className={`px-3 py-1 text-xs rounded-full ${filterStatus === 'all' ? t.buttonPrimary : t.buttonSecondary}`}>Todos</button>
                    <button onClick={() => setFilterStatus('active')} className={`px-3 py-1 text-xs rounded-full ${filterStatus === 'active' ? t.buttonPrimary : t.buttonSecondary}`}>Activos</button>
                    <button onClick={() => setFilterStatus('expired')} className={`px-3 py-1 text-xs rounded-full ${filterStatus === 'expired' ? t.buttonPrimary : t.buttonSecondary}`}>Vencidos</button>
                    <button onClick={() => setFilterStatus('suspended')} className={`px-3 py-1 text-xs rounded-full ${filterStatus === 'suspended' ? t.buttonPrimary : t.buttonSecondary}`}>Suspendidos</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {currentItems.map(sale => {
                        const isExpired = sale.expiresAt?.toDate() < new Date();
                        const isSuspended = sale.status === 'suspended';
                        const cardClass = isSuspended ? 'opacity-50' : isExpired ? 'border-red-500/50' : t.border;
                        const inventoryIds = sale.inventoryIds || (sale.inventoryId ? [sale.inventoryId] : []);
                        const service = services.find(s => s.id === sale.serviceId);
                        const accountDetails = inventoryIds.map(id => {
                            const item = inventory[sale.serviceId]?.find(i => i.id === id);
                            return getInventoryIdentifier(item, service);
                        }).join(', ');
                        return (
                            <div key={sale.id} className={`${t.bg} p-4 rounded-lg border ${cardClass} flex flex-col justify-between shadow-lg`}>
                                <div className="space-y-2 mb-4">
                                    <p className={`text-xs font-bold ${t.textMuted}`}>Cliente</p>
                                    <p className={`font-semibold ${t.secondary}`}>{getEntityName(sale.clientId, clients)}</p>
                                    <p className={`text-xs font-bold ${t.textMuted}`}>Servicio</p>
                                    <p className={`font-semibold ${t.secondary}`}>{getEntityName(sale.serviceId, services)} {sale.quantity > 1 ? `(x${sale.quantity})` : ''}</p>
                                    <p className={`text-xs font-bold ${t.textMuted}`}>Cuenta(s) Entregada(s)</p>
                                    <p className={`font-mono text-sm ${t.textMuted} break-all`}>{accountDetails}</p>
                                    <p className={`text-xs font-bold ${t.textMuted}`}>Membresía</p>
                                    <p className={t.textMuted}>{sale.membershipName}</p>
                                    <div className="flex justify-between items-baseline">
                                        <div>
                                            <p className={`text-xs font-bold ${t.textMuted}`}>Fecha Venta</p>
                                            <p className={t.textMuted}>{sale.saleDate}</p>
                                        </div>
                                        <div>
                                            <p className={`text-xs font-bold ${isExpired || isSuspended ? 'text-red-400' : t.textMuted}`}>Vence</p>
                                            <p className={`${isExpired || isSuspended ? 'text-red-400 font-bold' : t.textMuted}`}>{sale.expiresAt?.toDate ? sale.expiresAt.toDate().toLocaleDateString() : 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                                {(userRole === 'owner' || userRole === 'asesor_ventas') && (
                                    <div className={`border-t ${t.border} pt-3 mt-auto flex items-center justify-center gap-2`}>
                                        {!isSuspended && !isExpired && (
                                            <>
                                                <a href={generateActionLink('deliver', sale)} target="_blank" rel="noopener noreferrer" title="Entregar" className="p-2 bg-green-500/20 text-green-400 rounded-md hover:bg-green-500/40"><SparklesIcon /></a>
                                                <a href={generateActionLink('renewal_warning', sale)} target="_blank" rel="noopener noreferrer" title="Avisar Vencimiento" className="p-2 bg-yellow-500/20 text-yellow-400 rounded-md hover:bg-yellow-500/40"><BellIcon /></a>
                                                <button onClick={() => setRenewalModal({ isOpen: true, sale, membershipId: '' })} title="Extender Plan" className="p-2 bg-blue-500/20 text-blue-400 rounded-md hover:bg-blue-500/40"><RefreshCwIcon /></button>
                                                <a href={generateActionLink('renewal_success', sale)} target="_blank" rel="noopener noreferrer" title="Notificar Renovación" className="p-2 bg-cyan-500/20 text-cyan-400 rounded-md hover:bg-cyan-500/40"><SendIcon /></a>
                                            </>
                                        )}
                                        {!isSuspended && isExpired && (
                                            <a href={generateActionLink('charge', sale)} target="_blank" rel="noopener noreferrer" title="Cobrar" className="p-2 bg-red-500/20 text-red-400 rounded-md hover:bg-red-500/40"><DollarSignIcon /></a>
                                        )}
                                        {(isExpired || isSuspended) && (
                                            <button onClick={() => setRenewalModal({ isOpen: true, sale, membershipId: '' })} title="Renovar" className="p-2 bg-blue-500 rounded-md text-white"><RefreshCwIcon /></button>
                                        )}
                                        {isExpired && !isSuspended && (
                                            <button onClick={() => handleSuspendSale(sale.id)} title="Suspender" className="p-2 bg-gray-600 rounded-md text-white"><AlertTriangleIcon /></button>
                                        )}
                                        {isSuspended && (
                                            <a href={generateActionLink('offer', sale)} target="_blank" rel="noopener noreferrer" title="Enviar Oferta" className="p-2 bg-purple-500 rounded-md text-white"><SendIcon /></a>
                                        )}
                                    </div>
                                )}
                                <div className={`border-t ${t.border} pt-3 mt-auto flex items-center justify-center gap-2`}>
                                    <button onClick={() => openModalForEdit(sale)} title="Editar" className="p-1 hover:text-blue-400"><EditIcon /></button>
                                    <button onClick={() => handleDeleteRequest(sale.id)} title="Eliminar" className="p-1 hover:text-red-400"><TrashIcon /></button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {totalPages > 1 && (
                    <div className="mt-8 flex justify-center items-center space-x-2">
                        <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className={`px-4 py-2 rounded-md ${t.buttonSecondary} disabled:opacity-50`}>Anterior</button>
                        {paginationItems.map((item, index) => {
                            if (item === '...') { return <span key={index} className="px-4 py-2 text-gray-500">...</span>; }
                            return (<button key={index} onClick={() => paginate(item)} className={`px-4 py-2 rounded-md ${currentPage === item ? t.buttonPrimary : t.buttonSecondary}`}>{item}</button>);
                        })}
                        <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className={`px-4 py-2 rounded-md ${t.buttonSecondary} disabled:opacity-50`}>Siguiente</button>
                    </div>
                )}
            </div>

            <Modal isOpen={confirmDeleteModal.isOpen} onClose={() => setConfirmDeleteModal({ isOpen: false, id: null })} title="Confirmar Eliminación">
                <p className={t.textMuted}>
                    ¿Estás seguro de que quieres eliminar esta venta? Esta acción es irreversible y el producto volverá a estar disponible en el inventario.
                </p>
                <div className="flex justify-end gap-4 pt-6">
                    <button onClick={() => setConfirmDeleteModal({ isOpen: false, id: null })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>
                        Cancelar
                    </button>
                    <button onClick={handleConfirmDelete} className="px-4 py-2 bg-red-600 text-white font-bold rounded-md hover:bg-red-500">
                        Eliminar Venta
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={renewalModal.isOpen}
                onClose={() => setRenewalModal({ isOpen: false, sale: null, membershipId: '' })}
                title="Renovar Suscripción"
                maxWidth="max-w-md"
            >
                <p>Cliente: <span className={t.primary}>{getEntityName(renewalModal.sale?.clientId, clients)}</span></p>
                <p className="mb-4">Servicio: <span className={t.primary}>{getEntityName(renewalModal.sale?.serviceId, services)}</span></p>
                <div className="mb-4">
                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Seleccionar Membresía</label>
                    <select value={renewalModal.membershipId} onChange={e => setRenewalModal(prev => ({ ...prev, membershipId: e.target.value }))} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                        <option value="">-- Seleccionar --</option>
                        {renewalMemberships.map(m => <option key={m.id} value={m.id}>{m.name} ({m.durationDays} días) - ${m.price}</option>)}
                    </select>
                </div>
                <div className="flex justify-end gap-4">
                    <button type="button" onClick={() => setRenewalModal({ isOpen: false, sale: null, membershipId: '' })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                    <button type="button" onClick={handleRenewSale} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Confirmar Renovación</button>
                </div>
            </Modal>

            <Modal
                isOpen={notifyRenewalModal.isOpen}
                onClose={() => setNotifyRenewalModal({ isOpen: false, sale: null, client: null })}
                title="Renovación Exitosa"
                maxWidth="max-w-md"
            >
                <div className="text-center">
                    <p className={t.textMuted}>La suscripción para <span className="font-bold text-white">{notifyRenewalModal.client?.name}</span> se ha renovado con éxito.</p>
                    <p className={`${t.textMuted} mt-2`}>¿Deseas notificarle ahora con la nueva plantilla de renovación?</p>
                    <div className="flex justify-center gap-4 mt-6">
                        <button type="button" onClick={() => setNotifyRenewalModal({ isOpen: false, sale: null, client: null })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cerrar</button>
                        <a
                            href={notifyRenewalModal.sale ? generateActionLink('renewal_success', notifyRenewalModal.sale) : '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setNotifyRenewalModal({ isOpen: false, sale: null, client: null })}
                            className={`flex items-center gap-2 px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}
                        >
                            <SendIcon /> Notificar al Cliente
                        </a>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setFormData(initialFormState);
                    setClientSearch('');
                    setIsCreatingClient(false);
                    setShowResellerPlans(false);
                }}
                title={formData.isEditing ? "Editar Venta" : "Registrar Venta"}
            >
                <form onSubmit={handleSaveSale} className="space-y-4">
                    {!formData.isEditing && (
                        <div className="grid grid-cols-2 gap-4">
                            <label className={`flex items-center gap-2 p-4 border ${t.border} rounded-md cursor-pointer has-[:checked]:bg-cyan-500/20`}>
                                <input type="radio" name="saleType" value="new" checked={formData.saleType === 'new'} onChange={e => setFormData({ ...initialFormState, saleType: 'new' })} className="accent-pink-500" /> Venta Nueva
                            </label>
                            <label className={`flex items-center gap-2 p-4 border ${t.border} rounded-md cursor-pointer has-[:checked]:bg-cyan-500/20`}>
                                <input type="radio" name="saleType" value="renewal" checked={formData.saleType === 'renewal'} onChange={e => setFormData({ ...initialFormState, saleType: 'renewal' })} className="accent-pink-500" /> Renovación
                            </label>
                        </div>
                    )}
                    <div>
                        <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Cliente</label>
                        <div className="relative">
                            <input type="text" placeholder="Buscar cliente..." value={clientSearch} onChange={e => { setClientSearch(e.target.value); setFormData({ ...formData, clientId: '' }); setIsCreatingClient(false); }} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            <div className={`absolute z-[60] mt-1 w-full ${t.bgMuted} border ${t.border} rounded-md max-h-40 overflow-y-auto ${(!clientSearch || formData.clientId) ? 'hidden' : ''}`}>
                                {filteredClients.map(c => <div key={c.id} onClick={() => { setFormData({ ...formData, clientId: c.id }); setClientSearch(c.name); }} className={`p-2 hover:${t.bgInput} cursor-pointer`}>{c.name}</div>)}
                                {clientSearch && filteredClients.length === 0 && !isCreatingClient && (<button type="button" onClick={() => setIsCreatingClient(true)} className={`w-full p-2 text-left ${t.buttonSecondary}`}>+ Crear cliente "{clientSearch}"</button>)}
                            </div>
                        </div>
                    </div>
                    {isCreatingClient && (
                        <div className={`p-4 border ${t.border} rounded-md space-y-2`}>
                            <p className="font-bold">Nuevo Cliente</p>
                            <input type="text" placeholder="Nombre del Cliente" value={newClientName} onChange={e => setNewClientName(e.target.value)} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            <input type="text" placeholder="Contacto (WhatsApp/Telegram)" value={newClientContact} onChange={e => setNewClientContact(e.target.value)} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        </div>
                    )}
                    {(formData.saleType === 'new' || formData.isEditing) ? (
                        <>
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Servicio</label>
                                <select name="serviceId" value={formData.serviceId} onChange={(e) => setFormData({ ...formData, serviceId: e.target.value, selectedInventoryIds: [] })} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                    <option value="">-- Seleccionar Servicio --</option>
                                    {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            {userRole === 'owner' && (
                                <div className="flex items-center space-x-2">
                                    <input type="checkbox" id="reseller-switch" checked={showResellerPlans} onChange={() => setShowResellerPlans(!showResellerPlans)} className="h-4 w-4 rounded accent-cyan-500" />
                                    <label htmlFor="reseller-switch" className={`text-sm font-bold ${t.textMuted}`}>Vender a precio de Revendedor</label>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Membresía</label>
                                    <select value={formData.membershipId} onChange={(e) => setFormData({ ...formData, membershipId: e.target.value })} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                        <option value="">-- Seleccionar --</option>
                                        {serviceMemberships.map(m => <option key={m.id} value={m.id}>{m.name} ({m.durationDays} días) - ${m.price}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Cantidad a Vender</label>
                                    <input
                                        type="text"
                                        value={formData.quantity}
                                        onChange={(e) => {
                                            const numericValue = e.target.value.replace(/\D/g, '');
                                            setFormData({
                                                ...formData,
                                                quantity: numericValue,
                                                selectedInventoryIds: []
                                            });
                                        }}
                                        required
                                        placeholder="1"
                                        className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}
                                    />
                                </div>
                            </div>
                            {formData.serviceId && (
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Cuentas Disponibles ({formData.selectedInventoryIds.length} / {Number(formData.quantity) || 0} seleccionadas)</label>
                                    <div className={`p-2 border ${t.border} rounded-md max-h-48 overflow-y-auto space-y-2`}>
                                        {(inventory[formData.serviceId] || [])
                                            .filter(item => {
                                                const hasSlots = (item.assignments || 0) < (item.maxAssignments || 1);
                                                if (!hasSlots) return formData.selectedInventoryIds.includes(item.id);
                                                if (selectedServiceForSale && !selectedServiceForSale.allowMultipleAssignments && formData.clientId && !formData.isEditing) {
                                                    const isAlreadyAssignedToClient = sales.some(sale =>
                                                        sale.clientId === formData.clientId &&
                                                        (sale.inventoryIds || [sale.inventoryId]).includes(item.id) &&
                                                        sale.expiresAt?.toDate() > new Date() &&
                                                        sale.status !== 'suspended'
                                                    );
                                                    return !isAlreadyAssignedToClient;
                                                }
                                                return true;
                                            })
                                            .map(item => (
                                                <label key={item.id} className={`flex items-center justify-between gap-3 p-2 rounded-md cursor-pointer ${formData.selectedInventoryIds.includes(item.id) ? 'bg-cyan-500/20' : 'hover:bg-gray-500/10'}`}>
                                                    <div className="flex items-center gap-3">
                                                        <input type="checkbox" checked={formData.selectedInventoryIds.includes(item.id)} onChange={() => handleInventorySelection(item.id)} className="h-4 w-4 rounded accent-cyan-500" />
                                                        <span>{getInventoryIdentifier(item, selectedServiceForSale)}</span>
                                                    </div>
                                                    <span className="text-xs font-mono bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
                                                        {item.assignments || 0}/{item.maxAssignments || 1}
                                                    </span>
                                                </label>
                                            ))
                                        }
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Lógica de renovación, sin cambios */}
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Cuenta a Renovar</label>
                                <select
                                    value={formData.selectedSaleId || ''}
                                    onChange={e => {
                                        const selectedSale = sales.find(s => s.id === e.target.value);
                                        setFormData({
                                            ...formData,
                                            serviceId: selectedSale ? selectedSale.serviceId : '',
                                            selectedSaleId: e.target.value
                                        });
                                    }}
                                    required
                                    className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}
                                >
                                    <option value="">-- Seleccionar Cuenta Existente --</option>
                                    {renewableSales.map(sale => {
                                        const service = services.find(s => s.id === sale.serviceId);
                                        let identifier;
                                        const inventoryItem = inventory[sale.serviceId]?.find(i => {
                                            const inventoryIds = sale.inventoryIds || [sale.inventoryId];
                                            return inventoryIds.includes(i.id);
                                        });

                                        if (inventoryItem) {
                                            identifier = getInventoryIdentifier(inventoryItem, service);
                                        } else if (sale.inventoryId) {
                                            identifier = 'ID: ' + sale.inventoryId.slice(-6);
                                        } else if (sale.inventoryIds && sale.inventoryIds[0]) {
                                            identifier = 'ID: ' + sale.inventoryIds[0].slice(-6);
                                        } else {
                                            identifier = 'Inventario no encontrado';
                                        }

                                        const expiry = sale.expiresAt?.toDate ? sale.expiresAt.toDate().toLocaleDateString() : 'N/A';
                                        return (<option key={sale.id} value={sale.id}>{`${service?.name} - ${identifier} (Vence: ${expiry})`}</option>);
                                    })}
                                </select>
                            </div>
                            {formData.serviceId && (
                                <div>
                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Membresía de Renovación</label>
                                    <select
                                        value={formData.membershipId}
                                        onChange={(e) => setFormData({ ...formData, membershipId: e.target.value })}
                                        required
                                        className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}
                                    >
                                        <option value="">-- Seleccionar Plan --</option>
                                        {serviceMemberships.map(m => <option key={m.id} value={m.id}>{m.name} ({m.durationDays} días) - ${m.price}</option>)}
                                    </select>
                                </div>
                            )}
                        </>
                    )}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={() => { setIsModalOpen(false); setFormData(initialFormState); setClientSearch(''); setIsCreatingClient(false); }} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                        <button type="submit" className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>{formData.isEditing ? "Guardar Cambios" : "Guardar"}</button>
                    </div>
                </form>
            </Modal>
        </>
    );
}

// --- VISTA DE ESTADO DE CUENTAS (Alertas) CON LAYOUT HORIZONTAL ---
function AccountStatusView({ user, providers, messageTemplates, businessProfile, allServices, sales, clients, inventory }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();

    const [expiringSales, setExpiringSales] = useState([]);
    const [expiringPurchases, setExpiringPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [renewalModal, setRenewalModal] = useState({ isOpen: false, purchase: null, duration: 30 });

    useEffect(() => {
        if (!user?.uid) { setLoading(false); return; }
        
        const salesRef = collection(db, 'users', user.uid, 'sales');
        const fiveDaysFromNow = new Date();
        fiveDaysFromNow.setDate(fiveDaysFromNow.getDate() + 5);

        const qSales = query(salesRef, where('expiresAt', '<=', fiveDaysFromNow));
        
        const unsubSales = onSnapshot(qSales, (snapshot) => {
            const salesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const enrichedSales = salesData
                .filter(sale => sale.status !== 'suspended' && sale.expiresAt?.toDate() >= new Date())
                .map(sale => ({
                    ...sale,
                    clientName: (clients || []).find(c => c.id === sale.clientId)?.name || 'N/A',
                    serviceName: (allServices || []).find(s => s.id === sale.serviceId)?.name || 'N/A',
                })).sort((a, b) => a.expiresAt.toDate() - b.expiresAt.toDate());
            setExpiringSales(enrichedSales);
            setLoading(false);
        }, err => { console.error("Error fetching expiring sales:", err); setLoading(false); });

        const purchasesRef = collection(db, 'users', user.uid, 'purchases');
        
        const qPurchases = query(purchasesRef, where('renewalDate', '<=', fiveDaysFromNow.toISOString().split('T')[0]));
        const unsubPurchases = onSnapshot(qPurchases, (snapshot) => {
            const purchasesData = snapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
            setExpiringPurchases(purchasesData.filter(p => new Date(p.renewalDate) >= new Date()));
        }, err => { console.error("Error fetching expiring purchases:", err); });

        return () => {
            unsubSales();
            unsubPurchases();
        };
    }, [user.uid, db, clients, allServices]);

    const generateActionLink = (actionType, sale) => {
        const client = (clients || []).find(c => c.id === sale.clientId);
        const service = (allServices || []).find(s => s.id === sale.serviceId);
        if (!client || !service || !inventory || !inventory[sale.serviceId]) return '#';
        const inventoryIds = sale.inventoryIds || (sale.inventoryId ? [sale.inventoryId] : []);
        const inventoryItems = inventoryIds.map(id => inventory[sale.serviceId].find(i => i.id === id)).filter(Boolean);
        if (inventoryItems.length === 0) return '#';
        const template = messageTemplates[actionType] || '';
        const expirationDate = sale.expiresAt?.toDate ? sale.expiresAt.toDate().toLocaleDateString() : 'N/A';
        const businessContact = [businessProfile.whatsapp, businessProfile.telegram, businessProfile.website].filter(Boolean).join(' | ');
        const allAccountDetails = inventoryItems.map(item => (serviceTypeFields[service.type] || []).map(field => `${field.label}: ${item[field.name] || ''}`).join('\n')).join('\n\n---\n\n');
        let message = template.replace(/{cliente}/g, client.name).replace(/{servicio}/g, service.name).replace(/{vencimiento}/g, expirationDate).replace(/{datos_cuenta}/g, allAccountDetails).replace(/{instrucciones}/g, inventoryItems[0].instructions || '').replace(/{mi_negocio}/g, businessProfile.name || 'nuestro equipo').replace(/{mi_contacto}/g, businessContact);
        if (client.contact?.includes('@')) { return `https://t.me/${client.contact.replace('@', '')}?text=${encodeURIComponent(message)}`; }
        return `https://wa.me/${client.contact}?text=${encodeURIComponent(message)}`;
    };
    
    const generateProviderContactLink = (purchase) => {
        const provider = (providers || []).find(p => p.id === purchase.providerId);
        if (!provider || !provider.contact) return '#';
        const message = `Hola ${provider.name}, te contacto para renovar la cuenta de ${purchase.serviceName} (${purchase.email}). ¡Gracias!`;
        if (/^\d+$/.test(provider.contact.replace(/\s/g, ''))) { return `https://wa.me/${provider.contact.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`; }
        if (provider.contact.includes('@')) { return `https://t.me/${provider.contact.replace('@', '')}?text=${encodeURIComponent(message)}`; }
        return '#';
    };

    const handleOpenRenewModal = (purchase) => setRenewalModal({ isOpen: true, purchase, duration: purchase.duration || 30 });

    const handleConfirmRenewal = async () => {
        const { purchase, duration } = renewalModal;
        if (!purchase || !duration) return;
        const purchaseRef = doc(db, 'users', user.uid, 'purchases', purchase.id);
        const newRenewalDate = new Date(purchase.renewalDate);
        newRenewalDate.setDate(newRenewalDate.getDate() + parseInt(duration));
        try {
            await updateDoc(purchaseRef, {
                renewalDate: newRenewalDate.toISOString().split('T')[0],
                duration: parseInt(duration),
            });
            showNotification('Compra renovada con éxito.', 'success');
            setRenewalModal({ isOpen: false, purchase: null, duration: 30 });
        } catch (error) {
            console.error("Error renewing purchase:", error);
            showNotification('Error al renovar la compra.', 'error');
        }
    };

    if (loading) return <div className="p-4 text-center">Cargando alertas...</div>;
    if (expiringSales.length === 0 && expiringPurchases.length === 0) {
        return <p className={`text-sm ${t.textMuted}`}>No hay alertas por el momento.</p>;
    }

    return (
        <>
            {/* ▼▼▼ CORRECCIÓN: Se vuelve a un grid de 2 columnas en pantallas medianas (md) y más grandes ▼▼▼ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* --- Columna Izquierda: Alertas de Clientes --- */}
                {expiringSales.length > 0 && (
                    <div>
                        <h3 className="text-sm font-bold mb-2 flex items-center"><AlertTriangleIcon className="h-4 w-4 mr-2"/> Cuentas por Vencer (Clientes)</h3>
                        <div className="max-h-48 overflow-y-auto pr-2 space-y-2">
                            {expiringSales.map(sale => (
                                <div key={sale.id} className={`${t.bgMuted} p-2 rounded-md flex justify-between items-center text-xs`}>
                                    <div className="flex-grow truncate">
                                        <p className={`font-bold ${t.secondary} truncate`}>{sale.serviceName}</p>
                                        <p className={`text-xs ${t.text} truncate`}>{sale.clientName}</p>
                                        <p className={`text-xs text-yellow-400 truncate`}>Vence: {sale.expiresAt?.toDate ? sale.expiresAt.toDate().toLocaleDateString() : 'N/A'}</p>
                                    </div>
                                    <a href={generateActionLink('renewal_warning', sale)} target="_blank" rel="noopener noreferrer" title="Avisar Vencimiento" className="ml-2 flex-shrink-0 p-2 bg-yellow-500 rounded-md text-white hover:bg-yellow-400 transition-colors">
                                        <BellIcon/>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Si no hay alertas de clientes, se muestra un espacio vacío para mantener el layout */}
                {expiringSales.length === 0 && <div></div>}

                {/* --- Columna Derecha: Alertas de Compras --- */}
                {expiringPurchases.length > 0 && (
                     <div>
                        <h3 className="text-sm font-bold mb-2 flex items-center"><RefreshCwIcon className="h-4 w-4 mr-2"/> Cuentas por Vencer (Compras)</h3>
                        <div className="max-h-48 overflow-y-auto pr-2 space-y-2">
                            {expiringPurchases.map(purchase => (
                                <div key={purchase.id} className={`${t.bgMuted} p-2 rounded-md flex justify-between items-center text-xs`}>
                                    <div className="flex-grow truncate">
                                        <p className={`font-bold ${t.secondary} truncate`}>{purchase.serviceName}</p>
                                        <p className={`text-xs ${t.text} truncate`}>Proveedor: {(providers || []).find(p => p.id === purchase.providerId)?.name || purchase.providerId}</p>
                                        <p className={`text-xs text-blue-400 truncate`}>Renovar el: {purchase.renewalDate ? new Date(purchase.renewalDate).toLocaleDateString() : 'N/A'}</p>
                                    </div>
                                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                        <a href={generateProviderContactLink(purchase)} target="_blank" rel="noopener noreferrer" title="Contactar Proveedor" className="p-2 bg-blue-500 rounded-md text-white hover:bg-blue-400 transition-colors">
                                            <BellIcon/>
                                        </a>
                                        {purchase.isRenewable && (
                                            <button onClick={() => handleOpenRenewModal(purchase)} title="Registrar Renovación" className="p-2 bg-green-500 rounded-md text-white hover:bg-green-400 transition-colors">
                                                <RefreshCwIcon />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            
            <Modal isOpen={renewalModal.isOpen} onClose={() => setRenewalModal({isOpen: false, purchase: null, duration: 30})} title="Renovar Compra">
                <p className="mb-2">Servicio: <span className={t.primary}>{renewalModal.purchase?.serviceName}</span></p>
                <div className="mb-4">
                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Nueva Duración (días)</label>
                    <input type="number" value={renewalModal.duration} onChange={(e) => setRenewalModal(prev => ({ ...prev, duration: e.target.value }))} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                </div>
                <div className="flex justify-end gap-4">
                    <button onClick={() => setRenewalModal({ isOpen: false, purchase: null, duration: 30 })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                    <button onClick={handleConfirmRenewal} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Confirmar Renovación</button>
                </div>
            </Modal>
        </>
    );
}

// --- COMPONENTE DE ACTIVACIÓN DE LICENCIA ---
function LicenseActivation({ user, onActivationSuccess }) {
    const { theme, themes, font, fonts } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();

    const [licenseKey, setLicenseKey] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleActivate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        if (!licenseKey.trim()) { setError('Por favor, introduce una clave de licencia.'); setLoading(false); return; }

        const licensesRef = collection(db, "licenses");
        const q = query(licensesRef, where("key", "==", licenseKey.trim()), limit(1));

        try {
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) { setError('Clave de licencia no válida o no encontrada.'); setLoading(false); return; }

            const licenseDoc = querySnapshot.docs[0];
            const licenseData = licenseDoc.data();

            if (licenseData.assignedTo && licenseData.assignedTo !== user.uid) { setError('Esta licencia ya ha sido asignada a otro usuario.'); setLoading(false); return; }
            if (licenseData.status !== 'active') { setError('Esta licencia no está activa. Contacta al administrador.'); setLoading(false); return; }
            if (licenseData.expiresAt.toDate() < new Date()) { setError('Esta licencia ha expirado. Contacta al administrador.'); setLoading(false); return; }

            await updateDoc(doc(db, 'licenses', licenseDoc.id), { assignedTo: user.uid });
            showNotification('¡Panel activado con éxito!', 'success');
            onActivationSuccess();

        } catch (err) {
            console.error(err);
            setError('Ocurrió un error al verificar la licencia.');
            setLoading(false);
        }
    };

    return (
        <div className={`flex flex-col items-center justify-center min-h-screen ${t.bg} ${t.text} p-4`} style={{ fontFamily: fonts[font] }}>
            <div className={`w-full max-w-md p-8 text-center ${t.bgMuted} backdrop-blur-sm rounded-lg shadow-2xl ${t.shadow} border ${t.border}`}>
                <h1 className={`text-3xl font-bold ${t.primary} mb-2`}>Activar Panel</h1><p className={`${t.textMuted} mb-6`}>Introduce la clave de licencia que te proporcionó el administrador para activar tu panel de "Gestión de Cuentas".</p>
                <form onSubmit={handleActivate} className="space-y-4"><input type="text" value={licenseKey} onChange={(e) => setLicenseKey(e.target.value)} required className={`w-full px-4 py-3 ${t.bgInput} border ${t.border} rounded-md focus:ring-2 ${t.borderFocus} text-center font-mono tracking-widest`} placeholder="GCS1-LIC-XXXX-XXXX" />{error && <p className="text-red-400 text-sm text-center">{error}</p>}<button type="submit" disabled={loading} className={`w-full py-3 font-bold rounded-md disabled:opacity-50 ${t.buttonPrimary}`}>{loading ? 'Activando...' : 'Activar Panel'}</button></form>
            </div>
        </div>
    );
}

// --- VISTA DE COMPRAS (CON TODAS LAS CORRECCIONES Y SIN ABREVIATURAS) ---
function PurchasesView({ user, services, providers, sales, clients, messageTemplates, businessProfile, inventory }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, id: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [renewalModal, setRenewalModal] = useState({ isOpen: false, purchase: null, duration: 30 });
    const [passwordModal, setPasswordModal] = useState({ isOpen: false, purchase: null, newPassword: '' });
    const [manualNotifyModal, setManualNotifyModal] = useState({ isOpen: false, clients: [], newPassword: '', purchase: null });
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 9;
    const userRole = user.role || 'owner';
    const teamId = user.teamId || user.uid;

    const [purchaseType, setPurchaseType] = useState('single'); 
    const [isFullAccountSale, setIsFullAccountSale] = useState(false);
    const [profileData, setProfileData] = useState([{ profile: '', pin: '' }]);
 
        const getLocalDateString = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const initialFormState = { serviceId: '', providerId: '', purchaseDate: getLocalDateString(new Date()), duration: 30, price: 0, isRenewable: true, notes: '', email: '', password: '', profile: '', pin: '', salePriceFull: 0, salePriceProfile: 0, matrixEmail: '', className: '', inviteLink: '', allowedDevices: '', maxAssignments: 1, user: '', url: '', batchData: '' };
    const [formData, setFormData] = useState(initialFormState);
    const selectedService = services.find(s => s.id === formData.serviceId);

    useEffect(() => {
        if (!isFullAccountSale && selectedService?.type === 'Streaming' && purchaseType === 'single') {
            const count = parseInt(formData.maxAssignments, 10) || 1;
            if (count > 0 && count !== profileData.length) {
                const newProfiles = Array.from({ length: count }, (_, i) => profileData[i] || { profile: '', pin: '' });
                setProfileData(newProfiles);
            }
        }
    }, [formData.maxAssignments, isFullAccountSale, selectedService, purchaseType]);

    useEffect(() => {
        const q = query(collection(db, 'users', user.uid, 'purchases'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPurchases(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (err) => {
            console.error("Error fetching purchases:", err);
            showNotification('Error al cargar las compras.', 'error');
            setLoading(false);
        });
        return () => unsubscribe();
    }, [user.uid, db, showNotification]);

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newFormData = { ...formData, [name]: type === 'checkbox' ? checked : value };

        if (name === 'serviceId' && value) {
            const serviceTemplate = services.find(s => s.id === value);
            if (serviceTemplate) {
                newFormData.providerId = serviceTemplate.defaultProviderId || '';
                newFormData.duration = serviceTemplate.defaultPurchaseDuration || 30;
                newFormData.price = serviceTemplate.defaultPurchasePrice || 0;
                newFormData.instructions = serviceTemplate.defaultInstructions || '';
            }
        }
        setFormData(newFormData);
    };
    
    const handleNumericChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = name.toLowerCase().includes('price') 
            ? value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1')
            : value.replace(/\D/g, '');
        setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    };

    const handleProfileDataChange = (index, field, value) => {
        const newProfileData = [...profileData];
        newProfileData[index][field] = value;
        setProfileData(newProfileData);
    };
    
    const openModal = (item = null) => {
        setCurrentItem(item);
        setPurchaseType('single');
        if (item) {
            setFormData({ ...initialFormState, ...item });
        } else {
            setFormData(initialFormState);
        }
        setIsModalOpen(true);
    };
    
    const handleSavePurchase = async (e) => {
        e.preventDefault();

        if(currentItem) {
            const purchaseRef = doc(db, 'users', user.uid, 'purchases', currentItem.id);
            try {
                const renewalDate = new Date(formData.purchaseDate);
                renewalDate.setDate(renewalDate.getDate() + parseInt(formData.duration));
                const dataToUpdate = {...formData, renewalDate: renewalDate.toISOString().split('T')[0]};
                delete dataToUpdate.batchData;
                await updateDoc(purchaseRef, dataToUpdate);
                showNotification('¡Compra actualizada!', 'success');
                setIsModalOpen(false);
                setCurrentItem(null);
            } catch(error) {
                console.error("Error updating purchase:", error);
                showNotification('Error al actualizar la compra.', 'error');
            }
            return;
        }

        if (!selectedService) {
            showNotification('Por favor, selecciona un servicio válido.', 'error');
            return;
        }

        const purchaseDate = new Date(formData.purchaseDate);
        const renewalDate = new Date(purchaseDate);
        renewalDate.setDate(renewalDate.getDate() + parseInt(formData.duration));
        const batch = writeBatch(db);
        const purchaseRef = doc(collection(db, 'users', user.uid, 'purchases'));

        try {
            if (purchaseType === 'batch') {
                const lines = formData.batchData.trim().split('\n').filter(line => line.trim() !== '');
                if (lines.length === 0) throw new Error('Debes agregar al menos una cuenta en el área de texto.');
                
                const purchaseData = { serviceId: formData.serviceId, serviceName: selectedService.name, providerId: formData.providerId, purchaseDate: formData.purchaseDate, renewalDate: renewalDate.toISOString().split('T')[0], duration: parseInt(formData.duration), price: parseFloat(formData.price), isRenewable: formData.isRenewable, notes: formData.notes, accountCount: lines.length, createdAt: serverTimestamp() };
                batch.set(purchaseRef, purchaseData);

                for (const line of lines) {
                    if (selectedService.type === 'Streaming') {
                        const [accountPart, ...profilesPart] = line.split('|').map(p => p.trim());
                        const [email, password] = accountPart.split(',').map(v => v.trim());

                        if (profilesPart.length > 0) {
                            for (const profileStr of profilesPart) {
                                const [profile, pin] = profileStr.split(',').map(v => v.trim());
                                if (!email || !password || !profile || !pin) continue;
                                const inventoryRef = doc(collection(db, 'users', user.uid, 'services', formData.serviceId, 'inventory'));
                                batch.set(inventoryRef, { email, password, profile, pin, assignments: 0, maxAssignments: 1, instructions: formData.instructions || '', purchaseId: purchaseRef.id, createdAt: serverTimestamp() });
                            }
                        } else {
                            if (!email || !password) continue;
                            const inventoryRef = doc(collection(db, 'users', user.uid, 'services', formData.serviceId, 'inventory'));
                            batch.set(inventoryRef, { email, password, assignments: 0, maxAssignments: 1, instructions: formData.instructions || '', purchaseId: purchaseRef.id, createdAt: serverTimestamp() });
                        }
                    } else {
                        const values = line.split(',').map(v => v.trim());
                        const inventoryData = { assignments: 0, instructions: formData.instructions || '', purchaseId: purchaseRef.id, createdAt: serverTimestamp() };
                        const inventoryFields = serviceTypeFields[selectedService.type] || [];
                        
                        inventoryFields.forEach((field, index) => {
                            if (values[index]) inventoryData[field.name] = values[index];
                        });
                        
                        const maxAssignmentsIndex = inventoryFields.length;
                        inventoryData.maxAssignments = (values[maxAssignmentsIndex] && parseInt(values[maxAssignmentsIndex], 10)) || 1;

                        const inventoryRef = doc(collection(db, 'users', user.uid, 'services', formData.serviceId, 'inventory'));
                        batch.set(inventoryRef, inventoryData);
                    }
                }
                await batch.commit();
                showNotification(`¡Lote de ${lines.length} cuentas registrado con éxito!`, 'success');
            } else { // purchaseType === 'single'
                const purchaseData = { ...formData, serviceName: selectedService.name, renewalDate: renewalDate.toISOString().split('T')[0], duration: parseInt(formData.duration), price: parseFloat(formData.price), createdAt: serverTimestamp() };
                delete purchaseData.batchData;
                batch.set(purchaseRef, purchaseData);

                if (selectedService.type === 'Streaming' && !isFullAccountSale) {
                    for (const profileItem of profileData) {
                        if (!profileItem.profile || !profileItem.pin) { throw new Error('Todos los campos de perfil y PIN son obligatorios.'); }
                        const inventoryRef = doc(collection(db, 'users', user.uid, 'services', formData.serviceId, 'inventory'));
                        const inventoryData = { email: formData.email, password: formData.password, profile: profileItem.profile, pin: profileItem.pin, assignments: 0, maxAssignments: 1, instructions: formData.instructions || '', purchaseId: purchaseRef.id, createdAt: serverTimestamp(), salePriceProfile: parseFloat(formData.salePriceProfile) || 0, isFullAccountSale: false };
                        batch.set(inventoryRef, inventoryData);
                    }
                } else {
                    const inventoryRef = doc(collection(db, 'users', user.uid, 'services', formData.serviceId, 'inventory'));
                    const specificFields = serviceTypeFields[selectedService.type] || [];
                    const inventoryData = { assignments: 0, maxAssignments: parseInt(formData.maxAssignments) || 1, instructions: formData.instructions || '', purchaseId: purchaseRef.id, createdAt: serverTimestamp(), salePriceFull: parseFloat(formData.salePriceFull) || 0, isFullAccountSale: true };
                    specificFields.forEach(field => { if (formData[field.name]) { inventoryData[field.name] = formData[field.name]; } });
                    batch.set(inventoryRef, inventoryData);
                }
                await batch.commit();
                showNotification('¡Compra registrada y añadida al inventario!', 'success');
            }
            setIsModalOpen(false);
            setFormData(initialFormState);
            setProfileData([{ profile: '', pin: '' }]);
            setIsFullAccountSale(false);
        } catch (error) {
            console.error("Error saving purchase:", error);
            showNotification(error.message || 'Error al guardar la compra.', 'error');
        }
    };
    
    const handleDeleteRequest = (id) => { setConfirmDeleteModal({ isOpen: true, id }); };

    const handleConfirmDelete = async () => {
        if (confirmDeleteModal.id) {
            try {
                await deleteDoc(doc(db, 'users', user.uid, 'purchases', confirmDeleteModal.id));
                showNotification('Compra eliminada. El inventario asociado no se ha modificado.', 'info');
            } catch (error) {
                console.error("Error deleting purchase:", error);
                showNotification('Error al eliminar la compra.', 'error');
            }
        }
        setConfirmDeleteModal({ isOpen: false, id: null });
    };
    
    const handleOpenRenewModal = (purchase) => {
        setRenewalModal({ isOpen: true, purchase, duration: 30 });
    };

    const handleConfirmRenewal = async () => {
        const { purchase, duration } = renewalModal;
        if (!purchase || !duration) return;
        const purchaseRef = doc(db, 'users', user.uid, 'purchases', purchase.id);
        const newRenewalDate = new Date(purchase.renewalDate);
        newRenewalDate.setDate(newRenewalDate.getDate() + parseInt(duration));
        try {
            await updateDoc(purchaseRef, {
                renewalDate: newRenewalDate.toISOString().split('T')[0],
                duration: parseInt(duration),
            });
            showNotification('Compra renovada con éxito.', 'success');
            setRenewalModal({ isOpen: false, purchase: null, duration: 30 });
        } catch (error) {
            console.error("Error renewing purchase:", error);
            showNotification('Error al renovar la compra.', 'error');
        }
    };

    const handleConfirmPasswordChange = async () => {
        const { purchase, newPassword } = passwordModal;
        if (!purchase || !newPassword) {
            showNotification('La nueva contraseña no puede estar vacía.', 'error');
            return;
        }
        try {
            const serviceInventory = inventory[purchase.serviceId] || [];
            const affectedInventoryItems = serviceInventory.filter(item => item.purchaseId === purchase.id);
            const affectedInventoryIds = affectedInventoryItems.map(item => item.id);
            const affectedClientIds = new Set();
            sales.forEach(sale => {
                const saleInventoryIds = sale.inventoryIds || (sale.inventoryId ? [sale.inventoryId] : []);
                const hasMatch = saleInventoryIds.some(id => affectedInventoryIds.includes(id));
                if (hasMatch && sale.expiresAt?.toDate() > new Date() && sale.status !== 'suspended') {
                    affectedClientIds.add(sale.clientId);
                }
            });
            const clientsToNotify = clients.filter(client => affectedClientIds.has(client.id));
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const updatePassword = httpsCallable(functions, 'updatePurchasePassword'); 
            const result = await updatePassword({
                purchaseId: purchase.id,
                serviceId: purchase.serviceId,
                newPassword: newPassword
            });
            showNotification(result.data.message, 'success');
            if (clientsToNotify.length > 0) {
                setManualNotifyModal({ 
                    isOpen: true, 
                    clients: clientsToNotify, 
                    newPassword: newPassword, 
                    purchase: purchase 
                });
            }
            setPasswordModal({ isOpen: false, purchase: null, newPassword: '' });
        } catch (error) {
            console.error("Error changing password:", error);
            showNotification(error.message, 'error');
        }
    };
    
    const generatePasswordChangeLink = (client, purchase, newPassword) => {
        const service = (services || []).find(srv => srv.id === purchase.serviceId);
        if (!client || !service) return '#';
        const template = messageTemplates['password_change'] || "¡Hola, {cliente}! 👋\n\nTe informamos que los datos de tu cuenta de {servicio} han sido actualizados. Por favor, utiliza las nuevas credenciales:\n\n{datos_cuenta}\n\n¡Gracias por tu comprensión!\n- El equipo de {mi_negocio}\n{mi_contacto}";
        const accountData = `Email: ${purchase.email}\nContraseña: ${newPassword}`;
        const businessContact = [businessProfile.whatsapp, businessProfile.telegram, businessProfile.website].filter(Boolean).join(' | ');
        const message = template
            .replace(/{cliente}/g, client.name)
            .replace(/{servicio}/g, service.name)
            .replace(/{datos_cuenta}/g, accountData)
            .replace(/{mi_negocio}/g, businessProfile.name || 'nuestro equipo')
            .replace(/{mi_contacto}/g, businessContact);
        if (client.contact?.includes('@')) { return `https://t.me/${client.contact.replace('@', '')}?text=${encodeURIComponent(message)}`; }
        return `https://wa.me/${client.contact}?text=${encodeURIComponent(message)}`;
    };

    const getIdentifier = (item) => item?.email || item?.user || (item.accountCount ? `${item.accountCount} cuentas` : item.id.slice(-6));
    
    // --- INICIO DE LA CORRECCIÓN: Función getBatchFormat restaurada ---
    const getBatchFormat = () => {
        if (!selectedService) return 'Selecciona un servicio para ver el formato.';
        const fields = serviceTypeFields[selectedService.type] || [];
        if (selectedService.type === 'Streaming') {
            return 'Formato: email,contraseña|perfil1,pin1|perfil2,pin2...';
        }
        return `Formato: ${fields.map(f => f.name).join(', ')},veces_a_vender`;
    };
    // --- FIN DE LA CORRECCIÓN ---

    const filteredPurchases = purchases.filter(purchase => {
        const search = searchTerm.toLowerCase();
        const serviceName = purchase.serviceName?.toLowerCase() || '';
        const providerName = (providers.find(p => p.id === purchase.providerId)?.name || purchase.providerId)?.toLowerCase();
        const accountIdentifier = (purchase.email || '').toLowerCase();
        return serviceName.includes(search) || providerName.includes(search) || accountIdentifier.includes(search);
    });

    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = filteredPurchases.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredPurchases.length / ITEMS_PER_PAGE);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <>
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h2 className={`text-2xl font-bold ${t.primary}`}>Control de Compras</h2>
                    <div className="relative w-full sm:w-auto">
                        <input type="text" placeholder="Buscar por servicio, cuenta..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full sm:w-64 pl-10 pr-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SearchIcon /></div>
                    </div>
                    {(userRole === 'owner' || userRole === 'asesor_ventas') && (
                        <button onClick={() => openModal()} className={`flex items-center px-4 py-2 font-bold rounded-md w-full sm:w-auto justify-center ${t.buttonPrimary}`}><PlusCircleIcon /> <span className="ml-2">Añadir Compra</span></button>
                    )}
                </div>

                {loading ? ( <div className="text-center p-4">Cargando compras...</div> ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {currentItems.map(purchase => {
                                const isExpired = new Date(purchase.renewalDate) < new Date();
                                const cardClass = isExpired ? 'border-red-500/50' : t.border;
                                const providerName = purchase.providerId === 'propia' ? 'Propiedad Propia' : providers.find(p => p.id === purchase.providerId)?.name || 'N/A';

                                return (
                                    <div key={purchase.id} className={`${t.bg} p-4 rounded-lg border ${cardClass} flex flex-col justify-between shadow-lg`}>
                                        <div className="space-y-2 mb-4">
                                            <div>
                                                <p className={`text-xs font-bold ${t.textMuted}`}>Servicio</p>
                                                <p className={`font-semibold text-lg ${t.secondary}`}>{purchase.serviceName}</p>
                                            </div>
                                            <div>
                                                <p className={`text-xs font-bold ${t.textMuted}`}>Cuenta(s)</p>
                                                <p className={`font-mono text-sm ${t.textMuted}`}>{getIdentifier(purchase)}</p>
                                            </div>
                                            <div>
                                                <p className={`text-xs font-bold ${t.textMuted}`}>Proveedor</p>
                                                <p className={`${t.textMuted}`}>{providerName}</p>
                                            </div>
                                            <div className="flex justify-between items-baseline pt-2">
                                                <div>
                                                    <p className={`text-xs font-bold ${t.textMuted}`}>Fecha Compra</p>
                                                    <p className={t.textMuted}>{purchase.purchaseDate}</p>
                                                </div>
                                                <div>
                                                    <p className={`text-xs font-bold ${isExpired ? 'text-red-400' : 'text-green-400'}`}>Renovación</p>
                                                    <p className={`${isExpired ? 'text-red-400 font-bold' : 'text-green-400'}`}>{purchase.renewalDate}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`border-t ${t.border} pt-3 mt-auto flex items-center justify-end gap-2`}>
                                            {userRole === 'owner' && (
                                                <>
                                                    <button onClick={() => setPasswordModal({ isOpen: true, purchase: purchase, newPassword: '' })} title="Cambiar Contraseña" className="p-2 bg-yellow-500/20 text-yellow-400 rounded-md hover:bg-yellow-500/40"><KeyIcon /></button>
                                                    <button onClick={() => openModal(purchase)} title="Editar Compra" className={`p-2 rounded-md ${t.buttonSecondary}`}><EditIcon /></button>
                                                    <button onClick={() => handleDeleteRequest(purchase.id)} title="Eliminar Compra" className="p-2 bg-red-500/20 text-red-400 rounded-md hover:bg-red-500/40"><TrashIcon /></button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        {totalPages > 1 && (
                           <div className="mt-8 flex justify-center items-center space-x-2">
                                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} className={`px-4 py-2 rounded-md ${t.buttonSecondary} disabled:opacity-50`}>Anterior</button>
                                {[...Array(totalPages).keys()].map(number => (
                                    <button key={number + 1} onClick={() => paginate(number + 1)} className={`px-4 py-2 rounded-md ${currentPage === number + 1 ? t.buttonPrimary : t.buttonSecondary}`}>{number + 1}</button>
                                ))}
                                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Siguiente</button>
                           </div>
                        )}
                    </>
                )}
            </div>
            
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={currentItem ? 'Editar Compra' : 'Registrar Nueva Compra'} maxWidth="max-w-3xl">
                <form onSubmit={handleSavePurchase} className="space-y-4">
                    {!currentItem && (
                        <div className="grid grid-cols-2 gap-4">
                            <label className={`flex items-center gap-2 p-4 border ${t.border} rounded-md cursor-pointer has-[:checked]:bg-cyan-500/20`}>
                                <input type="radio" name="purchaseType" value="single" checked={purchaseType === 'single'} onChange={() => setPurchaseType('single')} className="accent-pink-500" /> Cuenta Individual
                            </label>
                            <label className={`flex items-center gap-2 p-4 border ${t.border} rounded-md cursor-pointer has-[:checked]:bg-cyan-500/20`}>
                                <input type="radio" name="purchaseType" value="batch" checked={purchaseType === 'batch'} onChange={() => setPurchaseType('batch')} className="accent-pink-500" /> Lote de Cuentas
                            </label>
                        </div>
                    )}
                    
                    <div>
                        <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Servicio</label>
                        <select name="serviceId" value={formData.serviceId} onChange={handleFormChange} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                            <option value="">-- Seleccionar Servicio --</option>
                            {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>

                    {selectedService && !currentItem && (
                        <div className="border-t border-gray-700 my-4 pt-4">
                            <h4 className={`text-lg font-bold ${t.primary} mb-2`}>Datos de la Cuenta</h4>
                            {purchaseType === 'single' ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {(serviceTypeFields[selectedService.type] || []).map(field => {
                                            if (field.name === 'profile' || field.name === 'pin') return null;
                                            return (<div key={field.name}>
                                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>{field.label}</label>
                                                <input type={field.type || 'text'} name={field.name} value={formData[field.name] || ''} onChange={handleFormChange} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                            </div>)
                                        })}
                                    </div>
                                    {selectedService.type === 'Streaming' && (
                                        <div className="border-t border-gray-700 my-4 pt-4">
                                            <div className="flex items-center gap-4 mb-4">
                                                <input type="checkbox" id="fullAccountSale" name="isFullAccountSale" checked={isFullAccountSale} onChange={(e) => setIsFullAccountSale(e.target.checked)} className="accent-pink-500 h-4 w-4" />
                                                <label htmlFor="fullAccountSale" className={`text-sm font-bold ${t.textMuted}`}>Venta de Cuenta Completa</label>
                                            </div>
                                            {isFullAccountSale ? (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div><label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Perfil</label><input type="text" name="profile" value={formData.profile} onChange={handleFormChange} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} /></div>
                                                    <div><label className={`block text-sm font-bold ${t.textMuted} mb-2`}>PIN</label><input type="text" name="pin" value={formData.pin} onChange={handleFormChange} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} /></div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Veces a vender (Perfiles)</label>
                                                    <input type="text" name="maxAssignments" value={formData.maxAssignments || ''} onChange={handleNumericChange} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md mb-4`} />
                                                    <div className="space-y-3">{profileData.map((p, index) => (<div key={index} className="grid grid-cols-2 gap-2 p-2 border border-gray-700 rounded-md">
                                                        <input type="text" placeholder={`Perfil ${index + 1}`} value={p.profile} onChange={(e) => handleProfileDataChange(index, 'profile', e.target.value)} required className={`w-full px-3 py-1 ${t.bgInput} border ${t.border} rounded-md`} />
                                                        <input type="text" placeholder={`PIN ${index + 1}`} value={p.pin} onChange={(e) => handleProfileDataChange(index, 'pin', e.target.value)} required className={`w-full px-3 py-1 ${t.bgInput} border ${t.border} rounded-md`} />
                                                    </div>))}</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {(selectedService.type === 'Canva' || selectedService.type === 'Capcut' || selectedService.type === 'IPTV') && (
                                        <div className="border-t border-gray-700 my-4 pt-4">
                                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Veces que se puede vender esta cuenta</label>
                                            <input type="text" name="maxAssignments" value={formData.maxAssignments} onChange={handleNumericChange} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <textarea name="batchData" value={formData.batchData} onChange={handleFormChange} rows="6" placeholder={`Pega las cuentas aquí. Una por línea.`} className={`w-full p-4 font-mono text-sm ${t.bgInput} border ${t.border} rounded-md`} />
                                    <p className="text-xs text-gray-500 mt-1">{getBatchFormat()}</p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="border-t border-gray-700 my-4 pt-4">
                        <h4 className={`text-lg font-bold ${t.primary} mb-2`}>Detalles de la Compra ({currentItem ? 'Individual' : 'para el lote completo'})</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Proveedor</label>
                                <select name="providerId" value={formData.providerId} onChange={handleFormChange} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                                    <option value="">-- Seleccionar --</option>
                                    <option value="propia">Propiedad Propia</option>
                                    {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Fecha de Compra</label>
                                <input type="date" name="purchaseDate" value={formData.purchaseDate} onChange={handleFormChange} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Duración (días)</label>
                                <input type="text" name="duration" value={formData.duration} onChange={handleNumericChange} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                            <div>
                                <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Precio Total de Compra</label>
                                <input type="text" name="price" value={formData.price} onChange={handleNumericChange} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                            </div>
                            <div className="flex items-center pt-6">
                                <input type="checkbox" name="isRenewable" checked={formData.isRenewable} onChange={handleFormChange} className="accent-pink-500 h-4 w-4" />
                                <label className={`ml-2 text-sm font-bold ${t.textMuted}`}>Es Renovable</label>
                            </div>
                        </div>
                        <div>
                            <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Instrucciones para Cliente (para todas las cuentas)</label>
                            <textarea name="instructions" value={formData.instructions || ''} onChange={handleFormChange} className={`w-full h-24 px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={() => {setIsModalOpen(false); setCurrentItem(null);}} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                        <button type="submit" className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar Compra</button>
                    </div>
                </form>
            </Modal>
            
            <Modal isOpen={confirmDeleteModal.isOpen} onClose={() => setConfirmDeleteModal({ isOpen: false, id: null })} title="Confirmar Eliminación" maxWidth="max-w-md">
                 <p className={t.textMuted}>¿Estás seguro de que quieres eliminar esta compra?</p>
                 <div className="flex justify-end gap-4 pt-6">
                     <button type="button" onClick={() => setConfirmDeleteModal({ isOpen: false, id: null })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                     <button onClick={handleConfirmDelete} className="px-4 py-2 bg-red-600 text-white font-bold rounded-md hover:bg-red-500">Eliminar</button>
                 </div>
            </Modal>
            
            <Modal isOpen={renewalModal.isOpen} onClose={() => setRenewalModal({ isOpen: false, purchase: null, duration: 30 })} title="Renovar Compra" maxWidth="max-w-md">
                 <p className="mb-2">Servicio: <span className={t.primary}>{renewalModal.purchase?.serviceName}</span></p>
                 <div className="mb-4">
                     <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Nueva Duración (días)</label>
                     <input type="text" value={renewalModal.duration} onChange={(e) => setRenewalModal(prev => ({ ...prev, duration: e.target.value.replace(/\D/g, '') }))} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                 </div>
                 <div className="flex justify-end gap-4">
                     <button type="button" onClick={() => setRenewalModal({ isOpen: false, purchase: null, duration: 30 })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                     <button onClick={handleConfirmRenewal} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Confirmar Renovación</button>
                 </div>
            </Modal>

            <Modal isOpen={passwordModal.isOpen} onClose={() => setPasswordModal({ isOpen: false, purchase: null, newPassword: '' })} title="Cambiar Contraseña" maxWidth="max-w-md">
                <p className="mb-2">Cuenta: <span className={t.primary}>{passwordModal.purchase?.email}</span></p>
                <div className="mb-4">
                    <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Nueva Contraseña</label>
                    <input type="text" value={passwordModal.newPassword} onChange={(e) => setPasswordModal(prev => ({ ...prev, newPassword: e.target.value }))} className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                </div>
                <div className="flex justify-end gap-4">
                    <button type="button" onClick={() => setPasswordModal({ isOpen: false, purchase: null, newPassword: '' })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                    <button onClick={handleConfirmPasswordChange} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>Guardar y Continuar</button>
                </div>
            </Modal>
            
            <Modal isOpen={manualNotifyModal.isOpen} onClose={() => setManualNotifyModal({ isOpen: false, clients: [], newPassword: '', purchase: null })} title="Notificar Cambio Manualmente" maxWidth="max-w-lg">
                 <p className="mb-4 text-sm text-gray-400">La contraseña se ha actualizado correctamente. Haz clic en los botones para enviar la nueva información a cada cliente afectado.</p>
                 <div className="max-h-64 overflow-y-auto space-y-2 border-t border-b border-gray-700 py-4">
                     {manualNotifyModal.clients.length > 0 ? (
                         manualNotifyModal.clients.map(client => (
                             <div key={client.id} className={`${t.bgMuted} p-3 rounded-md flex justify-between items-center`}>
                                 <span className="font-semibold">{client.name}</span>
                                 <a 
                                     href={generatePasswordChangeLink(client, manualNotifyModal.purchase, manualNotifyModal.newPassword)} 
                                     target="_blank" 
                                     rel="noopener noreferrer" 
                                     className="flex items-center gap-2 px-3 py-1 text-sm rounded-md bg-green-500 text-white font-bold hover:bg-green-600"
                                 >
                                     <WhatsAppIcon /> Notificar
                                 </a>
                             </div>
                         ))
                     ) : (
                         <p className="text-center text-gray-500">No se encontraron clientes activos para notificar.</p>
                     )}
                 </div>
                 <div className="flex justify-end mt-6">
                     <button onClick={() => setManualNotifyModal({ isOpen: false, clients: [], newPassword: '', purchase: null })} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cerrar</button>
                 </div>
            </Modal>
        </>
    );
}

// --- VISTA DE ESTADÍSTICAS (CON LÓGICA DE FECHAS "HOY" DEFINITIVA Y ROBUSTA) ---
function StatsView({ purchases, sales, allServices, clients, inventory }) {
    const { theme, themes } = useTheme();
    const t = themes[theme] || themes.netflix_dark;

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    // --- LÓGICA DE CÁLCULO DE FECHAS Y MÉTRICAS (CORREGIDA) ---
    const getDocDate = (doc) => {
        // Prioriza la fecha manual del formulario
        const dateString = doc.purchaseDate || doc.saleDate;
        if (dateString) {
            const parts = dateString.split('-').map(Number);
            // new Date(year, month-1, day) crea la fecha en la zona horaria local del navegador
            if (parts.length === 3) {
                return new Date(parts[0], parts[1] - 1, parts[2]);
            }
        }
        // Si no hay fecha manual, usa el timestamp de creación
        if (doc.createdAt?.toDate) {
            return doc.createdAt.toDate();
        }
        return null;
    };

    const today = new Date();
    const startOfWeek = new Date();
    startOfWeek.setDate(today.getDate() - 6);
    startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    // **CORRECCIÓN DEFINITIVA:** Filtro para "Hoy" que compara día, mes y año exactos.
    const salesToday = (sales || []).filter(s => { 
        const d = getDocDate(s); 
        return d && d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); 
    });
    const salesThisWeek = (sales || []).filter(s => { const d = getDocDate(s); return d && d >= startOfWeek; });
    const salesThisMonth = (sales || []).filter(s => { const d = getDocDate(s); return d && d >= startOfMonth; });
    
    const purchasesToday = (purchases || []).filter(p => { 
        const d = getDocDate(p); 
        return d && d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); 
    });
    const purchasesThisWeek = (purchases || []).filter(p => { const d = getDocDate(p); return d && d >= startOfWeek; });
    const purchasesThisMonth = (purchases || []).filter(p => { const d = getDocDate(p); return d && d >= startOfMonth; });
    
    const newClientsToday = (clients || []).filter(c => { 
        const d = getDocDate(c); 
        return d && d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); 
    });
    const newClientsThisWeek = (clients || []).filter(c => { const d = getDocDate(c); return d && d >= startOfWeek; });
    const newClientsThisMonth = (clients || []).filter(c => { const d = getDocDate(c); return d && d >= startOfMonth; });
    
    // --- CORRECCIÓN CLAVE: Se suma directamente salePrice, que ya es el total de la venta ---
    const revenueToday = salesToday.reduce((acc, s) => acc + (Number(s.salePrice) || 0), 0);
    const investmentToday = purchasesToday.reduce((acc, p) => acc + (Number(p.price) || 0), 0);
    const profitToday = revenueToday - investmentToday;

    const revenueThisWeek = salesThisWeek.reduce((acc, s) => acc + (Number(s.salePrice) || 0), 0);
    const investmentThisWeek = purchasesThisWeek.reduce((acc, p) => acc + (Number(p.price) || 0), 0);
    const profitThisWeek = revenueThisWeek - investmentThisWeek;
    
    const revenueThisMonth = salesThisMonth.reduce((acc, s) => acc + (Number(s.salePrice) || 0), 0);
    const investmentThisMonth = purchasesThisMonth.reduce((acc, p) => acc + (Number(p.price) || 0), 0);
    const profitThisMonth = revenueThisMonth - investmentThisMonth;

    const totalRevenue = (sales || []).reduce((acc, s) => acc + (Number(s.salePrice) || 0), 0);
    const totalInvestment = (purchases || []).reduce((acc, p) => acc + (Number(p.price) || 0), 0);
    const estimatedProfit = totalRevenue - totalInvestment;

    const activeSubscriptions = (sales || []).filter(s => s.expiresAt?.toDate() > new Date()).length;
    const topService = (() => { if (!sales || sales.length === 0) return 'N/A'; const counts = sales.reduce((acc, sale) => { acc[sale.serviceId] = (acc[sale.serviceId] || 0) + (Number(sale.quantity) || 1); return acc; }, {}); if (Object.keys(counts).length === 0) return 'N/A'; const topId = Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b); return (allServices || []).find(s => s.id === topId)?.name || 'N/A'; })();
    
    const StatCell = ({ value, isCurrency = false, colorClass = t.secondary }) => (<div className={`p-3 rounded-lg text-center ${t.bgMuted}`}><p className={`text-xl font-bold ${colorClass}`}>{isCurrency ? formatCurrency(value) : value}</p></div>);
    const HeaderCell = ({ title }) => (<div className="p-3 text-center"><p className={`text-sm font-bold ${t.primary}`}>{title}</p></div>);
    const MetricTitleCell = ({ title, icon }) => (<div className={`p-3 rounded-lg flex items-center gap-3 ${t.bgMuted}`}><icon.type className="h-5 w-5 opacity-70" /> <p className="font-bold">{title}</p></div>);
    const GeneralStatCard = ({ title, value, icon }) => (<div className={`${t.bgMuted} p-4 rounded-lg border ${t.border}`}><div className="flex items-center gap-3"><icon.type className={`h-6 w-6 ${t.primary}`} /> <p className={`text-sm font-bold ${t.textMuted}`}>{title}</p></div><p className={`text-2xl font-bold mt-2 ${t.secondary}`}>{value}</p></div>);

    const StatRowMobile = ({ label, value, colorClass = t.secondary, isCurrency = false }) => (
        <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
            <span className={t.textMuted}>{label}</span>
            <span className={`font-bold ${colorClass}`}>{isCurrency ? formatCurrency(value) : value}</span>
        </div>
    );
    
    return (
        <div className="space-y-8">
            <div>
                <h3 className={`text-xl font-bold ${t.primary} mb-4`}>Comparativa por Período</h3>

                {/* --- VISTA PARA ESCRITORIO Y TABLET (md en adelante) --- */}
                <div className="hidden md:grid md:grid-cols-4 gap-4 items-center">
                    <div className="hidden md:block"></div>
                    <HeaderCell title="Hoy" />
                    <HeaderCell title="Últimos 7 Días" />
                    <HeaderCell title="Este Mes" />
                    <MetricTitleCell title="Ganancia" icon={<TrendingUpIcon />} />
                    <StatCell value={profitToday} isCurrency={true} colorClass="text-green-400" />
                    <StatCell value={profitThisWeek} isCurrency={true} colorClass="text-green-400" />
                    <StatCell value={profitThisMonth} isCurrency={true} colorClass="text-green-400" />
                    <MetricTitleCell title="Ventas (Total)" icon={<DollarSignIcon />} />
                    <StatCell value={revenueToday} isCurrency={true} />
                    <StatCell value={revenueThisWeek} isCurrency={true} />
                    <StatCell value={revenueThisMonth} isCurrency={true} />
                    <MetricTitleCell title="Compras (Inversión)" icon={<ShoppingBagIcon />} />
                    <StatCell value={investmentToday} isCurrency={true} colorClass="text-red-400" />
                    <StatCell value={investmentThisWeek} isCurrency={true} colorClass="text-red-400" />
                    <StatCell value={investmentThisMonth} isCurrency={true} colorClass="text-red-400" />
                    <MetricTitleCell title="Nuevos Clientes" icon={<UserIcon />} />
                    <StatCell value={newClientsToday.length} />
                    <StatCell value={newClientsThisWeek.length} />
                    <StatCell value={newClientsThisMonth.length} />
                </div>

                {/* --- VISTA PARA MÓVIL (se oculta en md en adelante) --- */}
                <div className="grid grid-cols-1 gap-6 md:hidden">
                    <div className={`${t.bgMuted} p-4 rounded-lg border ${t.border}`}>
                        <h4 className={`font-bold text-lg text-center mb-3 ${t.primary}`}>Hoy</h4>
                        <div className="space-y-1 text-sm">
                            <StatRowMobile label="Ganancia Neta" value={profitToday} isCurrency={true} colorClass="text-green-400" />
                            <StatRowMobile label="Ventas (Total)" value={revenueToday} isCurrency={true} />
                            <StatRowMobile label="Compras (Inversión)" value={investmentToday} isCurrency={true} colorClass="text-red-400"/>
                            <StatRowMobile label="Nuevos Clientes" value={newClientsToday.length} />
                        </div>
                    </div>
                    <div className={`${t.bgMuted} p-4 rounded-lg border ${t.border}`}>
                        <h4 className={`font-bold text-lg text-center mb-3 ${t.primary}`}>Últimos 7 Días</h4>
                        <div className="space-y-1 text-sm">
                            <StatRowMobile label="Ganancia Neta" value={profitThisWeek} isCurrency={true} colorClass="text-green-400" />
                            <StatRowMobile label="Ventas (Total)" value={revenueThisWeek} isCurrency={true} />
                            <StatRowMobile label="Compras (Inversión)" value={investmentThisWeek} isCurrency={true} colorClass="text-red-400"/>
                            <StatRowMobile label="Nuevos Clientes" value={newClientsThisWeek.length} />
                        </div>
                    </div>
                    <div className={`${t.bgMuted} p-4 rounded-lg border ${t.border}`}>
                        <h4 className={`font-bold text-lg text-center mb-3 ${t.primary}`}>Este Mes</h4>
                        <div className="space-y-1 text-sm">
                            <StatRowMobile label="Ganancia Neta" value={profitThisMonth} isCurrency={true} colorClass="text-green-400" />
                            <StatRowMobile label="Ventas (Total)" value={revenueThisMonth} isCurrency={true} />
                            <StatRowMobile label="Compras (Inversión)" value={investmentThisMonth} isCurrency={true} colorClass="text-red-400"/>
                            <StatRowMobile label="Nuevos Clientes" value={newClientsThisMonth.length} />
                        </div>
                    </div>
                </div>
            </div>
            
            <div>
                <h3 className={`text-xl font-bold ${t.primary} mb-4`}>Estadísticas Generales (Histórico)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <GeneralStatCard title="Ganancia Total" value={formatCurrency(estimatedProfit)} icon={<TrendingUpIcon />} />
                    <GeneralStatCard title="Suscripciones Activas" value={activeSubscriptions} icon={<UsersIcon />} />
                    <GeneralStatCard title="Servicio Más Vendido" value={topService} icon={<TrophyIcon />} />
                </div>
            </div>
        </div>
    );
}

// --- [NUEVO] VISTA DE MEJORA DE PLAN ---
function UpgradePlanView({ featureName }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    return (
        <div className={`${t.bgMuted} p-8 rounded-lg border ${t.border} text-center`}>
            <h2 className={`text-2xl font-bold ${t.primary} mb-4`}>Función Premium</h2>
            <p className={t.textMuted}>
                La función "{featureName}" no está incluida en tu plan actual.
            </p>
            <button className={`mt-6 px-6 py-3 font-bold rounded-md ${t.buttonPrimary}`}>
                Mejorar mi Plan
            </button>
        </div>
    );
}

// --- NUEVO COMPONENTE: VISTA PARA LA GESTIÓN DE SALDOS DE REVENDEDORES ---
function ResellerBalanceView({ user, teamMembers }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentReseller, setCurrentReseller] = useState(null);
    const [rechargeAmount, setRechargeAmount] = useState('');
    const [loading, setLoading] = useState(false);

    // Filtra solo a los miembros con el rol de 'revendedor'
    const resellers = teamMembers.filter(member => member.role === 'revendedor');

    const openModal = (reseller) => {
        setCurrentReseller(reseller);
        setRechargeAmount('');
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentReseller(null);
        setRechargeAmount('');
    };

    const handleRecharge = async (e) => {
        e.preventDefault();
        if (!currentReseller || !rechargeAmount || isNaN(rechargeAmount) || parseFloat(rechargeAmount) <= 0) {
            showNotification('Ingresa un monto válido.', 'error');
            return;
        }

        setLoading(true);

        try {
            const resellerRef = doc(db, 'users', currentReseller.id);
            await updateDoc(resellerRef, {
                resellerBalance: increment(parseFloat(rechargeAmount))
            });
            showNotification(`¡${rechargeAmount} USD recargados a ${currentReseller.email}!`, 'success');
            closeModal();
        } catch (error) {
            console.error("Error recargando el saldo:", error);
            showNotification('Error al recargar el saldo.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border} space-y-6`}>
                <div>
                    <h2 className={`text-2xl font-bold ${t.primary}`}>Saldos de Revendedores</h2>
                    <p className={t.textMuted}>Administra el saldo de tus distribuidores para que puedan comprar servicios.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {resellers.length > 0 ? (
                        resellers.map(reseller => (
                            <div key={reseller.id} className={`${t.bg} p-4 rounded-lg border ${t.border}`}>
                                <p className={`font-bold text-lg ${t.secondary}`}>{reseller.email}</p>
                                <p className={`text-sm ${t.textMuted}`}>Saldo Actual:</p>
                                <p className={`text-3xl font-bold ${t.primary}`}>${(reseller.resellerBalance || 0).toFixed(2)}</p>
                                <div className="mt-4">
                                    <button onClick={() => openModal(reseller)} className={`w-full flex items-center justify-center gap-2 px-4 py-2 font-bold rounded-md ${t.buttonPrimary}`}>
                                        <DollarSignIcon /> Recargar Saldo
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-span-full text-center p-8">
                            <p className={`text-lg ${t.textMuted}`}>No tienes revendedores en tu equipo.</p>
                            <p className="text-sm mt-2">Puedes añadir uno desde la vista de "Equipo".</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Recarga */}
            <Modal isOpen={isModalOpen} onClose={closeModal} title={`Recargar Saldo a ${currentReseller?.email}`} maxWidth="max-w-md">
                <form onSubmit={handleRecharge} className="space-y-4">
                    <div>
                        <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Monto a Recargar (USD)</label>
                        <input
                            type="number"
                            step="0.01"
                            value={rechargeAmount}
                            onChange={(e) => setRechargeAmount(e.target.value)}
                            required
                            className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}
                        />
                    </div>
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={closeModal} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                        <button type="submit" disabled={loading} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary} disabled:opacity-50`}>
                            {loading ? 'Recargando...' : 'Confirmar Recarga'}
                        </button>
                    </div>
                </form>
            </Modal>
        </>
    );
}

// --- [COMPONENTE MODIFICADO]: DASHBOARD PARA REVENDEDORES ---
function ResellerDashboardView({ user, sales, clients, services, setActiveView }) {
    const { theme, themes } = useTheme();
    const t = themes[theme] || themes.netflix_dark;
    
    // --- LÓGICA AÑADIDA PARA FILTRAR DATOS ---
    // Filtra las ventas que fueron creadas por este revendedor.
    const resellerSales = sales.filter(s => s.createdBy === user.uid);
    // Filtra los clientes que fueron creados por este revendedor.
    const resellerClients = clients.filter(c => c.createdBy === user.uid);

    const totalSales = resellerSales.length;
    const totalClients = resellerClients.length;
    // --- FIN DE LA LÓGICA AÑADIDA ---

    const SummaryTile = ({ title, value, icon, onClick }) => (
        <div onClick={onClick} className={`${t.bgMuted} p-6 rounded-lg border ${t.border} shadow-lg flex flex-col justify-center items-center text-center ${onClick ? 'cursor-pointer hover:bg-gray-700/50 transition-colors' : ''}`}>
            <div className={`p-3 rounded-full ${t.bg} mb-4`}>{React.cloneElement(icon, { className: `h-8 w-8 ${t.primary}` })}</div>
            <p className={`text-sm font-bold ${t.textMuted} mb-1`}>{title}</p>
            <p className={`text-4xl font-bold ${t.secondary}`}>{value}</p>
        </div>
    );
    
    return (
        <div className="space-y-6">
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                <h2 className={`text-2xl font-bold ${t.primary} mb-2`}>Panel de Revendedor</h2>
                <p className={t.textMuted}>Bienvenido, {user.email}. Aquí puedes gestionar tus ventas y clientes.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SummaryTile
                    title="Saldo Disponible"
                    value={`$${(user.resellerBalance || 0).toFixed(2)}`}
                    icon={<DollarSignIcon />}
                />
                <SummaryTile
                    title="Clientes Registrados"
                    value={totalClients} // <-- Usa la variable filtrada
                    icon={<UserIcon />}
                    onClick={() => setActiveView('clients')}
                />
                <SummaryTile
                    title="Ventas Totales"
                    value={totalSales} // <-- Usa la variable filtrada
                    icon={<ShoppingCartIcon />}
                    onClick={() => setActiveView('sales')}
                />
            </div>
            
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border} space-y-4`}>
                <h3 className={`text-xl font-bold ${t.primary}`}>Atajos Rápidos</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button onClick={() => setActiveView('reseller-inventory')} className={`flex flex-col items-center justify-center p-4 rounded-lg ${t.buttonSecondary} h-32 transition-transform hover:scale-105`}>
                        <BoxIcon />
                        <span className="mt-2">Comprar Inventario</span>
                    </button>
                    <button onClick={() => setActiveView('sales')} className={`flex flex-col items-center justify-center p-4 rounded-lg ${t.buttonSecondary} h-32 transition-transform hover:scale-105`}>
                        <ShoppingCartIcon />
                        <span className="mt-2">Nueva Venta</span>
                    </button>
                    <button onClick={() => setActiveView('clients')} className={`flex flex-col items-center justify-center p-4 rounded-lg ${t.buttonSecondary} h-32 transition-transform hover:scale-105`}>
                        <UserIcon />
                        <span className="mt-2">Añadir Cliente</span>
                    </button>
                </div>
            </div>
        </div>
    );
}

// --- [COMPONENTE COMPLETO]: VISTA DE INVENTARIO PARA REVENDEDORES ---
function ResellerInventoryView({ user, services, inventory: allInventory, setActiveView }) {
    const { theme, themes } = useTheme();
    const t = themes[theme];
    const { db } = initializeFirebase();
    const { showNotification } = useNotification();
    const [searchTerm, setSearchTerm] = useState('');
    const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
    const [currentService, setCurrentService] = useState(null);
    const [serviceMemberships, setServiceMemberships] = useState([]);
    const [purchaseMembershipId, setPurchaseMembershipId] = useState('');
    const [loadingPurchase, setLoadingPurchase] = useState(false);

    // Filtramos los servicios para la búsqueda
    const filteredServices = services.filter(service =>
        service.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Cargar membresías para el servicio seleccionado
    useEffect(() => {
        if (!currentService) return;
        // Apunta al teamId del owner para obtener las membresías
        const membershipsRef = collection(db, 'users', user.teamId, 'services', currentService.id, 'memberships');
        const q = query(membershipsRef, orderBy('durationDays', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const allMembresias = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // El revendedor solo puede comprar planes que tengan un resellerPrice > 0 y no sean exclusivos para clientes finales
            const resellerMembresias = allMembresias.filter(m => m.resellerPrice && Number(m.resellerPrice) > 0);
            setServiceMemberships(resellerMembresias);
        });
        return () => unsubscribe();
    }, [currentService, user.teamId, db]);

    const openPurchaseModal = (service) => {
        const serviceInventory = allInventory[service.id] || [];
        const availableCount = serviceInventory.filter(item => (item.assignments || 0) < (item.maxAssignments || 1)).length;

        if (availableCount === 0) {
            showNotification('Este servicio no tiene inventario disponible.', 'error');
            return;
        }

        setCurrentService(service);
        setIsPurchaseModalOpen(true);
        setPurchaseMembershipId('');
    };

    const closePurchaseModal = () => {
        setIsPurchaseModalOpen(false);
        setCurrentService(null);
        setPurchaseMembershipId('');
    };

    const handlePurchase = async () => {
        if (!currentService || !purchaseMembershipId) {
            showNotification('Por favor, selecciona un plan de membresía.', 'error');
            return;
        }

        setLoadingPurchase(true);
        const selectedMembership = serviceMemberships.find(m => m.id === purchaseMembershipId);

        if (!selectedMembership || user.resellerBalance < selectedMembership.resellerPrice) {
            showNotification('Saldo insuficiente para realizar la compra.', 'error');
            setLoadingPurchase(false);
            return;
        }

        try {
            const { app } = initializeFirebase();
            const functions = getFunctions(app);
            const processResellerPurchase = httpsCallable(functions, 'processResellerPurchase');

            const result = await processResellerPurchase({
                serviceId: currentService.id,
                membershipId: selectedMembership.id,
            });

            if (result.data.success) {
                showNotification(result.data.message, 'success');
                closePurchaseModal();
                // Opcional: Redirigir al usuario a su inventario o a ventas
                setActiveView('sales');
            } else {
                throw new Error(result.data.message || 'Error desconocido al procesar la compra.');
            }
        } catch (error) {
            console.error("Error al procesar la compra del revendedor:", error);
            showNotification(error.message, 'error');
        } finally {
            setLoadingPurchase(false);
        }
    };

    return (
        <>
            <div className={`${t.bgMuted} p-6 rounded-lg border ${t.border}`}>
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h2 className={`text-2xl font-bold ${t.primary}`}>Inventario para Comprar</h2>
                    <div className="relative w-full sm:w-auto">
                        <input type="text" placeholder="Buscar servicios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full sm:w-64 pl-10 pr-4 py-2 ${t.bgInput} border ${t.border} rounded-md`} />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SearchIcon /></div>
                    </div>
                </div>

                {/* --- CONTENEDOR CON SCROLL CORREGIDO --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto p-1">
                    {filteredServices.map(service => {
                        const serviceInventory = allInventory[service.id] || [];
                        const availableCount = serviceInventory.filter(item => (item.assignments || 0) < (item.maxAssignments || 1)).length;
                        const isOutOfStock = availableCount === 0;

                        return (
                            // --- CORRECCIÓN: Se quitó 'hover:scale-105' del contenedor principal para evitar el parpadeo ---
                            <div key={service.id} className={`${t.bg} p-4 rounded-lg border ${t.border} flex flex-col justify-between relative ${isOutOfStock ? 'opacity-50 grayscale' : ''}`}>
                                <img src={service.imageUrl || 'https://placehold.co/600x400/0a0a0a/cyan?text=GCS1'} alt={service.name} className="w-full h-32 object-cover rounded-md mb-4" />
                                
                                <div className="flex-grow">
                                    <div className="flex justify-between items-start mb-1">
                                        <h3 className={`text-xl font-bold ${t.secondary}`}>{service.name}</h3>
                                        <div className={`text-xs font-bold px-2 py-1 rounded-full ${isOutOfStock ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                                            {availableCount} Disp.
                                        </div>
                                    </div>
                                    <p className={`text-sm ${t.textMuted}`}>{service.type}</p>
                                </div>
                                
                                <div className="mt-4">
                                    {/* AÑADIDO: Efecto hover directamente al botón */}
                                    <button onClick={() => openPurchaseModal(service)} disabled={isOutOfStock || loadingPurchase} className={`w-full flex items-center justify-center px-4 py-2 font-bold rounded-md ${t.buttonPrimary} disabled:opacity-50 disabled:cursor-not-allowed transition-transform hover:scale-105`}>
                                        <ShoppingCartIcon /> Comprar
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <Modal isOpen={isPurchaseModalOpen} onClose={closePurchaseModal} title={`Comprar ${currentService?.name}`} maxWidth="max-w-md">
                <div className="space-y-4">
                    <div className="bg-yellow-500/20 text-yellow-400 p-3 rounded-md">
                        <p className="font-bold">Tu saldo actual: ${(user.resellerBalance || 0).toFixed(2)} USD</p>
                    </div>
                    <div>
                        <label className={`block text-sm font-bold ${t.textMuted} mb-2`}>Seleccionar Plan</label>
                        <select value={purchaseMembershipId} onChange={e => setPurchaseMembershipId(e.target.value)} required className={`w-full px-4 py-2 ${t.bgInput} border ${t.border} rounded-md`}>
                            <option value="">-- Seleccionar --</option>
                            {serviceMemberships.map(m => (
                                <option key={m.id} value={m.id}>
                                    {m.name} ({m.durationDays} días) - ${m.resellerPrice} USD
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={closePurchaseModal} className={`px-4 py-2 rounded-md ${t.buttonSecondary}`}>Cancelar</button>
                        <button type="button" onClick={handlePurchase} disabled={loadingPurchase || !purchaseMembershipId} className={`px-4 py-2 font-bold rounded-md ${t.buttonPrimary} disabled:opacity-50`}>
                            {loadingPurchase ? 'Procesando...' : 'Confirmar Compra'}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

// --- DEFINICIÓN DE CAMPOS PARA CRUDs ---
const clientFields = [
    { name: 'name', label: 'Nombre', placeholder: 'Nombre del Cliente', required: true },
    { name: 'contact', label: 'Contacto', placeholder: 'Número de WhatsApp o User de Telegram', required: true },
    { name: 'note', label: 'Nota', placeholder: 'Alguna nota sobre el cliente', required: false }
];

const providerFields = [
    { name: 'name', label: 'Nombre', placeholder: 'Nombre del Proveedor', required: true },
    { name: 'contact', label: 'Contacto', placeholder: 'Dato de contacto del proveedor', required: true },
    { name: 'note', label: 'Nota', placeholder: 'Información adicional', required: false }
];

const membershipFields = [
    { name: 'name', label: 'Nombre', placeholder: 'Ej: 1 Mes, 3 Meses' },
    { name: 'durationDays', label: 'Duración (Días)', type: 'number', placeholder: 'Ej: 30' }
];

const serviceTypeFields = {
    'Streaming': [
        { name: 'email', label: 'Correo', type: 'email' }, { name: 'password', label: 'Contraseña' }, { name: 'profile', label: 'Perfil' }, { name: 'pin', label: 'PIN' },
    ],
    'Canva': [
        { name: 'matrixEmail', label: 'Correo Matríz', type: 'email' }, { name: 'className', label: 'Nombre Clase/Equipo' }, { name: 'inviteLink', label: 'Enlace de Invitación', type: 'url' },
    ],
    'Capcut': [
        { name: 'email', label: 'Correo', type: 'email' }, { name: 'password', label: 'Contraseña' }, { name: 'allowedDevices', label: 'Dispositivos Permitidos' },
    ],
    'IPTV': [
        { name: 'user', label: 'Usuario' }, { name: 'password', label: 'Contraseña' }, { name: 'url', label: 'URL' }, { name: 'note', label: 'Nota' },
    ]
};

const cannedResponseFields = [
    { name: 'shortcut', label: 'Atajo', placeholder: 'Ej: /saludo', required: true },
    { name: 'text', label: 'Texto de la Respuesta', placeholder: 'Escribe la respuesta completa...', required: true }
];

// --- [NUEVO] VISTA DEL PORTAL DEL CLIENTE FINAL ---
function ClientPortalView() {
    const { theme, themes } = useTheme();
    const t = themes['netflix_dark']; // Usamos un tema oscuro por defecto para el portal
    const [portalData, setPortalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchPortalData = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const token = params.get('token');

                if (!token) {
                    throw new Error('Token de acceso no encontrado. El enlace puede ser inválido.');
                }

                const { app } = initializeFirebase();
                const functions = getFunctions(app);
                const getPortalData = httpsCallable(functions, 'getClientPortalData');
                
                const result = await getPortalData({ token });
                setPortalData(result.data);

            } catch (err) {
                console.error("Error al cargar los datos del portal:", err);
                setError(err.message || 'Ocurrió un error al cargar tus servicios.');
            } finally {
                setLoading(false);
            }
        };

        fetchPortalData();
    }, []);

    return (
        <div className={`min-h-screen p-4 sm:p-8 flex items-center justify-center ${t.bg}`} style={{ fontFamily: fonts['Lato'] }}>
            <div className={`w-full max-w-2xl mx-auto ${t.bgMuted} border ${t.border} rounded-lg shadow-2xl p-8`}>
                {loading ? (
                    <div className="text-center">
                        <p className={`text-2xl font-bold ${t.primary} animate-pulse`}>Validando acceso...</p>
                    </div>
                ) : error ? (
                    <div className="text-center">
                        <div className="mx-auto w-16 h-16 text-red-500"><AlertTriangleIcon /></div>
                        <h2 className="mt-4 text-2xl font-bold text-red-400">Acceso Denegado</h2>
                        <p className={t.textMuted}>{error}</p>
                    </div>
                ) : portalData ? (
                    <div>
                        <h1 className={`text-3xl font-bold ${t.primary} mb-2`}>¡Hola, {portalData.clientName}!</h1>
                        <p className={t.textMuted}>Aquí tienes un resumen de tus servicios activos.</p>
                        
                        <div className="mt-6 space-y-4">
                            {portalData.subscriptions.length > 0 ? (
                                portalData.subscriptions.map((sub, index) => (
                                    <div key={index} className={`${t.bg} border ${t.border} p-4 rounded-lg flex items-center gap-4`}>
                                        <img src={sub.imageUrl || 'https://placehold.co/100x100/0a0a0a/cyan?text=Servicio'} alt={sub.serviceName} className="w-16 h-16 rounded-md object-cover" />
                                        <div className="flex-1">
                                            <h3 className={`font-bold text-lg ${t.secondary}`}>{sub.serviceName}</h3>
                                            <p className={`text-sm ${t.textMuted}`}>Vence el: <span className="font-semibold text-green-400">{sub.expiresAt}</span></p>
                                            <div className={`mt-2 text-xs p-2 rounded ${t.bgInput}`}>
                                                <p><strong>Email:</strong> {sub.email}</p>
                                                {sub.profile && <p><strong>Perfil:</strong> {sub.profile}</p>}
                                                {sub.pin && <p><strong>PIN:</strong> {sub.pin}</p>}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className={t.textMuted}>No tienes suscripciones activas en este momento.</p>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

// --- VISTA DEL CATÁLOGO PÚBLICO (CON DISEÑO Y FUNCIONALIDADES MEJORADAS) ---
function PublicCatalogView({ previewData = null }) {
    const [catalogData, setCatalogData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Estados para los filtros y la selección de planes/cantidades
    const [selectedPlans, setSelectedPlans] = useState({});
    const [quantities, setQuantities] = useState({});
    const [paymentMethods, setPaymentMethods] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const { showNotification } = useNotification(); 

    useEffect(() => {
        const fetchCatalogData = async () => {
            try {
                let data;
                if (previewData) {
                    data = previewData;
                } else {
                    const path = window.location.pathname;
                    const slug = path.split('/c/')[1];
                    if (!slug) throw new Error('No se ha especificado un nombre de tienda en la URL.');
                    
                    const { app } = initializeFirebase();
                    const functions = getFunctions(app);
                    const getCatalogData = httpsCallable(functions, 'getPublicCatalogData');
                    const result = await getCatalogData({ catalogSlug: slug });

                    if (!result.data || !result.data.storeName) {
                        throw { code: 'functions/not-found' };
                    }
                    data = result.data;
                }
                
                setCatalogData(data);

                const initialSelectedPlans = {};
                const initialQuantities = {};
                const initialPaymentMethods = {};
                if (data.services) {
                    data.services.forEach(service => {
                        if (service.memberships && service.memberships.length > 0) {
                            initialSelectedPlans[service.id] = service.memberships[0];
                            initialQuantities[service.id] = 1;
                            initialPaymentMethods[service.id] = 'Pago Móvil';
                        }
                    });
                }
                setSelectedPlans(initialSelectedPlans);
                setQuantities(initialQuantities);
                setPaymentMethods(initialPaymentMethods);

            } catch (err) {
                console.error("Error al cargar los datos del catálogo:", err);
                setError(err.code === 'functions/not-found' ? 'No se encontró una tienda con este nombre.' : err.message || 'Ocurrió un error.');
            } finally {
                setLoading(false);
            }
        };

        fetchCatalogData();
    }, [previewData]);

    const handlePlanChange = (serviceId, membershipJson) => {
        const membership = JSON.parse(membershipJson);
        setSelectedPlans(prev => ({ ...prev, [serviceId]: membership }));
    };

    const handleQuantityChange = (serviceId, qty) => {
        const newQty = Math.max(1, parseInt(qty, 10) || 1);
        setQuantities(prev => ({ ...prev, [serviceId]: newQty }));
    };

    const handlePaymentMethodChange = (serviceId, method) => {
        setPaymentMethods(prev => ({ ...prev, [serviceId]: method }));
    };

    const createWhatsAppLink = (serviceName, membership, quantity, paymentMethod) => {
        if (!catalogData || !catalogData.whatsapp || !membership) return '#';
        
        const price = Number(membership.price) || 0;
        const totalUSD = price * quantity;
        let finalTotalUSD = totalUSD;
        let commissionText = '';

        if (paymentMethod === 'PayPal (+ comisión)') {
            finalTotalUSD = totalUSD * 1.054 + 0.30; // Ejemplo de comisión PayPal
            commissionText = `\n*Comisión PayPal:* $${(finalTotalUSD - totalUSD).toFixed(2)}\n*Total Final con Comisión:* $${finalTotalUSD.toFixed(2)}`;
        }

        const totalBs = (membership.priceBs ? Number(membership.priceBs) * quantity : 0).toFixed(2);
        const priceBsText = membership.priceBs ? `*Precio Bs.:* Bs. ${totalBs}` : '';

        const message = `¡Hola! 👋\n\nEstoy interesado/a en el siguiente pedido desde tu catálogo:\n\n*Servicio:* ${serviceName}\n*Plan:* ${membership.name} (${membership.durationDays} días)\n*Cantidad:* ${quantity}\n\n*Subtotal USD:* $${totalUSD.toFixed(2)}\n${priceBsText}\n*Método de Pago:* ${paymentMethod}${commissionText}\n\n¡Espero tu respuesta para coordinar el pago! Gracias.`;
        return `https://wa.me/${catalogData.whatsapp}?text=${encodeURIComponent(message)}`;
    };

    const createResellerLink = (serviceName) => {
        if (!catalogData || !catalogData.whatsapp) return '#';
        const message = `¡Hola! 👋\n\nEstoy interesado/a en convertirme en revendedor del servicio *${serviceName}* que vi en tu catálogo. ¿Podrías darme más información? Gracias.`;
        return `https://wa.me/${catalogData.whatsapp}?text=${encodeURIComponent(message)}`;
    };

    const handleCopyDetails = (service) => {
        let details = `*${service.name}*\n\n`;
        details += service.memberships.map(mem => 
            `*Plan:* ${mem.name} (${mem.durationDays} días)\n` +
            `*Precio USD:* $${Number(mem.price).toFixed(2)}\n` +
            (mem.priceBs ? `*Precio Bs.:* Bs. ${mem.priceBs}\n` : '')
        ).join('\n');
        
        navigator.clipboard.writeText(details)
            .then(() => showNotification('Detalles copiados al portapapeles', 'success'))
            .catch(() => showNotification('No se pudieron copiar los detalles', 'error'));
    };

    const serviceCategories = useMemo(() => {
        if (!catalogData?.services) return [];
        const categories = new Set(catalogData.services.map(s => s.type));
        return ['all', ...Array.from(categories)];
    }, [catalogData]);

    const filteredServices = useMemo(() => {
        if (!catalogData?.services) return [];
        return catalogData.services
            .filter(service => selectedCategory === 'all' || service.type === selectedCategory)
            .filter(service => service.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [catalogData, selectedCategory, searchTerm]);

    if (loading) {
        return <div className="flex items-center justify-center min-h-screen bg-[#141414] text-white"><div className="text-2xl animate-pulse">Cargando catálogo...</div></div>;
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-[#141414] text-white p-4 text-center">
                <div className="mx-auto w-16 h-16 text-red-500"><AlertTriangleIcon /></div>
                <h2 className="mt-4 text-2xl font-bold text-red-400">Error al Cargar</h2>
                <p className="text-gray-400">{error}</p>
                <a href="https://streamdesk.solu7ec.com" className="mt-6 px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600">Volver al inicio</a>
            </div>
        );
    }
    
    const t = catalogTemplates[catalogData.template] || catalogTemplates.elegant_gold;
    const fontFamily = catalogData.template === 'matrix_dark' ? "'Roboto Mono', monospace" : "'Lato', sans-serif";
    
    document.body.className = t.bg;

    return (
        <div className={`min-h-screen p-4 sm:p-8 ${t.bg} ${t.text}`} style={{ fontFamily: fontFamily }}>
            <header className="text-center mb-8">
                {catalogData.logoUrl && <img src={catalogData.logoUrl} alt={`Logo de ${catalogData.storeName}`} className="mx-auto h-24 w-auto object-contain mb-4" />}
                <h1 className={`text-4xl font-bold ${t.secondary}`}>{catalogData.storeName}</h1>
            </header>

            <main className="max-w-5xl mx-auto">
                <div className="mb-8 space-y-4">
                    <div className="relative max-w-lg mx-auto">
                        <input 
                            type="text" 
                            placeholder="Buscar servicio..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 ${t.card.bg} border ${t.card.border} rounded-full focus:ring-2 ${t.primary}`}
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SearchIcon /></div>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                        {serviceCategories.map(category => (
                            <button 
                                key={category} 
                                onClick={() => setSelectedCategory(category)}
                                className={`px-4 py-1 text-sm font-bold rounded-full transition-all duration-300 transform hover:scale-105 ${selectedCategory === category ? t.button : `${t.plan.bg} ${t.textMuted}`}`}
                            >
                                {category === 'all' ? 'Todos' : category}
                            </button>
                        ))}
                    </div>
                </div>

                {filteredServices.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredServices.map(service => {
                            const isResellerService = service.name.toLowerCase().includes('revendedor');
                            const selectedMembership = selectedPlans[service.id];
                            const quantity = quantities[service.id] || 1;
                            const paymentMethod = paymentMethods[service.id] || 'Pago Móvil';
                            const totalUSD = selectedMembership ? (Number(selectedMembership.price) || 0) * quantity : 0;
                            let finalTotalUSD = totalUSD;
                            if (paymentMethod === 'PayPal (+ comisión)') {
                                finalTotalUSD = totalUSD * 1.054 + 0.30;
                            }

                            return (
                                <div key={service.id} className={`${t.card.bg} rounded-lg ${t.card.shadow} overflow-hidden flex flex-col border ${t.card.border} transition-transform duration-300 hover:scale-105 hover:shadow-2xl`}>
                                    <img src={service.imageUrl} alt={service.name} className="w-full h-40 object-cover" />
                                    <div className="p-6 flex flex-col flex-grow">
                                        <div className="flex justify-between items-start">
                                            <h2 className={`text-2xl font-bold ${t.secondary}`}>{service.name}</h2>
                                            <button onClick={() => handleCopyDetails(service)} title="Copiar Detalles" className={`p-2 rounded-full ${t.plan.bg} hover:bg-opacity-50 transition-transform hover:scale-110`}><LinkIcon /></button>
                                        </div>
                                        
                                        <div className="mt-4 space-y-3 flex-grow flex flex-col justify-center">
                                            {isResellerService ? (
                                                <div className="flex-grow flex items-center justify-center">
                                                    <a href={createResellerLink(service.name)} target="_blank" rel="noopener noreferrer" className={`w-full text-center ${t.button}`}>Contacta para Convertirte en Revendedor</a>
                                                </div>
                                            ) : service.memberships && service.memberships.length > 0 ? (
                                                <div className="text-center space-y-4">
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <label className="text-xs font-bold block mb-1">Plan:</label>
                                                            <select onChange={(e) => handlePlanChange(service.id, e.target.value)} className={`w-full p-2 text-sm rounded-md ${t.plan.bg} border ${t.card.border} ${t.text}`}>
                                                                {service.memberships.map(mem => (<option key={mem.name} value={JSON.stringify(mem)}>{mem.name} ({mem.durationDays} días)</option>))}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-bold block mb-1">Cantidad:</label>
                                                            <input type="number" value={quantity} onChange={(e) => handleQuantityChange(service.id, e.target.value)} min="1" className={`w-full p-2 text-sm rounded-md ${t.plan.bg} border ${t.card.border} ${t.text}`} />
                                                        </div>
                                                    </div>
                                                    
                                                    {selectedMembership && (
                                                        <div className={`p-3 rounded-md ${t.plan.bg}`}>
                                                            <p className={`text-2xl font-bold ${t.plan.price}`}>${finalTotalUSD.toFixed(2)}</p>
                                                            {selectedMembership.priceBs && <p className={`text-md font-semibold ${t.primary}`}>Bs. {(Number(selectedMembership.priceBs) * quantity).toFixed(2)}</p>}
                                                        </div>
                                                    )}

                                                    <div>
                                                        <label className="text-xs font-bold block mb-1">Método de Pago:</label>
                                                        <select value={paymentMethod} onChange={(e) => handlePaymentMethodChange(service.id, e.target.value)} className={`w-full p-2 text-sm rounded-md ${t.plan.bg} border ${t.card.border} ${t.text}`}>
                                                            <option>Pago Móvil</option>
                                                            <option>Bancolombia</option>
                                                            <option>Binance Pay</option>
                                                            <option disabled={totalUSD < 10}>Wally (solo montos mayores a $10)</option>
                                                            <option disabled={totalUSD < 10}>Zinli (solo montos mayores a $10)</option>
                                                            <option>PayPal (+ comisión)</option>
                                                            <option>Otro</option>
                                                        </select>
                                                    </div>

                                                    <a href={createWhatsAppLink(service.name, selectedMembership, quantity, paymentMethod)} target="_blank" rel="noopener noreferrer" className={`block w-full text-center ${t.button}`}>Comprar</a>
                                                </div>
                                            ) : (
                                                <p className={t.textMuted}>No hay planes disponibles.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <h2 className="text-2xl font-bold text-yellow-400">Sin Resultados</h2>
                        <p className={t.textMuted}>No se encontraron servicios que coincidan con tu búsqueda.</p>
                    </div>
                )}
            </main>
            <footer className={`text-center mt-12 pt-6 border-t ${t.footer}`}>
                <p className="text-xs">Powered by StreamDesk | <a href="https://solu7ec.com" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400">solu7ec.com</a></p>
            </footer>
        </div>
    );
}

// --- COMPONENTE PRINCIPAL (CON LÓGICA DE ENRUTAMIENTO) ---
export default function App() {
    const [route, setRoute] = useState(window.location.pathname);

    useEffect(() => {
        const handlePopState = () => setRoute(window.location.pathname);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

const renderRoute = () => {
        if (route.startsWith('/c/')) {
            return <PublicCatalogView />;
        }

        if (route.startsWith('/portal')) {
            return <ClientPortalView />;
        }

        return <MainApp />;
    };

    return (
        <ThemeProvider>
            <NotificationProvider>
                {renderRoute()}
            </NotificationProvider>
        </ThemeProvider>
    );
}

function MainApp() {
    const [appState, setAppState] = useState('loading');
    const [userData, setUserData] = useState(null);
    const { auth, db } = initializeFirebase();
    // --- AÑADIMOS setDashboardStyle DEL CONTEXTO ---
    const { setTheme, setFont, setDashboardStyle } = useTheme();
    
    const [isSdkReady, setIsSdkReady] = useState(false);

    const handleLogout = useCallback(async () => {
        await signOut(auth);
        setUserData(null);
        setAppState('auth');
    }, [auth]);

    useEffect(() => {
        if (!window.FB) {
            window.fbAsyncInit = function() {
                window.FB.init({
                    appId      : '1279040290446693', // Reemplaza esto
                    cookie     : true,
                    xfbml      : true,
                    version    : 'v19.0'
                });
                window.FB.AppEvents.logPageView();
                setIsSdkReady(true);
            };

            (function(d, s, id){
                var js, fjs = d.getElementsByTagName(s)[0];
                if (d.getElementById(id)) {
                    setIsSdkReady(true);
                    return;
                }
                js = d.createElement(s); js.id = id;
                js.src = "https://connect.facebook.net/en_US/sdk.js";
                fjs.parentNode.insertBefore(js, fjs);
            }(document, 'script', 'facebook-jssdk'));
        } else {
            setIsSdkReady(true);
        }

        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            const handleUserCheck = async (user) => {
                if (!user) {
                    setUserData(null);
                    setAppState('auth');
                    return;
                }
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const currentUserData = { ...userDocSnap.data(), id: userDocSnap.id, uid: user.uid };
                    
                    // --- LÓGICA ACTUALIZADA PARA CARGAR TODAS LAS PREFERENCIAS ---
                    if (currentUserData.preferences?.theme) {
                        setTheme(currentUserData.preferences.theme);
                    }
                    if (currentUserData.preferences?.font) {
                        setFont(currentUserData.preferences.font);
                    }
                    if (currentUserData.preferences?.dashboardStyle) {
                        setDashboardStyle(currentUserData.preferences.dashboardStyle);
                    }
                    
                    if (currentUserData.planId) {
                        const planDocRef = doc(db, 'plans', currentUserData.planId);
                        const planDocSnap = await getDoc(planDocRef);
                        if (planDocSnap.exists()) {
                            currentUserData.plan = planDocSnap.data();
                        }
                    }
                    
                    setUserData(currentUserData);
                    
                    if (currentUserData.isAdmin) {
                        setAppState('dashboard');
                    } else {
                        if (currentUserData.subscriptionStatus === 'active' && currentUserData.subscriptionExpiresAt.toDate() >= new Date()) {
                            setAppState('dashboard');
                        } else {
                            setAppState('denied');
                        }
                    }
                } else {
                    console.error("User document not found for authenticated user.");
                    handleLogout();
                }
            };
            
            handleUserCheck(currentUser);
        });

        return () => unsubscribe();
    }, [auth, db, handleLogout, setTheme, setFont, setDashboardStyle]); // Dependencias actualizadas

    const renderContent = () => {
        if (appState === 'loading') return <div className="flex items-center justify-center min-h-screen"><div className="text-2xl animate-pulse">Iniciando sistema...</div></div>;

        switch (appState) {
            case 'auth':
                return <AuthComponent onLoginSuccess={setUserData} />;
            case 'dashboard':
                if (!userData) return <div className="flex items-center justify-center min-h-screen"><div className="text-2xl animate-pulse">Cargando datos...</div></div>;
                if (userData.isAdmin) return <AdminDashboard user={userData} handleLogout={handleLogout} />;
return <AccountManagementDashboard user={userData} handleLogout={handleLogout} isSdkReady={isSdkReady} setUserData={setUserData} />;
            case 'denied':
                return <SubscriptionView user={userData} handleLogout={handleLogout} />;
            default:
                return <AuthComponent onLoginSuccess={setUserData} />;
        }
    };

    return renderContent();
}